import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { collection, getDocs, doc, getDoc, setDoc, updateDoc, addDoc, deleteDoc, query, where, orderBy, serverTimestamp, onSnapshot } from 'firebase/firestore';
import { getMessaging, getToken, onMessage } from 'firebase/messaging';
import { auth, db, storage } from './firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import './AdminV2.css';

/* ══════════════════════════════════════════════
   DIALOG SYSTEM — Apple-style replacements for
   window.confirm / window.alert / window.prompt
   ══════════════════════════════════════════════ */

let _dialogResolve = null;
const _dialogListeners = new Set();
let _dialogState = null;

function _setDialog(state) {
  _dialogState = state;
  _dialogListeners.forEach(fn => fn(state));
}

const dialog = {
  confirm: (message, options = {}) =>
    new Promise(resolve => {
      _dialogResolve = resolve;
      _setDialog({
        kind: 'confirm',
        message,
        title: options.title || null,
        confirmLabel: options.confirmLabel || 'Confirm',
        cancelLabel: options.cancelLabel || 'Cancel',
        danger: options.danger ?? /\b(delete|remove|deactivate|cannot be undone|permanently)\b/i.test(message),
      });
    }),
  alert: (message, options = {}) =>
    new Promise(resolve => {
      _dialogResolve = resolve;
      _setDialog({
        kind: 'alert',
        message,
        title: options.title || null,
        confirmLabel: options.confirmLabel || 'OK',
        danger: options.danger ?? /\berror\b/i.test(message),
      });
    }),
  prompt: (message, defaultValue = '', options = {}) =>
    new Promise(resolve => {
      _dialogResolve = resolve;
      _setDialog({
        kind: 'prompt',
        message,
        title: options.title || null,
        defaultValue,
        confirmLabel: options.confirmLabel || 'OK',
        cancelLabel: options.cancelLabel || 'Cancel',
        placeholder: options.placeholder || '',
      });
    }),
};

function _resolveDialog(value) {
  const r = _dialogResolve;
  _dialogResolve = null;
  _setDialog(null);
  if (r) r(value);
}

function DialogRoot() {
  const [state, setState] = React.useState(_dialogState);
  const [inputValue, setInputValue] = React.useState('');
  const inputRef = React.useRef(null);

  React.useEffect(() => {
    const handler = s => setState(s);
    _dialogListeners.add(handler);
    return () => _dialogListeners.delete(handler);
  }, []);

  React.useEffect(() => {
    if (state?.kind === 'prompt') {
      setInputValue(state.defaultValue || '');
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [state]);

  React.useEffect(() => {
    if (!state) return;
    const onKey = e => {
      if (e.key === 'Escape') {
        _resolveDialog(state.kind === 'alert' ? undefined : state.kind === 'prompt' ? null : false);
      } else if (e.key === 'Enter' && state.kind !== 'prompt') {
        _resolveDialog(state.kind === 'alert' ? undefined : true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [state]);

  if (!state) return null;

  const { kind, message, title, confirmLabel, cancelLabel, danger, placeholder } = state;
  const handleCancel = () => _resolveDialog(kind === 'prompt' ? null : false);
  const handleConfirm = () => _resolveDialog(kind === 'alert' ? undefined : kind === 'prompt' ? inputValue : true);

  return (
    <div
      onClick={(e) => {
        if (e.target === e.currentTarget) handleCancel();
      }}
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(0,0,0,0.36)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        zIndex: 100000,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 20,
        animation: 'av2DialogFadeIn 0.18s cubic-bezier(0.4, 0, 0.2, 1)',
      }}
    >
      <style>{`
        @keyframes av2DialogFadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes av2DialogScaleIn { from { opacity: 0; transform: translateY(8px) scale(0.98); } to { opacity: 1; transform: translateY(0) scale(1); } }
      `}</style>
      <div
        style={{
          background: '#fff',
          borderRadius: 18,
          maxWidth: 420,
          width: '100%',
          boxShadow: '0 24px 56px rgba(0,0,0,0.18), 0 8px 20px rgba(0,0,0,0.08)',
          border: '1px solid rgba(0,0,0,0.08)',
          fontFamily: 'var(--av2-font)',
          animation: 'av2DialogScaleIn 0.24s cubic-bezier(0.4, 0, 0.2, 1)',
          overflow: 'hidden',
        }}
      >
        <div style={{ padding: '26px 26px 18px' }}>
          {title && (
            <div style={{ fontSize: 17, fontWeight: 700, color: '#1D1D1F', letterSpacing: '-0.012em', marginBottom: 6 }}>
              {title}
            </div>
          )}
          <div style={{ fontSize: 14, color: title ? '#424245' : '#1D1D1F', lineHeight: 1.5, letterSpacing: '-0.005em', whiteSpace: 'pre-wrap' }}>
            {message}
          </div>
          {kind === 'prompt' && (
            <input
              ref={inputRef}
              type="text"
              value={inputValue}
              onChange={e => setInputValue(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleConfirm(); } }}
              placeholder={placeholder}
              style={{
                marginTop: 14, width: '100%',
                padding: '10px 12px',
                border: '1px solid rgba(0,0,0,0.14)',
                borderRadius: 10,
                fontSize: 14,
                fontFamily: 'var(--av2-font)',
                color: '#1D1D1F',
                outline: 'none',
                background: '#fff',
                letterSpacing: '-0.005em',
              }}
              onFocus={e => { e.target.style.borderColor = '#007A53'; e.target.style.boxShadow = '0 0 0 4px rgba(0,122,83,0.18)'; }}
              onBlur={e => { e.target.style.borderColor = 'rgba(0,0,0,0.14)'; e.target.style.boxShadow = 'none'; }}
            />
          )}
        </div>
        <div style={{
          padding: '14px 22px 18px',
          display: 'flex', justifyContent: 'flex-end', gap: 8,
          borderTop: '1px solid rgba(0,0,0,0.05)',
        }}>
          {kind !== 'alert' && (
            <button
              onClick={handleCancel}
              style={{
                padding: '9px 18px',
                borderRadius: 10,
                border: '1px solid rgba(0,0,0,0.14)',
                background: '#fff',
                fontFamily: 'var(--av2-font)',
                fontSize: 13.5,
                fontWeight: 600,
                color: '#1D1D1F',
                cursor: 'pointer',
                letterSpacing: '-0.005em',
                boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
              }}
            >
              {cancelLabel}
            </button>
          )}
          <button
            onClick={handleConfirm}
            autoFocus
            style={{
              padding: '9px 18px',
              borderRadius: 10,
              border: 'none',
              background: danger ? '#D70015' : '#007A53',
              fontFamily: 'var(--av2-font)',
              fontSize: 13.5,
              fontWeight: 600,
              color: '#fff',
              cursor: 'pointer',
              letterSpacing: '-0.005em',
              boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
            }}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

const VAPID_KEY = 'BBoKaqrGkXM2svsCyxl8EiouHzS2c914Lw5z8vp48ym6oFleVJudoILLtd_krheSCRByKK5jVR2lhCgvC1K8nqw';

/* ══════════════════════════════════════════════
   ICONS
   ══════════════════════════════════════════════ */
const Icons = {
  dashboard: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>,
  leads: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>,
  clients: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"/></svg>,
  jobs: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94l-3.76 3.76z"/></svg>,
  calendar: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>,
  employees: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"/></svg>,
  reports: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="20" x2="12" y2="10"/><line x1="18" y1="20" x2="18" y2="4"/><line x1="6" y1="20" x2="6" y2="16"/></svg>,
  payroll: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 1v22M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/></svg>,
  logout: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9"/></svg>,
  trend_up: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>,
  alert: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>,
  check: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>,
  refresh: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15"/></svg>,
  plus: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M12 5v14M5 12h14"/></svg>,
  phone: <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z"/></svg>,
  mail: <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>,
  mapPin: <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>,
  close: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>,
  trash: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/></svg>,
  invoice: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>,
  bell: <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.73 21a2 2 0 01-3.46 0" /></svg>,
  inbox: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 12 16 12 14 15 10 15 8 12 2 12"/><path d="M5.45 5.11L2 12v6a2 2 0 002 2h16a2 2 0 002-2v-6l-3.45-6.89A2 2 0 0016.76 4H7.24a2 2 0 00-1.79 1.11z"/></svg>,
};

/* ══════════════════════════════════════════════
   RELATIVE TIME HELPER
   ══════════════════════════════════════════════ */
function formatRelativeTime(ms) {
  const diff = Date.now() - ms;
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  if (hours < 48) return 'Yesterday';
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(ms).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/* ══════════════════════════════════════════════
   CLIENT ID GENERATOR
   ══════════════════════════════════════════════ */
async function generateAccountNumber() {
  const counterRef = doc(db, 'system', 'counters');
  try {
    const counterSnap = await getDoc(counterRef);
    let nextNum = 1;
    if (counterSnap.exists() && counterSnap.data().lastClientNumber) {
      nextNum = counterSnap.data().lastClientNumber + 1;
    }
    await setDoc(counterRef, { lastClientNumber: nextNum }, { merge: true });
    return `RH-${String(nextNum).padStart(4, '0')}`;
  } catch (err) {
    // Fallback: use timestamp-based ID
    console.error('Counter error:', err);
    return `RH-${Date.now().toString().slice(-4)}`;
  }
}

/* Ensure all contacts have an accountNumber — runs once at load */
async function ensureAccountNumbers(contacts) {
  let updated = 0;
  for (const c of contacts) {
    if (!c.accountNumber) {
      const acctNum = await generateAccountNumber();
      await updateDoc(doc(db, 'contacts', c.id), { accountNumber: acctNum });
      c.accountNumber = acctNum;
      updated++;
    }
  }
  return updated;
}


/* ══════════════════════════════════════════════
   MAIN COMPONENT
   ══════════════════════════════════════════════ */
export default function AdminV2() {
  // CSS for cards now lives in AdminV2.css
  const [user, setUser] = useState(null);
  const [role, setRole] = useState(null);
  const [currentUserName, setCurrentUserName] = useState('Admin');
  const [loading, setLoading] = useState(true);
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPass, setLoginPass] = useState('');
  const [loginError, setLoginError] = useState('');

  const [page, setPage] = useState('dashboard');
  const [contacts, setContacts] = useState([]);
  const [workOrders, setWorkOrders] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [billingEntries, setBillingEntries] = useState([]);
  const [timePunches, setTimePunches] = useState([]);
  const [recurringJobs, setRecurringJobs] = useState([]);
  const [invoices, setInvoices] = useState([]);

  // Admin notifications
  const [adminNotifications, setAdminNotifications] = useState([]);
  const [notifFilter, setNotifFilter] = useState('all'); // 'all' | 'unread' | 'priority' | 'completed'
  const [pushState, setPushState] = useState('unknown');
  const [pushBannerDismissed, setPushBannerDismissed] = useState(() => {
    try { return localStorage.getItem('rh-admin-push-dismissed') === 'true'; } catch { return false; }
  });
  const pushRegistered = useRef(false);

  // Deep-link target (set when admin taps a notification with workOrderId/invoiceId/contactId)
  const [targetWorkOrderId, setTargetWorkOrderId] = useState(null);
  const [targetInvoiceId, setTargetInvoiceId] = useState(null);
  const [targetContactId, setTargetContactId] = useState(null);

  const isAdmin = role === 'admin';

  /* ── Auth ── */
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      if (u) {
        // Check admins collection first
        const adminDoc = await getDoc(doc(db, 'admins', u.uid));
        if (adminDoc.exists()) {
          setUser(u);
          setRole('admin');
          setCurrentUserName(adminDoc.data().name || u.email || 'Admin');
        } else {
          // Check employees collection
          const empDoc = await getDoc(doc(db, 'employees', u.uid));
          if (empDoc.exists()) {
            setUser(u);
            setRole(empDoc.data().role || 'basic');
            setCurrentUserName(empDoc.data().name || u.email || 'Admin');
          } else {
            // Not staff — sign them out and surface a clear error.
            // Members should never reach this app.
            try { await signOut(auth); } catch (e) { /* ignore */ }
            setUser(null);
            setRole(null);
            setLoginError('This account is not authorized for the admin portal.');
          }
        }
      } else {
        setUser(null);
        setRole(null);
      }
      setLoading(false);
    });
    return () => unsub();
  }, []);

  /* ── Live listener on adminNotifications (only for admins) ── */
  useEffect(() => {
    if (!user || role !== 'admin') return;
    const q = query(
      collection(db, 'adminNotifications'),
      orderBy('createdAt', 'desc')
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        const items = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        setAdminNotifications(items);
      },
      (err) => console.error('Admin notifications listener error:', err)
    );
    return unsub;
  }, [user, role]);

  /* ── Determine initial push state ── */
  useEffect(() => {
    if (!user || role !== 'admin') return;
    if (!('Notification' in window) || !('serviceWorker' in navigator)) {
      setPushState('unsupported');
      return;
    }
    if (Notification.permission === 'denied') {
      setPushState('denied');
      return;
    }
    if (Notification.permission === 'granted') {
      setPushState('granted');
      // Auto-register silently if permission already granted
      if (!pushRegistered.current) {
        registerAdminPush();
      }
      return;
    }
    setPushState('unknown');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, role]);

  const registerAdminPush = async () => {
    if (!user || pushRegistered.current) return;
    try {
      const swReg = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
      await navigator.serviceWorker.ready;
      const messaging = getMessaging();
      const token = await getToken(messaging, { vapidKey: VAPID_KEY, serviceWorkerRegistration: swReg });
      if (!token) return;
      await setDoc(doc(db, 'admins', user.uid), { fcmToken: token, fcmTokenUpdatedAt: new Date().toISOString() }, { merge: true });
      pushRegistered.current = true;
      onMessage(messaging, (payload) => {
        console.log('[AdminPush] Foreground:', payload?.data?.title);
      });
    } catch (err) {
      console.warn('[AdminPush] Silent registration failed:', err.message);
    }
  };

  const enableAdminPush = async () => {
    if (!user) return false;
    setPushState('pending');
    try {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        setPushState(permission === 'denied' ? 'denied' : 'unknown');
        return false;
      }
      await registerAdminPush();
      setPushState('granted');
      return true;
    } catch (err) {
      console.error('[AdminPush] Enable failed:', err);
      dialog.alert('Push notifications failed: ' + (err.message || 'Unknown error'));
      setPushState('unknown');
      return false;
    }
  };

  const adminUnreadCount = adminNotifications.filter(n => !n.read && !n.completed).length;

  /* ── Load data ── */
  const loadData = useCallback(async () => {
    try {
      // Load contacts
      const contactsSnap = await getDocs(query(collection(db, 'contacts'), orderBy('updatedAt', 'desc')));
      const loadedContacts = contactsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      
      // Ensure all contacts have an account number (RH-0001, etc.)
      await ensureAccountNumbers(loadedContacts);
      setContacts(loadedContacts);

      // Load employees
      const empSnap = await getDocs(collection(db, 'employees'));
      setEmployees(empSnap.docs.map(d => ({ id: d.id, ...d.data() })));

      // Load billing/time data
      const teSnap = await getDocs(query(collection(db, 'timeEntries'), orderBy('createdAt', 'desc')));
      setBillingEntries(teSnap.docs.map(d => ({ id: d.id, ...d.data() })));

      const tpSnap = await getDocs(query(collection(db, 'timePunches'), orderBy('timestamp', 'desc')));
      setTimePunches(tpSnap.docs.map(d => ({ id: d.id, ...d.data() })));

      // Load recurring jobs
      try {
        const rjSnap = await getDocs(collection(db, 'recurringJobs'));
        setRecurringJobs(rjSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch {
        setRecurringJobs([]);
      }
    } catch (err) {
      console.error('Error loading admin data:', err);
    }
  }, []);

  /* ── Real-time listeners for workOrders + invoices ── */
  useEffect(() => {
    if (!isAdmin) return;

    const unsubWO = onSnapshot(
      query(collection(db, 'workOrders'), orderBy('createdAt', 'desc')),
      (snap) => setWorkOrders(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
      (err) => console.error('workOrders listener error:', err)
    );

    let unsubInv;
    try {
      unsubInv = onSnapshot(
        collection(db, 'invoices'),
        (snap) => {
          const invs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
          invs.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
          setInvoices(invs);
        },
        (err) => console.error('invoices listener error:', err)
      );
    } catch {
      setInvoices([]);
    }

    return () => {
      unsubWO();
      if (unsubInv) unsubInv();
    };
  }, [isAdmin]);

  useEffect(() => {
    if (isAdmin) loadData();
  }, [isAdmin, loadData]);

  /* ── Login ── */
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    try {
      const { signInWithEmailAndPassword } = await import('firebase/auth');
      await signInWithEmailAndPassword(auth, loginEmail, loginPass);
      // Auth state listener will check roles and either set user+role
      // or sign back out and surface an "unauthorized" error.
    } catch {
      setLoginError('Invalid credentials.');
    }
  };

  if (loading) {
    return <div className="av2-loading">Loading...</div>;
  }

  if (!user || !role) {
    return (
      <div className="av2-login-page">
        <div className="av2-login-card">
          <div className="av2-login-logo">
            <span style={{ fontWeight: 400 }}>reliable</span> <span style={{ fontWeight: 700 }}>HANDYMAN</span>
          </div>
          <div className="av2-login-sub">Admin Portal v2</div>
          <form onSubmit={handleLogin} className="av2-login-form">
            <input type="email" placeholder="Email" value={loginEmail} onChange={e => setLoginEmail(e.target.value)} required />
            <input type="password" placeholder="Password" value={loginPass} onChange={e => setLoginPass(e.target.value)} required />
            {loginError && <div className="av2-login-error">{loginError}</div>}
            <button type="submit" className="av2-login-btn">Sign In</button>
          </form>
        </div>
        <DialogRoot />
      </div>
    );
  }

  return (
    <div className="av2-layout">
      {/* ── Sidebar ── */}
      <aside className="av2-sidebar">
        <div className="av2-sidebar-logo">
          <span style={{ fontWeight: 400 }}>reliable</span> <span style={{ fontWeight: 700 }}>HANDYMAN</span>
        </div>

        <nav className="av2-nav">
          <button className={`av2-nav-item ${page === 'dashboard' ? 'active' : ''}`} onClick={() => setPage('dashboard')}>
            {Icons.dashboard} Dashboard
          </button>
          <button className={`av2-nav-item ${page === 'notifications' ? 'active' : ''}`} onClick={() => setPage('notifications')}>
            {Icons.bell} Notifications
            {adminUnreadCount > 0 && (
              <span className="av2-nav-count" style={{ background: '#D70015', color: '#fff' }}>
                {adminUnreadCount > 99 ? '99+' : adminUnreadCount}
              </span>
            )}
          </button>
          <button className={`av2-nav-item ${page === 'leads' ? 'active' : ''}`} onClick={() => setPage('leads')}>
            {Icons.inbox} Leads
            <span className="av2-nav-count">{contacts.filter(c => c.stage === 'lead').length || ''}</span>
          </button>
          <button className={`av2-nav-item ${page === 'estimates' ? 'active' : ''}`} onClick={() => setPage('estimates')}>
            {Icons.leads} Estimates
            <span className="av2-nav-count">{workOrders.filter(w => w.status === 'Estimate Requested' || w.status === 'Pending' || w.status === 'Estimate Sent' || w.status === 'Declined' || w.status === 'Canceled').length}</span>
          </button>
          <button className={`av2-nav-item ${page === 'jobs' ? 'active' : ''}`} onClick={() => setPage('jobs')}>
            {Icons.jobs} Jobs
            <span className="av2-nav-count">{workOrders.filter(w => w.status === 'Approved' || w.status === 'Scheduled' || w.status === 'In Progress').length}</span>
          </button>
          <button className={`av2-nav-item ${page === 'invoices' ? 'active' : ''}`} onClick={() => setPage('invoices')}>
            {Icons.invoice} Invoices
            <span className="av2-nav-count">{
              (() => {
                const toInvoice = workOrders.filter(w => (w.status === 'Completed' || w.status === 'Done') && !invoices.some(inv => inv.workOrderId === w.id)).length;
                const unpaid = invoices.filter(i => i.status === 'sent' || i.status === 'unpaid').length;
                const overdue = invoices.filter(i => (i.status === 'sent' || i.status === 'unpaid') && i.dueDate && new Date(i.dueDate) < new Date()).length;
                return toInvoice + unpaid + overdue || '';
              })()
            }</span>
          </button>
          <button className={`av2-nav-item ${page === 'clients' ? 'active' : ''}`} onClick={() => setPage('clients')}>
            {Icons.clients} Properties
            <span className="av2-nav-count">{contacts.filter(c => c.stage === 'client').length}</span>
          </button>
          <button className={`av2-nav-item ${page === 'calendar' ? 'active' : ''}`} onClick={() => setPage('calendar')}>
            {Icons.calendar} Calendar
          </button>
          <button className={`av2-nav-item ${page === 'employees' ? 'active' : ''}`} onClick={() => setPage('employees')}>
            {Icons.employees} Employees
          </button>
          <button className={`av2-nav-item ${page === 'payroll' ? 'active' : ''}`} onClick={() => setPage('payroll')}>
            {Icons.payroll} Payroll
          </button>
          <button className={`av2-nav-item ${page === 'reports' ? 'active' : ''}`} onClick={() => setPage('reports')}>
            {Icons.reports} Reports
          </button>
          <button className={`av2-nav-item ${page === 'catalog' ? 'active' : ''}`} onClick={() => setPage('catalog')}>
            {Icons.estimates} Catalog
          </button>
        </nav>

        <div className="av2-sidebar-bottom">
          {/* Push enable banner */}
          {!pushBannerDismissed && (pushState === 'unknown' || pushState === 'denied') && (
            <div style={{
              background: pushState === 'denied' ? '#fff' : 'linear-gradient(180deg, #1D7A4D 0%, #155838 100%)',
              border: pushState === 'denied' ? '1px solid var(--av2-border)' : 'none',
              borderRadius: 10,
              padding: '12px 14px',
              marginBottom: 10,
              fontSize: 12,
              color: pushState === 'denied' ? 'var(--av2-text)' : '#fff',
              boxShadow: pushState === 'denied' ? 'none' : '0 4px 14px rgba(0,122,83,0.18)',
            }}>
              <div style={{ fontWeight: 700, marginBottom: 4, letterSpacing: '-0.005em' }}>
                {pushState === 'denied' ? 'Notifications blocked' : 'Enable push notifications'}
              </div>
              <div style={{
                fontSize: 11,
                opacity: pushState === 'denied' ? 0.65 : 0.85,
                lineHeight: 1.4,
                marginBottom: 8,
              }}>
                {pushState === 'denied'
                  ? "You've blocked notifications. Re-enable in browser settings."
                  : 'Get instant alerts for new clients, work orders, and paid invoices.'}
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                {pushState === 'unknown' && (
                  <button
                    onClick={enableAdminPush}
                    style={{
                      flex: 1,
                      background: '#fff', color: '#155838',
                      border: 'none', borderRadius: 999,
                      padding: '6px 10px',
                      fontFamily: "var(--av2-font)",
                      fontSize: 11, fontWeight: 700, cursor: 'pointer',
                    }}
                  >
                    Turn on
                  </button>
                )}
                <button
                  onClick={() => {
                    setPushBannerDismissed(true);
                    try { localStorage.setItem('rh-admin-push-dismissed', 'true'); } catch {}
                  }}
                  style={{
                    background: 'transparent',
                    color: pushState === 'denied' ? 'var(--av2-text-light)' : 'rgba(255,255,255,0.85)',
                    border: pushState === 'denied' ? '1px solid var(--av2-border)' : '1px solid rgba(255,255,255,0.3)',
                    borderRadius: 999,
                    padding: '6px 10px',
                    fontFamily: "var(--av2-font)",
                    fontSize: 11, fontWeight: 600, cursor: 'pointer',
                  }}
                >
                  Dismiss
                </button>
              </div>
            </div>
          )}

          <div className="av2-user">
            <div className="av2-user-email">{user.email}</div>
            <div className="av2-user-role">Admin</div>
          </div>
          <button className="av2-logout" onClick={() => signOut(auth)}>
            {Icons.logout} Sign Out
          </button>
        </div>
      </aside>

      {/* ── Main Content ── */}
      <main className="av2-main">
        {page === 'dashboard' && <Dashboard contacts={contacts} workOrders={workOrders} billingEntries={billingEntries} recurringJobs={recurringJobs} setPage={setPage} loadData={loadData} />}
        {page === 'estimates' && <EstimatesPage workOrders={workOrders} contacts={contacts} employees={employees} invoices={invoices} loadData={loadData} targetWorkOrderId={targetWorkOrderId} clearTarget={() => setTargetWorkOrderId(null)} />}
        {page === 'leads' && <LeadsPage contacts={contacts} loadData={loadData} />}
        {page === 'clients' && <ClientsPage contacts={contacts} workOrders={workOrders} billingEntries={billingEntries} recurringJobs={recurringJobs} employees={employees} invoices={invoices} currentUserName={currentUserName} loadData={loadData} targetContactId={targetContactId} clearTarget={() => setTargetContactId(null)} />}
        {page === 'jobs' && <JobsPage contacts={contacts} workOrders={workOrders} employees={employees} invoices={invoices} loadData={loadData} targetWorkOrderId={targetWorkOrderId} clearTarget={() => setTargetWorkOrderId(null)} />}
        {page === 'invoices' && <InvoicesPage invoices={invoices} workOrders={workOrders} contacts={contacts} loadData={loadData} targetInvoiceId={targetInvoiceId} clearTarget={() => setTargetInvoiceId(null)} />}
        {page === 'calendar' && <CalendarPage workOrders={workOrders} contacts={contacts} employees={employees} invoices={invoices} loadData={loadData} />}
        {page === 'employees' && <EmployeesPage employees={employees} workOrders={workOrders} loadData={loadData} />}
        {page === 'payroll' && <PayrollPage employees={employees} timePunches={timePunches} billingEntries={billingEntries} />}
        {page === 'reports' && <ReportsPage contacts={contacts} workOrders={workOrders} employees={employees} billingEntries={billingEntries} invoices={invoices} />}
        {page === 'catalog' && <CatalogPage loadData={loadData} />}
        {page === 'notifications' && <NotificationsPage
          adminNotifications={adminNotifications}
          notifFilter={notifFilter}
          setNotifFilter={setNotifFilter}
          workOrders={workOrders}
          setPage={setPage}
          setTargetWorkOrderId={setTargetWorkOrderId}
          setTargetInvoiceId={setTargetInvoiceId}
          setTargetContactId={setTargetContactId}
        />}
      </main>

      <DialogRoot />
    </div>
  );
}

/* ══════════════════════════════════════════════
   NOTIFICATIONS
   ══════════════════════════════════════════════ */
function NotificationsPage({ adminNotifications, notifFilter, setNotifFilter, workOrders, setPage, setTargetWorkOrderId, setTargetInvoiceId, setTargetContactId }) {
  const filtered = adminNotifications.filter(n => {
    if (notifFilter === 'unread') return !n.read && !n.completed;
    if (notifFilter === 'priority') return n.priority && !n.completed;
    if (notifFilter === 'completed') return n.completed;
    // 'all' = everything except completed (cleaner default)
    return !n.completed;
  });

  const unreadExists = adminNotifications.some(n => !n.read);

  const markAllRead = async () => {
    try {
      const unread = adminNotifications.filter(n => !n.read);
      await Promise.all(
        unread.map(n =>
          updateDoc(doc(db, 'adminNotifications', n.id), {
            read: true,
            readAt: serverTimestamp(),
          })
        )
      );
    } catch (err) {
      console.error('Mark all read error:', err);
    }
  };

  const handleNotifClick = async (n) => {
    // Mark as read
    if (!n.read) {
      try {
        await updateDoc(doc(db, 'adminNotifications', n.id), {
          read: true,
          readAt: serverTimestamp(),
        });
      } catch {}
    }

    // Deep-link to the specific record if available
    if (n.workOrderId) {
      const wo = workOrders.find(w => w.id === n.workOrderId);
      const woStatus = wo?.status || '';
      const goToJobs = !['Estimate Requested', 'Pending', 'Estimate Sent', 'Approved'].includes(woStatus);
      setTargetWorkOrderId(n.workOrderId);
      setPage(goToJobs ? 'jobs' : 'estimates');
    } else if (n.invoiceId) {
      setTargetInvoiceId(n.invoiceId);
      setPage('invoices');
    } else if (n.contactId) {
      setTargetContactId(n.contactId);
      setPage('clients');
    } else if (n.type === 'waitlist' || n.type === 'private_inquiry') {
      setPage('clients');
    }
  };

  return (
    <div className="av2-page">
      <div className="av2-page-header">
        <h1>Notifications</h1>
        {unreadExists && (
          <button className="av2-btn-secondary" onClick={markAllRead}>
            Mark all read
          </button>
        )}
      </div>

      {/* Filter pills */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 18, flexWrap: 'wrap' }}>
        {[
          { id: 'all', label: 'All', count: adminNotifications.filter(n => !n.completed).length },
          { id: 'unread', label: 'Unread', count: adminNotifications.filter(n => !n.read && !n.completed).length },
          { id: 'priority', label: '⭐ Priority', count: adminNotifications.filter(n => n.priority && !n.completed).length },
          { id: 'completed', label: 'Completed', count: adminNotifications.filter(n => n.completed).length },
        ].map(f => (
          <button
            key={f.id}
            onClick={() => setNotifFilter(f.id)}
            style={{
              background: notifFilter === f.id ? '#1D1D1F' : '#fff',
              color: notifFilter === f.id ? '#fff' : '#424245',
              border: notifFilter === f.id ? 'none' : '1px solid rgba(0,0,0,0.12)',
              borderRadius: 999,
              padding: '6px 14px',
              fontFamily: 'var(--av2-font)',
              fontSize: 12,
              fontWeight: 600,
              letterSpacing: '-0.005em',
              cursor: 'pointer',
              boxShadow: notifFilter === f.id ? 'none' : '0 1px 2px rgba(0,0,0,0.04)',
              transition: 'background 0.15s, color 0.15s',
            }}
          >
            {f.label}
            {f.count > 0 && (
              <span style={{ marginLeft: 6, opacity: 0.65, fontSize: 11, fontVariantNumeric: 'tabular-nums' }}>
                {f.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Notifications list */}
      <div style={{
        background: '#fff',
        border: '1px solid rgba(0,0,0,0.08)',
        borderRadius: 14,
        overflow: 'hidden',
        boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
      }}>
        {filtered.length === 0 ? (
          <div style={{ padding: '70px 24px', textAlign: 'center', color: '#86868B' }}>
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ margin: '0 auto 16px', display: 'block', opacity: 0.4 }}>
              <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9" />
              <path d="M13.73 21a2 2 0 01-3.46 0" />
            </svg>
            <div style={{ fontSize: 19, fontWeight: 700, color: '#424245', marginBottom: 6, letterSpacing: '-0.018em' }}>
              {notifFilter === 'all' ? 'All caught up' :
               notifFilter === 'unread' ? 'No unread notifications' :
               notifFilter === 'priority' ? 'No priority items' :
               'No completed items'}
            </div>
            <div style={{ fontSize: 13.5, color: '#86868B', lineHeight: 1.5, letterSpacing: '-0.005em', maxWidth: 420, margin: '0 auto' }}>
              {notifFilter === 'all'
                ? 'New clients, work orders, inquiries, paid invoices, and waitlist signups will appear here.'
                : 'Switch filter to see other notifications.'}
            </div>
          </div>
        ) : (
          filtered.map((n, idx) => (
            <div
              key={n.id}
              onClick={() => handleNotifClick(n)}
              style={{
                padding: '18px 24px',
                borderBottom: idx === filtered.length - 1 ? 'none' : '1px solid rgba(0,0,0,0.05)',
                cursor: 'pointer',
                background: n.completed
                  ? 'rgba(0,0,0,0.025)'
                  : n.read
                    ? 'transparent'
                    : 'rgba(0,122,83,0.04)',
                transition: 'background 0.15s',
                display: 'flex', gap: 14,
                opacity: n.completed ? 0.65 : 1,
              }}
              onMouseEnter={e => e.currentTarget.style.background = n.completed ? 'rgba(0,0,0,0.04)' : (n.read ? '#F5F5F7' : 'rgba(0,122,83,0.07)')}
              onMouseLeave={e => e.currentTarget.style.background = n.completed ? 'rgba(0,0,0,0.025)' : (n.read ? 'transparent' : 'rgba(0,122,83,0.04)')}
            >
              <div style={{ width: 8, paddingTop: 7, flexShrink: 0 }}>
                {!n.read && !n.completed && <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#007A53' }} />}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 4 }}>
                  {n.priority && <span style={{ fontSize: 14, lineHeight: 1, color: '#C8A55A' }}>⭐</span>}
                  <div style={{
                    fontFamily: 'var(--av2-font)',
                    fontSize: 14, fontWeight: 700,
                    color: '#1D1D1F',
                    letterSpacing: '-0.005em',
                    textDecoration: n.completed ? 'line-through' : 'none',
                    flex: 1, minWidth: 0,
                  }}>
                    {n.title}
                  </div>
                </div>
                <div style={{
                  fontFamily: 'var(--av2-font)',
                  fontSize: 13.5,
                  color: '#424245',
                  lineHeight: 1.5,
                  marginBottom: 10,
                  whiteSpace: 'pre-wrap',
                  letterSpacing: '-0.005em',
                }}>
                  {n.body}
                </div>
                {n.workOrderId && (() => {
                  const wo = workOrders.find(w => w.id === n.workOrderId);
                  if (wo?.woNumber) {
                    return (
                      <div style={{
                        display: 'inline-block',
                        fontFamily: 'var(--av2-font)',
                        fontSize: 11, fontWeight: 700,
                        color: '#007A53',
                        background: 'rgba(0,122,83,0.08)',
                        border: '1px solid rgba(0,122,83,0.18)',
                        padding: '3px 10px',
                        borderRadius: 999,
                        marginBottom: 10,
                        letterSpacing: '0.02em',
                        fontVariantNumeric: 'tabular-nums',
                      }}>
                        {wo.woNumber} →
                      </div>
                    );
                  }
                  return null;
                })()}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                  <div style={{
                    fontFamily: 'var(--av2-font)',
                    fontSize: 11.5,
                    color: '#86868B',
                    letterSpacing: '-0.005em',
                    fontVariantNumeric: 'tabular-nums',
                  }}>
                    {n.createdAt?.seconds ? formatRelativeTime(n.createdAt.seconds * 1000) : 'Just now'}
                  </div>
                  <div style={{ display: 'flex', gap: 6 }} onClick={e => e.stopPropagation()}>
                    {!n.completed && (
                      <button
                        onClick={async () => {
                          try {
                            await updateDoc(doc(db, 'adminNotifications', n.id), { priority: !n.priority });
                          } catch (err) { console.error(err); }
                        }}
                        style={{
                          background: n.priority ? 'rgba(200,165,90,0.18)' : '#fff',
                          border: n.priority ? '1px solid rgba(200,165,90,0.55)' : '1px solid rgba(0,0,0,0.12)',
                          color: n.priority ? '#9C7E32' : '#424245',
                          borderRadius: 999,
                          padding: '5px 11px',
                          fontSize: 11.5, fontWeight: 600,
                          fontFamily: 'var(--av2-font)',
                          cursor: 'pointer',
                          display: 'flex', alignItems: 'center', gap: 4,
                          letterSpacing: '-0.005em',
                        }}
                        title={n.priority ? 'Remove priority' : 'Mark priority'}
                      >
                        <span style={{ fontSize: 11 }}>⭐</span>
                        {n.priority ? 'Priority' : ''}
                      </button>
                    )}
                    <button
                      onClick={async () => {
                        try {
                          await updateDoc(doc(db, 'adminNotifications', n.id), {
                            completed: !n.completed,
                            completedAt: !n.completed ? serverTimestamp() : null,
                            read: true,
                          });
                        } catch (err) { console.error(err); }
                      }}
                      style={{
                        background: n.completed ? '#007A53' : '#fff',
                        border: n.completed ? 'none' : '1px solid rgba(0,0,0,0.12)',
                        color: n.completed ? '#fff' : '#424245',
                        borderRadius: 999,
                        padding: '5px 11px',
                        fontSize: 11.5, fontWeight: 600,
                        fontFamily: 'var(--av2-font)',
                        cursor: 'pointer',
                        display: 'flex', alignItems: 'center', gap: 4,
                        letterSpacing: '-0.005em',
                      }}
                      title={n.completed ? 'Mark incomplete' : 'Mark complete'}
                    >
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                      {n.completed ? 'Done' : ''}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════
   DASHBOARD
   ══════════════════════════════════════════════ */
function Dashboard({ contacts, workOrders, billingEntries, recurringJobs, setPage, loadData }) {
  const today = new Date().toISOString().split('T')[0];
  const todayJobs = workOrders.filter(w => w.scheduledDate === today);
  const pendingEstimates = workOrders.filter(w => w.status === 'Estimate Requested' || w.status === 'Pending');
  const needsAttention = workOrders.filter(w => w.status === 'Estimate Sent');
  const completedThisMonth = workOrders.filter(w => {
    if (w.status !== 'Completed' && w.status !== 'Done') return false;
    const ts = w.completedAt?.seconds ? new Date(w.completedAt.seconds * 1000) : null;
    if (!ts) return false;
    const now = new Date();
    return ts.getMonth() === now.getMonth() && ts.getFullYear() === now.getFullYear();
  });

  const monthRevenue = billingEntries.reduce((sum, e) => {
    const ts = e.createdAt?.seconds ? new Date(e.createdAt.seconds * 1000) : null;
    if (!ts) return sum;
    const now = new Date();
    if (ts.getMonth() !== now.getMonth() || ts.getFullYear() !== now.getFullYear()) return sum;
    return sum + ((e.hoursWorked || 0) * 120);
  }, 0);

  const newLeadsThisWeek = contacts.filter(c => {
    if (c.stage !== 'lead') return false;
    const ts = c.createdAt?.seconds ? new Date(c.createdAt.seconds * 1000) : null;
    if (!ts) return false;
    const weekAgo = new Date();
    weekAgo.setDate(weekAgo.getDate() - 7);
    return ts >= weekAgo;
  });

  const activeClients = contacts.filter(c => c.stage === 'client');

  return (
    <div className="av2-page">
      <div className="av2-page-header">
        <h1>Dashboard</h1>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <button className="av2-btn-secondary" onClick={loadData}>
            {Icons.refresh} Refresh
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="av2-kpi-grid">
        <div className="av2-kpi-card">
          <div className="av2-kpi-label">Active Properties</div>
          <div className="av2-kpi-value">{activeClients.length}</div>
          <div className="av2-kpi-sub">Properties in active service</div>
        </div>
        <div className="av2-kpi-card">
          <div className="av2-kpi-label">Pending Estimates</div>
          <div className="av2-kpi-value">{workOrders.filter(w => w.status === 'Estimate Requested' || w.status === 'Pending' || w.status === 'Estimate Sent').length}</div>
          <div className="av2-kpi-sub">{workOrders.filter(w => w.status === 'Estimate Requested' || w.status === 'Pending').length} need pricing</div>
        </div>
        <div className="av2-kpi-card">
          <div className="av2-kpi-label">Jobs Today</div>
          <div className="av2-kpi-value">{todayJobs.length}</div>
          <div className="av2-kpi-sub">{todayJobs.filter(j => j.status === 'Completed' || j.status === 'Done').length} completed</div>
        </div>
        <div className="av2-kpi-card">
          <div className="av2-kpi-label">Revenue (This Month)</div>
          <div className="av2-kpi-value">${monthRevenue.toLocaleString()}</div>
          <div className="av2-kpi-sub">{completedThisMonth.length} jobs completed</div>
        </div>
      </div>

      {/* Action items */}
      <div className="av2-two-col">
        <div className="av2-section">
          <div className="av2-section-header">
            <h3>Needs Attention</h3>
            <span className="av2-section-count">{pendingEstimates.length + needsAttention.length}</span>
          </div>
          {pendingEstimates.length === 0 && needsAttention.length === 0 ? (
            <div className="av2-empty-state">
              {Icons.check}
              <p>All caught up! No pending actions.</p>
            </div>
          ) : (
            <div className="av2-action-list">
              {pendingEstimates.slice(0, 5).map(wo => (
                <div key={wo.id} className="av2-action-item" onClick={() => setPage('estimates')}>
                  <div className="av2-action-icon av2-action-warn">{Icons.alert}</div>
                  <div className="av2-action-content">
                    <div className="av2-action-title">Estimate needed: {wo.title}</div>
                    <div className="av2-action-sub">{wo.clientName || wo.contactName}</div>
                  </div>
                </div>
              ))}
              {needsAttention.slice(0, 5).map(wo => (
                <div key={wo.id} className="av2-action-item" onClick={() => setPage('estimates')}>
                  <div className="av2-action-icon av2-action-info">{Icons.alert}</div>
                  <div className="av2-action-content">
                    <div className="av2-action-title">Waiting on approval: {wo.title}</div>
                    <div className="av2-action-sub">{wo.clientName || wo.contactName} · Estimate sent</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="av2-section">
          <div className="av2-section-header">
            <h3>Today&rsquo;s Schedule</h3>
            <span className="av2-section-count">{todayJobs.length}</span>
          </div>
          {todayJobs.length === 0 ? (
            <div className="av2-empty-state">
              <p>No jobs scheduled today.</p>
            </div>
          ) : (
            <div className="av2-action-list">
              {todayJobs.map(wo => (
                <div key={wo.id} className="av2-action-item" onClick={() => setPage('jobs')}>
                  <div className="av2-action-time">{wo.scheduledTime || '—'}</div>
                  <div className="av2-action-content">
                    <div className="av2-action-title">{wo.title}</div>
                    <div className="av2-action-sub">{wo.clientName || wo.contactName} · {wo.assignedTo || 'Unassigned'}</div>
                  </div>
                  <span className={`av2-badge av2-badge-${(wo.status || '').toLowerCase().replace(/ /g, '-')}`}>{wo.status}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Upcoming Recurring Jobs */}
      {recurringJobs && recurringJobs.filter(r => r.active).length > 0 && (() => {
        const upcoming = recurringJobs
          .filter(r => r.active)
          .map(r => ({
            ...r,
            days: Math.ceil((new Date(r.nextDue) - new Date()) / (1000 * 60 * 60 * 24)),
          }))
          .filter(r => r.days <= 60)
          .sort((a, b) => a.days - b.days);

        if (upcoming.length === 0) return null;

        return (
          <div className="av2-section" style={{ marginTop: 24 }}>
            <div className="av2-section-header">
              <h3>Upcoming Recurring Jobs</h3>
              <span className="av2-section-count">{upcoming.length}</span>
            </div>
            <div className="av2-action-list">
              {upcoming.slice(0, 8).map(r => (
                <div key={r.id} className="av2-action-item" onClick={() => setPage('clients')}>
                  <div className={`av2-action-icon ${r.days < 0 ? 'av2-action-warn' : r.days <= 14 ? 'av2-action-warn' : 'av2-action-info'}`}>
                    {Icons.alert}
                  </div>
                  <div className="av2-action-content">
                    <div className="av2-action-title">{r.title} · {r.clientName}</div>
                    <div className="av2-action-sub">
                      {r.days < 0 ? `Overdue by ${Math.abs(r.days)} days` : r.days === 0 ? 'Due today' : `Due in ${r.days} days`} · {new Date(r.nextDue).toLocaleDateString()}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {/* Quick Stats */}
      <div className="av2-section" style={{ marginTop: 24 }}>
        <div className="av2-section-header">
          <h3>Quick Stats</h3>
        </div>
        <div className="av2-stats-row">
          <div className="av2-stat">
            <div className="av2-stat-label">Total Contacts</div>
            <div className="av2-stat-value">{contacts.length}</div>
          </div>
          <div className="av2-stat">
            <div className="av2-stat-label">Open Jobs</div>
            <div className="av2-stat-value">{workOrders.filter(w => w.status !== 'Completed' && w.status !== 'Done').length}</div>
          </div>
          <div className="av2-stat">
            <div className="av2-stat-label">Completed Jobs (All Time)</div>
            <div className="av2-stat-value">{workOrders.filter(w => w.status === 'Completed' || w.status === 'Done').length}</div>
          </div>
          <div className="av2-stat">
            <div className="av2-stat-label">Conversion Rate</div>
            <div className="av2-stat-value">
              {contacts.filter(c => c.stage === 'lead').length > 0
                ? Math.round((activeClients.length / (activeClients.length + contacts.filter(c => c.stage === 'lead').length)) * 100)
                : 0}%
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════
   LEADS — simplified two-section view
   Top: new leads needing follow-up
   Bottom: everything else, filterable by date + source
   ══════════════════════════════════════════════ */
const LEAD_STAGES = [
  { key: 'new', label: 'New Lead', color: '#1E6091' },
  { key: 'contacted', label: 'Contacted', color: '#D4880F' },
  { key: 'estimate-sent', label: 'Estimate Sent', color: '#8E44AD' },
  { key: 'approved', label: 'Approved', color: '#007A53' },
  { key: 'lost', label: 'Lost', color: '#86868B' },
];

const LEAD_SOURCES = ['direct', 'google', 'nextdoor', 'referral', 'facebook', 'checklist-magnet', 'other'];

function LeadsPage({ contacts, loadData }) {
  const allLeads = contacts.filter(c => c.stage === 'lead' || c.stage === 'converted');
  const [selectedLead, setSelectedLead] = useState(null);
  const [showAddLead, setShowAddLead] = useState(false);
  const [dateFilter, setDateFilter] = useState('30'); // '7' | '30' | '90' | 'all'
  const [sourceFilter, setSourceFilter] = useState('all');

  // Add Lead form
  const [newLead, setNewLead] = useState({
    name: '', email: '', phone: '', address: '', source: 'direct', notes: '',
  });
  const [saving, setSaving] = useState(false);

  const handleAddLead = async () => {
    if (!newLead.name || !newLead.phone) {
      dialog.alert('Name and phone are required.');
      return;
    }
    setSaving(true);
    try {
      await addDoc(collection(db, 'contacts'), {
        ...newLead,
        stage: 'lead',
        leadStage: 'new',
        lifetimeValue: 0,
        jobCount: 0,
        propertyDetails: { sqft: '', yearBuilt: '', gateCode: '', parkingNotes: '', petInfo: '', rooms: {} },
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      await loadData();
      setNewLead({ name: '', email: '', phone: '', address: '', source: 'direct', notes: '' });
      setShowAddLead(false);
    } catch (err) {
      dialog.alert('Error: ' + err.message);
    }
    setSaving(false);
  };

  const updateLeadStage = async (leadId, newStage) => {
    try {
      await updateDoc(doc(db, 'contacts', leadId), {
        leadStage: newStage,
        updatedAt: serverTimestamp(),
      });
      await loadData();
    } catch (err) {
      console.error('Error updating lead:', err);
    }
  };

  const fmtDate = (ts) => {
    if (!ts) return '—';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const daysAgo = (ts) => {
    if (!ts) return null;
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return Math.floor((Date.now() - d.getTime()) / (1000 * 60 * 60 * 24));
  };

  const sourceLabel = (src) => {
    if (!src) return '—';
    if (src === 'checklist-magnet') return 'Checklist magnet';
    return src.charAt(0).toUpperCase() + src.slice(1);
  };

  // Map our lead stages onto existing av2-badge variants
  const stageBadgeClass = (stage) => {
    switch (stage || 'new') {
      case 'new': return 'av2-badge av2-badge-estimate-requested';
      case 'contacted': return 'av2-badge av2-badge-pending';
      case 'estimate-sent': return 'av2-badge av2-badge-in-progress';
      case 'approved': return 'av2-badge av2-badge-completed';
      case 'lost': return 'av2-badge av2-badge-canceled';
      default: return 'av2-badge';
    }
  };

  const stageLabel = (stage) => {
    const s = LEAD_STAGES.find(x => x.key === (stage || 'new'));
    return s ? s.label : 'New Lead';
  };

  // Partition leads
  const newLeads = allLeads
    .filter(l => (l.leadStage || 'new') === 'new')
    .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));

  const otherLeads = allLeads.filter(l => (l.leadStage || 'new') !== 'new');

  const filteredOther = otherLeads
    .filter(l => {
      if (dateFilter !== 'all') {
        const d = daysAgo(l.createdAt);
        if (d === null) return false;
        if (d > parseInt(dateFilter, 10)) return false;
      }
      if (sourceFilter !== 'all' && l.source !== sourceFilter) return false;
      return true;
    })
    .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));

  const sourcesPresent = Array.from(new Set(otherLeads.map(l => l.source).filter(Boolean)));
  const inProgressCount = otherLeads.filter(l => l.leadStage === 'contacted' || l.leadStage === 'estimate-sent').length;

  return (
    <div className="av2-page">
      <div className="av2-page-header">
        <div>
          <h1 className="av2-page-title">Leads</h1>
          <p className="av2-page-sub">New signups land here. Mark contacted as you work them, lost when they go cold.</p>
        </div>
        <button className="av2-btn-primary" onClick={() => setShowAddLead(true)}>
          + Add Lead
        </button>
      </div>

      {/* KPI cards */}
      <div className="av2-kpi-grid">
        <div className="av2-kpi-card">
          <div className="av2-kpi-label">New</div>
          <div className="av2-kpi-value">{newLeads.length}</div>
          <div className="av2-kpi-sub">Need follow-up</div>
        </div>
        <div className="av2-kpi-card">
          <div className="av2-kpi-label">In Progress</div>
          <div className="av2-kpi-value">{inProgressCount}</div>
          <div className="av2-kpi-sub">Contacted or estimate sent</div>
        </div>
        <div className="av2-kpi-card">
          <div className="av2-kpi-label">Total Leads</div>
          <div className="av2-kpi-value">{allLeads.length}</div>
          <div className="av2-kpi-sub">All time</div>
        </div>
      </div>

      {/* NEW LEADS SECTION */}
      <div className="av2-section">
        <div className="av2-section-header">
          <h3>New Leads</h3>
          <span className="av2-section-count">{newLeads.length}</span>
        </div>

        {newLeads.length === 0 ? (
          <div className="av2-empty-state">
            {Icons.check}
            <p>No new leads to act on — all caught up.</p>
          </div>
        ) : (
          <div className="av2-action-list">
            {newLeads.map(lead => {
              const d = daysAgo(lead.createdAt);
              return (
                <div key={lead.id} className="av2-action-item">
                  <div className="av2-action-content" onClick={() => setSelectedLead(lead)} style={{ cursor: 'pointer' }}>
                    <div className="av2-action-title">{lead.name || '(no name)'}</div>
                    <div className="av2-action-sub">
                      {lead.email || lead.phone || '—'} &middot; {sourceLabel(lead.source)} &middot; {fmtDate(lead.createdAt)}
                      {d !== null && d <= 1 ? ' · Today' : ''}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                    <button
                      className="av2-btn-primary"
                      onClick={(e) => { e.stopPropagation(); updateLeadStage(lead.id, 'contacted'); }}
                    >
                      Mark contacted
                    </button>
                    <button
                      className="av2-btn-secondary"
                      onClick={(e) => { e.stopPropagation(); updateLeadStage(lead.id, 'lost'); }}
                    >
                      Lost
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ALL OTHER LEADS SECTION */}
      <div className="av2-section" style={{ marginTop: 24 }}>
        <div className="av2-section-header">
          <h3>All Other Leads</h3>
          <span className="av2-section-count">{filteredOther.length}</span>
        </div>

        <div className="av2-filters-bar">
          <select value={dateFilter} onChange={e => setDateFilter(e.target.value)} className="av2-select">
            <option value="7">Last 7 days</option>
            <option value="30">Last 30 days</option>
            <option value="90">Last 90 days</option>
            <option value="all">All time</option>
          </select>
          {sourcesPresent.length > 1 && (
            <select value={sourceFilter} onChange={e => setSourceFilter(e.target.value)} className="av2-select">
              <option value="all">All sources</option>
              {sourcesPresent.map(s => <option key={s} value={s}>{sourceLabel(s)}</option>)}
            </select>
          )}
        </div>

        {filteredOther.length === 0 ? (
          <div className="av2-empty-state">
            <p>No leads match these filters.</p>
          </div>
        ) : (
          <div className="av2-table-wrap">
            <table className="av2-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Source</th>
                  <th>Added</th>
                  <th>Status</th>
                  <th style={{ textAlign: 'right' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredOther.map(lead => (
                  <tr key={lead.id}>
                    <td>
                      <button
                        onClick={() => setSelectedLead(lead)}
                        style={{ background: 'none', border: 'none', padding: 0, font: 'inherit', color: 'inherit', cursor: 'pointer', textAlign: 'left', textDecoration: 'underline', textDecorationColor: 'rgba(0,0,0,0.15)', textUnderlineOffset: 2 }}
                      >
                        {lead.name || '(no name)'}
                      </button>
                    </td>
                    <td>{lead.email || '—'}</td>
                    <td>{sourceLabel(lead.source)}</td>
                    <td>{fmtDate(lead.createdAt)}</td>
                    <td><span className={stageBadgeClass(lead.leadStage)}>{stageLabel(lead.leadStage)}</span></td>
                    <td style={{ textAlign: 'right' }}>
                      <button className="av2-btn-secondary" onClick={() => setSelectedLead(lead)}>
                        Open
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ADD LEAD MODAL */}
      {showAddLead && (
        <div className="av2-modal-overlay" onClick={() => setShowAddLead(false)}>
          <div className="av2-modal" onClick={e => e.stopPropagation()}>
            <div className="av2-modal-header">
              <h2>Add Lead</h2>
              <button className="av2-modal-close" onClick={() => setShowAddLead(false)}>×</button>
            </div>
            <div className="av2-modal-body">
              <div className="av2-form-field">
                <label>Name *</label>
                <input value={newLead.name} onChange={e => setNewLead({ ...newLead, name: e.target.value })} />
              </div>
              <div className="av2-form-field">
                <label>Phone *</label>
                <input value={newLead.phone} onChange={e => setNewLead({ ...newLead, phone: e.target.value })} />
              </div>
              <div className="av2-form-field">
                <label>Email</label>
                <input type="email" value={newLead.email} onChange={e => setNewLead({ ...newLead, email: e.target.value })} />
              </div>
              <div className="av2-form-field">
                <label>Address</label>
                <input value={newLead.address} onChange={e => setNewLead({ ...newLead, address: e.target.value })} />
              </div>
              <div className="av2-form-field">
                <label>Source</label>
                <select value={newLead.source} onChange={e => setNewLead({ ...newLead, source: e.target.value })} className="av2-select">
                  {LEAD_SOURCES.map(s => <option key={s} value={s}>{sourceLabel(s)}</option>)}
                </select>
              </div>
              <div className="av2-form-field">
                <label>Notes</label>
                <textarea rows={3} value={newLead.notes} onChange={e => setNewLead({ ...newLead, notes: e.target.value })} />
              </div>
            </div>
            <div className="av2-modal-footer">
              <button className="av2-btn-secondary" onClick={() => setShowAddLead(false)}>Cancel</button>
              <button className="av2-btn-primary" onClick={handleAddLead} disabled={saving}>
                {saving ? 'Saving…' : 'Add Lead'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Lead Detail Modal */}
      {selectedLead && (
        <LeadDetail
          lead={selectedLead}
          contacts={contacts}
          onClose={() => setSelectedLead(null)}
          onUpdate={async () => { await loadData(); }}
        />
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════
   LEAD DETAIL MODAL
   ══════════════════════════════════════════════ */
function LeadDetail({ lead, contacts, onClose, onUpdate }) {
  const [editing, setEditing] = useState(false);
  const [showIntake, setShowIntake] = useState(false);
  const [form, setForm] = useState({
    name: lead.name || '',
    email: lead.email || '',
    phone: lead.phone || '',
    address: lead.address || '',
    source: lead.source || 'direct',
    notes: lead.notes || '',
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateDoc(doc(db, 'contacts', lead.id), {
        ...form,
        updatedAt: serverTimestamp(),
      });
      await onUpdate();
      setEditing(false);
    } catch (err) {
      dialog.alert('Error: ' + err.message);
    }
    setSaving(false);
  };

  const handleDelete = async () => {
    if (!(await dialog.confirm(`Delete lead "${lead.name}"? This cannot be undone.`))) return;
    try {
      const { deleteDoc } = await import('firebase/firestore');
      await deleteDoc(doc(db, 'contacts', lead.id));
      await onUpdate();
      onClose();
    } catch (err) {
      dialog.alert('Error: ' + err.message);
    }
  };

  const handleIntake = () => {
    setShowIntake(true);
  };

  return (
    <div className="av2-modal-overlay" onClick={onClose}>
      <div className="av2-modal av2-modal-large" onClick={e => e.stopPropagation()}>
        <div className="av2-modal-header">
          <div>
            <h2>{lead.name}</h2>
            <div className="av2-modal-sub">Lead · Added {lead.createdAt?.seconds ? new Date(lead.createdAt.seconds * 1000).toLocaleDateString() : 'recently'}</div>
          </div>
          <button className="av2-modal-close" onClick={onClose}>{Icons.close}</button>
        </div>

        <div className="av2-modal-body">
          {!editing ? (
            <>
              <div className="av2-detail-grid">
                <div className="av2-detail-item">
                  <div className="av2-detail-label">Phone</div>
                  <div className="av2-detail-value">
                    {lead.phone ? <a href={`tel:${lead.phone}`}>{lead.phone}</a> : '—'}
                  </div>
                </div>
                <div className="av2-detail-item">
                  <div className="av2-detail-label">Email</div>
                  <div className="av2-detail-value">
                    {lead.email ? <a href={`mailto:${lead.email}`}>{lead.email}</a> : '—'}
                  </div>
                </div>
                <div className="av2-detail-item" style={{ gridColumn: '1 / -1' }}>
                  <div className="av2-detail-label">Address</div>
                  <div className="av2-detail-value">{lead.address || '—'}</div>
                </div>
                <div className="av2-detail-item">
                  <div className="av2-detail-label">Source</div>
                  <div className="av2-detail-value" style={{ textTransform: 'capitalize' }}>{lead.source || 'Direct'}</div>
                </div>
                <div className="av2-detail-item">
                  <div className="av2-detail-label">Stage</div>
                  <div className="av2-detail-value">
                    <span className="av2-badge" style={{ background: '#E8F1F8', color: '#1E6091' }}>
                      {LEAD_STAGES.find(s => s.key === (lead.leadStage || 'new'))?.label || 'New Lead'}
                    </span>
                  </div>
                </div>
              </div>

              {lead.notes && (
                <div className="av2-detail-notes">
                  <div className="av2-detail-label">Notes</div>
                  <div className="av2-detail-notes-body">{lead.notes}</div>
                </div>
              )}

              <div className="av2-detail-quick-actions">
                {lead.phone && (
                  <>
                    <a href={`tel:${lead.phone}`} className="av2-btn-secondary">
                      {Icons.phone} Call
                    </a>
                    <a href={`sms:${lead.phone}`} className="av2-btn-secondary">
                      {Icons.mail} Text
                    </a>
                  </>
                )}
              </div>
            </>
          ) : (
            <>
              <div className="av2-form-field">
                <label>Name</label>
                <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
              </div>
              <div className="av2-form-row">
                <div className="av2-form-field">
                  <label>Phone</label>
                  <input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} />
                </div>
                <div className="av2-form-field">
                  <label>Email</label>
                  <input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
                </div>
              </div>
              <div className="av2-form-field">
                <label>Address</label>
                <input value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} />
              </div>
              <div className="av2-form-field">
                <label>Source</label>
                <select value={form.source} onChange={e => setForm({ ...form, source: e.target.value })}>
                  {LEAD_SOURCES.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
                </select>
              </div>
              <div className="av2-form-field">
                <label>Notes</label>
                <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={4} />
              </div>
            </>
          )}
        </div>

        <div className="av2-modal-footer av2-modal-footer-split">
          <div>
            {!editing && (
              <button className="av2-btn-danger" onClick={handleDelete}>
                {Icons.trash} Delete
              </button>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {!editing ? (
              <>
                <button className="av2-btn-secondary" onClick={() => setEditing(true)}>Edit</button>
                <button className="av2-btn-primary" onClick={handleIntake}>Intake as Property →</button>
              </>
            ) : (
              <>
                <button className="av2-btn-secondary" onClick={() => { setEditing(false); setForm({ name: lead.name, email: lead.email, phone: lead.phone, address: lead.address, source: lead.source || 'direct', notes: lead.notes || '' }); }}>Cancel</button>
                <button className="av2-btn-primary" onClick={handleSave} disabled={saving}>
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Property Intake Modal */}
      {showIntake && (
        <PropertyIntakeModal
          contacts={contacts}
          existingLead={lead}
          onClose={() => setShowIntake(false)}
          onSaved={async () => {
            setShowIntake(false);
            await onUpdate();
            onClose();
          }}
        />
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════
   ESTIMATES PAGE
   ══════════════════════════════════════════════ */
const ESTIMATE_STAGES = [
  { key: 'estimate-requested', label: 'Estimate Needed', statuses: ['Estimate Requested', 'Pending'], color: '#D4880F' },
  { key: 'estimate-sent', label: 'Awaiting Approval', statuses: ['Estimate Sent'], color: '#8E44AD' },
  { key: 'declined', label: 'Declined', statuses: ['Declined'], color: '#86868B' },
  { key: 'canceled', label: 'Canceled', statuses: ['Canceled'], color: '#C0392B' },
];

function EstimatesPage({ workOrders, contacts, employees, invoices = [], loadData, targetWorkOrderId, clearTarget }) {
  const [selectedJob, setSelectedJob] = useState(null);
  const [expandedStages, setExpandedStages] = useState({});
  const [search, setSearch] = useState('');
  const [showAddWO, setShowAddWO] = useState(false);
  const [saving, setSaving] = useState(false);

  // Deep-link: auto-open the target work order if it's in this page's data
  useEffect(() => {
    if (!targetWorkOrderId) return;
    const wo = workOrders.find(w => w.id === targetWorkOrderId);
    if (wo) {
      setSelectedJob(wo);
      clearTarget?.();
    }
  }, [targetWorkOrderId, workOrders, clearTarget]);

  const handleApproveWithoutEstimate = async (e, job) => {
    e.stopPropagation();
    if (!(await dialog.confirm(`Approve "${job.title}" without an estimate? It will move to Jobs → Ready to Schedule.`))) return;
    try {
      await updateDoc(doc(db, 'workOrders', job.id), { status: 'Approved' });
      await loadData();
    } catch (err) {
      dialog.alert('Error: ' + err.message);
    }
  };

  const [woForm, setWoForm] = useState({
    title: '',
    description: '',
    serviceType: 'General Repair',
    priority: 'Normal',
    clientId: '',
    clientName: '',
    clientEmail: '',
    clientPhone: '',
    clientAddress: '',
    isNonMember: false,
    nonMemberName: '',
    nonMemberPhone: '',
    nonMemberEmail: '',
    nonMemberAddress: '',
  });

  const SERVICE_TYPES = ['General Repair', 'Plumbing', 'Electrical', 'Drywall / Painting', 'Doors / Windows', 'Carpentry', 'Appliance', 'Exterior / Gutters', 'Assembly / Install', 'Annual Home Health Report', 'Other'];
  const activeClients = contacts.filter(c => c.stage === 'client');

  const handleClientSelect = (clientId) => {
    if (clientId === 'non-member') {
      setWoForm({ ...woForm, clientId: '', isNonMember: true, clientName: '', clientEmail: '', clientPhone: '', clientAddress: '' });
      return;
    }
    const client = activeClients.find(c => c.id === clientId);
    if (client) {
      setWoForm({
        ...woForm,
        clientId: client.id,
        clientName: client.name,
        clientEmail: client.email || '',
        clientPhone: client.phone || '',
        clientAddress: client.address || '',
        isNonMember: false,
      });
    }
  };

  const handleCreateWO = async () => {
    if (!woForm.title) { dialog.alert('Title is required.'); return; }
    if (!woForm.isNonMember && !woForm.clientId) { dialog.alert('Select a property or choose new/one-time client.'); return; }
    if (woForm.isNonMember && !woForm.nonMemberName) { dialog.alert('Name is required for a new/one-time client.'); return; }
    setSaving(true);
    try {
      // Get account number + propertyId for the client
      let accountNumber = null;
      let propertyId = null;
      let propertyNotes = null;
      let secondaryContacts = [];
      if (!woForm.isNonMember && woForm.clientId) {
        const client = contacts.find(c => c.id === woForm.clientId);
        accountNumber = client?.accountNumber || null;
        propertyId = client?.propertyId || null;
        propertyNotes = client?.propertyNotes || null;
        secondaryContacts = client?.secondaryContacts || [];
      }
      await addDoc(collection(db, 'workOrders'), {
        title: woForm.title,
        description: woForm.description,
        serviceType: woForm.serviceType,
        priority: woForm.priority,
        clientName: woForm.isNonMember ? woForm.nonMemberName : woForm.clientName,
        clientEmail: woForm.isNonMember ? woForm.nonMemberEmail : woForm.clientEmail,
        clientId: woForm.isNonMember ? null : woForm.clientId,
        propertyId: propertyId,
        propertyNotes: propertyNotes,
        secondaryContacts: secondaryContacts,
        accountNumber: accountNumber,
        contactPhone: woForm.isNonMember ? woForm.nonMemberPhone : woForm.clientPhone,
        contactName: woForm.isNonMember ? woForm.nonMemberName : woForm.clientName,
        contactAddress: woForm.isNonMember ? woForm.nonMemberAddress : woForm.clientAddress,
        status: 'Estimate Requested',
        isNonMember: woForm.isNonMember,
        photos: [],
        createdAt: serverTimestamp(),
      });
      await loadData();
      setShowAddWO(false);
      setWoForm({ title: '', description: '', serviceType: 'General Repair', priority: 'Normal', clientId: '', clientName: '', clientEmail: '', clientPhone: '', clientAddress: '', isNonMember: false, nonMemberName: '', nonMemberPhone: '', nonMemberEmail: '', nonMemberAddress: '' });
    } catch (err) {
      dialog.alert('Error: ' + err.message);
    }
    setSaving(false);
  };

  const estimateJobs = workOrders.filter(wo => {
    const isEstimateStage = wo.status === 'Estimate Requested' || wo.status === 'Pending' || wo.status === 'Estimate Sent' || wo.status === 'Declined' || wo.status === 'Canceled';
    if (!isEstimateStage) return false;
    if (search && !(
      (wo.title || '').toLowerCase().includes(search.toLowerCase()) ||
      (wo.clientName || wo.contactName || '').toLowerCase().includes(search.toLowerCase()) ||
      (wo.woNumber || '').includes(search)
    )) return false;
    return true;
  });

  const getJobsForStage = (stage) => {
    return estimateJobs.filter(wo => stage.statuses.includes(wo.status))
      .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
  };

  return (
    <div className="av2-page">
      <div className="av2-page-header">
        <h1>Estimates</h1>
        <div style={{ display: 'flex', gap: 12 }}>
          <button className="av2-btn-secondary" onClick={loadData}>
            {Icons.refresh} Refresh
          </button>
          <button className="av2-btn-primary" onClick={() => setShowAddWO(true)}>
            {Icons.plus} New Work Order
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="av2-filters-bar">
        <div className="av2-search-box">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
          <input placeholder="Search by title, client, or WO#..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
      </div>

      {/* Stat strip */}
      <div className="av2-stage-strip" style={{ gridTemplateColumns: `repeat(${ESTIMATE_STAGES.length}, 1fr)` }}>
        {ESTIMATE_STAGES.map(stage => {
          const count = getJobsForStage(stage).length;
          return (
            <div
              key={stage.key}
              className={`av2-stage-stat ${expandedStages[stage.key] === false ? '' : 'active'}`}
              onClick={() => setExpandedStages(prev => ({ ...prev, [stage.key]: prev[stage.key] === false ? true : false }))}
            >
              <span className="av2-stage-stat-dot" style={{ background: stage.color }} />
              <div className="av2-stage-stat-content">
                <div className="av2-stage-stat-count">{count}</div>
                <div className="av2-stage-stat-label">{stage.label}</div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Stage groups */}
      <div className="av2-stage-groups">
        {ESTIMATE_STAGES.map(stage => {
          const jobs = getJobsForStage(stage);
          const collapsed = expandedStages[stage.key] === false;
          return (
            <div key={stage.key} className={`av2-stage-group ${collapsed ? 'collapsed' : ''}`}>
              <div
                className="av2-stage-group-header"
                onClick={() => setExpandedStages(prev => ({ ...prev, [stage.key]: prev[stage.key] === false ? true : false }))}
              >
                <span className="av2-stage-group-dot" style={{ background: stage.color }} />
                <span className="av2-stage-group-title">{stage.label}</span>
                <span className="av2-stage-group-count">{jobs.length}</span>
                <svg className="av2-stage-group-chevron" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </div>
              <div className="av2-stage-group-body">
                {jobs.length === 0 ? (
                  <div className="av2-row-card-empty">No estimates in this stage</div>
                ) : (
                  jobs.map(job => {
                    const dateStr = job.createdAt?.seconds
                      ? new Date(job.createdAt.seconds * 1000).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                      : '—';
                    const showApprove = job.status === 'Estimate Requested' || job.status === 'Pending' || job.status === 'Estimate Sent';
                    return (
                      <div key={job.id} className="av2-row-card" onClick={() => setSelectedJob(job)}>
                        <span className="av2-row-card-ribbon" style={{ background: stage.color }} />
                        {job.woNumber && <div className="av2-row-card-num">{job.woNumber}</div>}
                        <div className="av2-row-card-main">
                          <div className="av2-row-card-title">{job.title || 'Untitled'}</div>
                          <div className="av2-row-card-meta">
                            <span>{job.clientName || job.contactName || 'No client'}</span>
                            {job.accountNumber && (
                              <>
                                <span className="av2-row-card-meta-dot" />
                                <span className="av2-acct-badge" style={{ fontSize: 11 }}>{job.accountNumber}</span>
                              </>
                            )}
                            {job.isNonMember && (
                              <>
                                <span className="av2-row-card-meta-dot" />
                                <span className="av2-nm-badge">One-Time</span>
                              </>
                            )}
                          </div>
                        </div>
                        {job.estimate?.total != null && (
                          <div className="av2-row-card-amount success">${job.estimate.total.toFixed(0)}</div>
                        )}
                        <div className="av2-row-card-trail">{dateStr}</div>
                        {showApprove && (
                          <div className="av2-row-card-action">
                            <button
                              className="av2-btn-secondary"
                              style={{ fontSize: 11, padding: '6px 12px' }}
                              onClick={(e) => handleApproveWithoutEstimate(e, job)}
                            >
                              Approve
                            </button>
                          </div>
                        )}
                        <svg className="av2-row-card-chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="9 18 15 12 9 6" />
                        </svg>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Empty state */}
      {estimateJobs.length === 0 && (
        <div className="av2-section" style={{ marginTop: 20 }}>
          <div className="av2-empty-state">
            <p>No estimates pending. New service requests from clients will appear here.</p>
          </div>
        </div>
      )}

      {selectedJob && (
        <JobDetail
          job={selectedJob}
          workOrders={workOrders}
          contacts={contacts}
          employees={employees}
          invoices={invoices}
          onClose={() => setSelectedJob(null)}
          onUpdate={async () => { await loadData(); setSelectedJob(null); }}
        />
      )}

      {/* New Work Order Modal */}
      {showAddWO && (
        <div className="av2-modal-overlay" onClick={() => setShowAddWO(false)}>
          <div className="av2-modal av2-modal-large" onClick={e => e.stopPropagation()}>
            <div className="av2-modal-header">
              <h2>New Work Order</h2>
              <button className="av2-modal-close" onClick={() => setShowAddWO(false)}>{Icons.close}</button>
            </div>
            <div className="av2-modal-body">
              {/* Client selection */}
              <div className="av2-form-field">
                <label>Client *</label>
                <select
                  value={woForm.isNonMember ? 'non-member' : woForm.clientId}
                  onChange={e => handleClientSelect(e.target.value)}
                >
                  <option value="">Select a property...</option>
                  {activeClients.sort((a, b) => (a.name || '').localeCompare(b.name || '')).map(c => {
                    const pid = c.propertyId || c.accountNumber;
                    return (
                      <option key={c.id} value={c.id}>
                        {pid ? `${pid} — ` : ''}{c.name}{c.address ? ` · ${c.address.split(',')[0]}` : ''}
                      </option>
                    );
                  })}
                  <option value="non-member">— New / One-Time Client —</option>
                </select>
              </div>

              {/* New / one-time client fields */}
              {woForm.isNonMember && (
                <div style={{ background: 'var(--av2-warn-bg)', border: '1px solid #F5DFA0', borderRadius: 8, padding: 14, marginBottom: 14 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--av2-warn)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>Non-Member Details</div>
                  <div className="av2-form-row">
                    <div className="av2-form-field">
                      <label>Name *</label>
                      <input value={woForm.nonMemberName} onChange={e => setWoForm({ ...woForm, nonMemberName: e.target.value })} placeholder="John Smith" />
                    </div>
                    <div className="av2-form-field">
                      <label>Phone</label>
                      <input value={woForm.nonMemberPhone} onChange={e => setWoForm({ ...woForm, nonMemberPhone: e.target.value })} placeholder="(516) 555-1234" />
                    </div>
                  </div>
                  <div className="av2-form-row">
                    <div className="av2-form-field">
                      <label>Email</label>
                      <input value={woForm.nonMemberEmail} onChange={e => setWoForm({ ...woForm, nonMemberEmail: e.target.value })} />
                    </div>
                    <div className="av2-form-field">
                      <label>Address</label>
                      <input value={woForm.nonMemberAddress} onChange={e => setWoForm({ ...woForm, nonMemberAddress: e.target.value })} />
                    </div>
                  </div>
                </div>
              )}

              {/* Selected client preview */}
              {!woForm.isNonMember && woForm.clientId && (
                <div style={{ background: 'var(--av2-bg)', borderRadius: 8, padding: 12, marginBottom: 14 }}>
                  <div style={{ fontSize: 14, fontWeight: 700 }}>{woForm.clientName}</div>
                  <div style={{ fontSize: 11, color: 'var(--av2-text-light)', marginTop: 2 }}>
                    {woForm.clientPhone && <span>{woForm.clientPhone} · </span>}
                    {woForm.clientAddress || 'No address'}
                  </div>
                </div>
              )}

              <div className="av2-form-field">
                <label>Job Title *</label>
                <input value={woForm.title} onChange={e => setWoForm({ ...woForm, title: e.target.value })} placeholder="e.g. Fix leaky kitchen faucet" />
              </div>
              <div className="av2-form-row">
                <div className="av2-form-field">
                  <label>Priority</label>
                  <select value={woForm.priority} onChange={e => setWoForm({ ...woForm, priority: e.target.value })}>
                    <option value="Normal">Normal</option>
                    <option value="High">High</option>
                    <option value="Urgent">Urgent</option>
                  </select>
                </div>
              </div>
              <div className="av2-form-field">
                <label>Description</label>
                <textarea value={woForm.description} onChange={e => setWoForm({ ...woForm, description: e.target.value })} rows={3} placeholder="What needs to be done?" />
              </div>
            </div>
            <div className="av2-modal-footer">
              <button className="av2-btn-secondary" onClick={() => setShowAddWO(false)}>Cancel</button>
              <button className="av2-btn-primary" onClick={handleCreateWO} disabled={saving}>
                {saving ? 'Creating...' : 'Create Work Order'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════
   CLIENTS LIST
   ══════════════════════════════════════════════ */
function ClientsPage({ contacts, workOrders, billingEntries, recurringJobs, employees, invoices, currentUserName, loadData, targetContactId, clearTarget }) {
  const clients = contacts.filter(c => c.stage === 'client' || c.stage === 'former');
  const [selectedClient, setSelectedClient] = useState(null);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('recent');
  const [filterStatus, setFilterStatus] = useState('active');
  const [showIntake, setShowIntake] = useState(false);

  // Keep selectedClient in sync with contacts after any refresh (save, edit, etc.)
  useEffect(() => {
    if (!selectedClient) return;
    const fresh = contacts.find(c => c.id === selectedClient.id);
    if (fresh && fresh !== selectedClient) {
      setSelectedClient(fresh);
    }
  }, [contacts, selectedClient]);

  // Deep-link: auto-open the target contact if it's in this page's data
  useEffect(() => {
    if (!targetContactId) return;
    const c = contacts.find(c => c.id === targetContactId);
    if (c) {
      setSelectedClient(c);
      clearTarget?.();
    }
  }, [targetContactId, contacts, clearTarget]);

  // Calculate LTV and job count for each client
  const enrichedClients = clients.map(c => {
    const clientJobs = workOrders.filter(w => w.clientEmail === c.email || w.clientId === c.id);
    const clientBilling = billingEntries.filter(b => b.clientEmail === c.email || b.clientId === c.id);
    const ltv = clientBilling.reduce((sum, e) => sum + ((e.hoursWorked || 0) * 120), 0);
    const lastJob = clientJobs.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0))[0];
    return { ...c, jobCount: clientJobs.length, ltv, lastJob };
  });

  // Filter
  const filtered = enrichedClients.filter(c => {
    const matchesSearch = !search ||
      (c.name || '').toLowerCase().includes(search.toLowerCase()) ||
      (c.email || '').toLowerCase().includes(search.toLowerCase()) ||
      (c.phone || '').includes(search) ||
      (c.propertyId || '').toLowerCase().includes(search.toLowerCase()) ||
      (c.accountNumber || '').toLowerCase().includes(search.toLowerCase()) ||
      (c.address || '').toLowerCase().includes(search.toLowerCase());
    const matchesStatus = filterStatus === 'all' ||
      (filterStatus === 'active' && c.stage === 'client') ||
      (filterStatus === 'inactive' && c.stage === 'former');
    return matchesSearch && matchesStatus;
  });

  // Sort
  const sorted = [...filtered].sort((a, b) => {
    if (sortBy === 'recent') return (b.updatedAt?.seconds || 0) - (a.updatedAt?.seconds || 0);
    if (sortBy === 'name') return (a.name || '').localeCompare(b.name || '');
    if (sortBy === 'ltv') return (b.ltv || 0) - (a.ltv || 0);
    if (sortBy === 'jobs') return (b.jobCount || 0) - (a.jobCount || 0);
    return 0;
  });

  // Repeat clients (3+ jobs) — proxy for "loyal" customers in on-demand model
  const repeatClients = enrichedClients.filter(c => (c.jobCount || 0) >= 3);
  const recentClients = enrichedClients.filter(c => {
    if (!c.lastJob) return false;
    const ts = c.lastJob.completedAt?.seconds || c.lastJob.createdAt?.seconds || 0;
    if (!ts) return false;
    const ninetyDaysAgo = Date.now() / 1000 - (90 * 86400);
    return ts >= ninetyDaysAgo;
  });

  return (
    <div className="av2-page">
      <div className="av2-page-header">
        <h1>Properties</h1>
        <div style={{ display: 'flex', gap: 10 }}>
          <button className="av2-btn-secondary" onClick={loadData}>
            {Icons.refresh} Refresh
          </button>
          <button className="av2-btn-primary" onClick={() => setShowIntake(true)}>
            + Intake New Property
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="av2-filters-bar">
        <div className="av2-search-box">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
          <input placeholder="Search by RH-####, name, email, phone, or address..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="av2-select">
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
          <option value="all">All</option>
        </select>
        <select value={sortBy} onChange={e => setSortBy(e.target.value)} className="av2-select">
          <option value="recent">Recently Updated</option>
          <option value="name">Name A-Z</option>
          <option value="ltv">Lifetime Value</option>
          <option value="jobs">Most Jobs</option>
        </select>
      </div>

      {/* Stats */}
      <div className="av2-client-stats">
        <div className="av2-stat-chip">
          <div className="av2-stat-chip-value">{clients.length}</div>
          <div className="av2-stat-chip-label">Total Properties</div>
        </div>
        <div className="av2-stat-chip">
          <div className="av2-stat-chip-value">{recentClients.length}</div>
          <div className="av2-stat-chip-label">Active in 90 days</div>
        </div>
        <div className="av2-stat-chip">
          <div className="av2-stat-chip-value">{repeatClients.length}</div>
          <div className="av2-stat-chip-label">Repeat (3+ jobs)</div>
        </div>
        <div className="av2-stat-chip">
          <div className="av2-stat-chip-value">
            ${enrichedClients.reduce((sum, c) => sum + (c.ltv || 0), 0).toLocaleString()}
          </div>
          <div className="av2-stat-chip-label">Total LTV</div>
        </div>
      </div>

      {/* Client Cards Grid */}
      {sorted.length === 0 ? (
        <div className="av2-section">
          <div className="av2-empty-state">
            <p>{search ? 'No properties match your search.' : 'No properties yet. Intake a property from the Leads page, or click "Intake New Property" above.'}</p>
          </div>
        </div>
      ) : (
        <div className="av2-client-grid">
          {sorted.map(client => (
            <div key={client.id} className="av2-client-card" onClick={() => setSelectedClient(client)}>
              <div className="av2-client-card-header">
                <div className="av2-client-avatar">{(client.name || '?').charAt(0).toUpperCase()}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace', fontSize: 11, fontWeight: 700, color: 'var(--av2-green)', letterSpacing: 0.5, marginBottom: 2 }}>
                    {client.propertyId || client.accountNumber || 'RH-????'}
                  </div>
                  <div className="av2-client-card-name">{client.name || 'Unnamed'}</div>
                  <div className="av2-client-card-plan">
                    {client.stage === 'former' ? (
                      <span className="av2-plan-badge" style={{ background: 'var(--av2-danger-bg)', color: 'var(--av2-danger)' }}>Inactive</span>
                    ) : (client.jobCount || 0) >= 3 ? (
                      <span className="av2-plan-badge av2-plan-total">Repeat</span>
                    ) : (
                      <span className="av2-plan-badge">Active</span>
                    )}
                  </div>
                </div>
              </div>
              <div className="av2-client-card-info">
                {client.phone && (
                  <div className="av2-client-card-row">
                    {Icons.phone} <span>{client.phone}</span>
                  </div>
                )}
                {client.address && (
                  <div className="av2-client-card-row">
                    {Icons.mapPin} <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{client.address}</span>
                  </div>
                )}
              </div>
              <div className="av2-client-card-footer">
                <div className="av2-client-metric">
                  <div className="av2-client-metric-value">{client.jobCount}</div>
                  <div className="av2-client-metric-label">Jobs</div>
                </div>
                <div className="av2-client-metric">
                  <div className="av2-client-metric-value">${client.ltv.toFixed(0)}</div>
                  <div className="av2-client-metric-label">LTV</div>
                </div>
                <div className="av2-client-metric">
                  <div className="av2-client-metric-value">
                    {client.lastJob?.createdAt?.seconds
                      ? Math.floor((Date.now() - client.lastJob.createdAt.seconds * 1000) / (1000 * 60 * 60 * 24))
                      : '—'}
                  </div>
                  <div className="av2-client-metric-label">Days ago</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {selectedClient && (
        <ClientDetail
          client={selectedClient}
          workOrders={workOrders}
          billingEntries={billingEntries}
          recurringJobs={recurringJobs}
          employees={employees}
          invoices={invoices}
          currentUserName={currentUserName}
          onClose={() => setSelectedClient(null)}
          onUpdate={async () => { await loadData(); }}
        />
      )}

      {showIntake && (
        <PropertyIntakeModal
          contacts={contacts}
          existingLead={null}
          onClose={() => setShowIntake(false)}
          onSaved={async (newId) => {
            setShowIntake(false);
            await loadData();
            // Auto-open the new property
            const fresh = contacts.find(c => c.id === newId);
            if (fresh) setSelectedClient(fresh);
          }}
        />
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════
   PROPERTY INTAKE MODAL
   Used from Properties page (+ Intake New Property)
   and from Lead Detail (Intake as Property).
   ══════════════════════════════════════════════ */
function PropertyIntakeModal({ contacts, existingLead, onClose, onSaved }) {
  const [propertyId, setPropertyId] = useState('');
  const [propertyIdError, setPropertyIdError] = useState('');
  const [form, setForm] = useState({
    name: existingLead?.name || '',
    email: existingLead?.email || '',
    phone: existingLead?.phone || '',
    address: existingLead?.address || '',
    propertyNotes: '',
    notes: existingLead?.notes || '',
  });
  const [saving, setSaving] = useState(false);

  // Compute next suggested RH-#### based on existing properties
  const suggestedNext = (() => {
    const existing = (contacts || [])
      .map(c => c.propertyId || c.accountNumber || '')
      .filter(id => /^RH-\d{4,}$/i.test(id))
      .map(id => parseInt(id.replace(/^RH-/i, ''), 10))
      .filter(n => !isNaN(n));
    const max = existing.length ? Math.max(...existing) : 0;
    const next = max + 1;
    return `RH-${String(next).padStart(4, '0')}`;
  })();

  const validate = () => {
    setPropertyIdError('');
    const id = propertyId.trim().toUpperCase();
    if (!id) {
      setPropertyIdError('Property ID is required.');
      return false;
    }
    if (!/^RH-\d{4,}$/i.test(id)) {
      setPropertyIdError('Format: RH-#### (e.g. RH-0001)');
      return false;
    }
    const dupe = (contacts || []).find(c =>
      (c.propertyId || '').toUpperCase() === id ||
      (c.accountNumber || '').toUpperCase() === id
    );
    if (dupe) {
      setPropertyIdError(`Property ID ${id} is already in use by ${dupe.name || 'another property'}.`);
      return false;
    }
    if (!form.name.trim()) {
      setPropertyIdError('Primary contact name is required.');
      return false;
    }
    if (!form.address.trim()) {
      setPropertyIdError('Address is required.');
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const newDoc = await addDoc(collection(db, 'contacts'), {
        propertyId: propertyId.trim().toUpperCase(),
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        phone: form.phone.trim(),
        address: form.address.trim(),
        propertyNotes: form.propertyNotes.trim(),
        notes: form.notes.trim(),
        secondaryContacts: [],
        stage: 'client',
        leadStage: null,
        memberSince: serverTimestamp(),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        intakeFromLeadId: existingLead?.id || null,
      });

      // If intaken from an existing lead, mark the lead as converted
      if (existingLead?.id) {
        await updateDoc(doc(db, 'contacts', existingLead.id), {
          stage: 'converted',
          convertedToPropertyId: newDoc.id,
          convertedAt: serverTimestamp(),
        });
      }

      onSaved?.(newDoc.id);
    } catch (err) {
      setPropertyIdError('Error saving: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="av2-modal-overlay" onClick={onClose}>
      <div className="av2-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 600 }}>
        <div className="av2-modal-header">
          <h2>{existingLead ? 'Intake as Property' : 'Intake New Property'}</h2>
          <button className="av2-modal-close" onClick={onClose}>{Icons.close}</button>
        </div>
        <div style={{ padding: 24 }}>
          <div style={{ background: 'var(--av2-info-bg)', border: '1px solid #C8DEF0', borderRadius: 8, padding: 14, marginBottom: 20, fontSize: 13, color: 'var(--av2-text)' }}>
            <strong>Suggested next ID:</strong>{' '}
            <span style={{ fontFamily: 'ui-monospace, monospace', fontWeight: 700, color: 'var(--av2-green)' }}>{suggestedNext}</span>
            <button
              type="button"
              className="av2-btn-link"
              style={{ marginLeft: 10, fontSize: 12 }}
              onClick={() => setPropertyId(suggestedNext)}
            >Use this</button>
          </div>

          <div className="av2-form-field">
            <label>Property ID (RH-####) <span style={{ color: 'var(--av2-danger)' }}>*</span></label>
            <input
              value={propertyId}
              onChange={e => { setPropertyId(e.target.value.toUpperCase()); setPropertyIdError(''); }}
              placeholder={suggestedNext}
              style={{ fontFamily: 'ui-monospace, monospace', textTransform: 'uppercase' }}
              maxLength={12}
            />
            <div style={{ fontSize: 11, color: 'var(--av2-text-muted)', marginTop: 4 }}>
              Format: RH-#### (e.g. {suggestedNext}). Each property gets its own unique ID.
            </div>
          </div>

          <div className="av2-form-field">
            <label>Primary Contact Name <span style={{ color: 'var(--av2-danger)' }}>*</span></label>
            <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Jane Smith" />
          </div>

          <div className="av2-form-row">
            <div className="av2-form-field">
              <label>Phone</label>
              <input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="(516) 555-1234" />
            </div>
            <div className="av2-form-field">
              <label>Email</label>
              <input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="jane@example.com" />
            </div>
          </div>

          <div className="av2-form-field">
            <label>Property Address <span style={{ color: 'var(--av2-danger)' }}>*</span></label>
            <input value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} placeholder="123 Main St, Lindenhurst, NY 11757" />
          </div>

          <div className="av2-form-field">
            <label>Property Notes</label>
            <textarea
              value={form.propertyNotes}
              onChange={e => setForm({ ...form, propertyNotes: e.target.value })}
              rows={3}
              placeholder="Gate code, dog, main shutoff location, parking notes, etc."
            />
          </div>

          <div className="av2-form-field">
            <label>Internal Notes</label>
            <textarea
              value={form.notes}
              onChange={e => setForm({ ...form, notes: e.target.value })}
              rows={2}
              placeholder="Anything you want to remember about this client / their project history"
            />
          </div>

          {propertyIdError && (
            <div style={{ marginTop: 10, padding: 12, background: 'var(--av2-danger-bg)', border: '1px solid var(--av2-danger)', borderRadius: 6, color: 'var(--av2-danger)', fontSize: 13 }}>
              {propertyIdError}
            </div>
          )}

          <div style={{ display: 'flex', gap: 10, marginTop: 20, justifyContent: 'flex-end' }}>
            <button className="av2-btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
            <button className="av2-btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : 'Create Property'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════
   CLIENT PROFILE HUB (Detail Modal)
   ══════════════════════════════════════════════ */
function ClientDetail({ client, workOrders, billingEntries, recurringJobs, employees, invoices, currentUserName, onClose, onUpdate }) {
  const [tab, setTab] = useState('overview');
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    propertyId: client.propertyId || client.accountNumber || '',
    name: client.name || '',
    email: client.email || '',
    phone: client.phone || '',
    address: client.address || '',
    propertyNotes: client.propertyNotes || '',
    notes: client.notes || '',
    preferredTech: client.preferredTech || '',
    contractSigned: !!client.contractSigned,
  });
  const [secondaryContacts, setSecondaryContacts] = useState(client.secondaryContacts || []);
  const [propertyForm, setPropertyForm] = useState({
    sqft: client.propertyDetails?.sqft || '',
    yearBuilt: client.propertyDetails?.yearBuilt || '',
    gateCode: client.propertyDetails?.gateCode || '',
    parkingNotes: client.propertyDetails?.parkingNotes || '',
    petInfo: client.propertyDetails?.petInfo || '',
    otherNotes: client.propertyDetails?.otherNotes || '',
  });
  const [saving, setSaving] = useState(false);
  const [showCreateWO, setShowCreateWO] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [woForm, setWoForm] = useState({
    title: '',
    description: '',
    serviceType: 'General Repair',
    priority: 'Normal',
    skipEstimate: false,
  });

  // Tags
  const [tags, setTags] = useState(client.tags || []);
  const [newTag, setNewTag] = useState('');
  const TAG_SUGGESTIONS = ['VIP', 'Referral Source', 'Slow Payer', 'New Construction', 'Elderly', 'Pet Household', 'Gated Property', 'HOA', 'Seasonal', 'Cash Preferred', 'High Value', 'Difficult Access'];

  // Activity log
  const [activities, setActivities] = useState(client.activities || []);
  const [activityNote, setActivityNote] = useState('');
  const [activityType, setActivityType] = useState('note');

  // Additional properties
  const [properties, setProperties] = useState(client.properties || []);
  const [showAddProperty, setShowAddProperty] = useState(false);
  const [propForm, setPropForm] = useState({ name: '', address: '', sqft: '', yearBuilt: '', gateCode: '', parkingNotes: '', petInfo: '', notes: '' });

  const techList = (employees || []).filter(e => e.status !== 'terminated').map(e => e.name);

  const SERVICE_TYPES = ['General Repair', 'Plumbing', 'Electrical', 'Drywall / Painting', 'Doors / Windows', 'Carpentry', 'Appliance', 'Exterior / Gutters', 'Assembly / Install', 'Annual Home Health Report', 'Other'];

  const clientJobs = workOrders.filter(w => w.clientEmail === client.email || w.clientId === client.id);
  const clientBilling = billingEntries.filter(b => b.clientEmail === client.email || b.clientId === client.id);
  const ltv = clientBilling.reduce((sum, e) => sum + ((e.hoursWorked || 0) * 80), 0);

  const handleCreateWorkOrder = async () => {
    if (!woForm.title) {
      dialog.alert('Title is required.');
      return;
    }
    setSaving(true);
    try {
      await addDoc(collection(db, 'workOrders'), {
        title: woForm.title,
        description: woForm.description,
        serviceType: woForm.serviceType,
        priority: woForm.priority,
        clientName: client.name,
        clientEmail: client.email,
        clientId: client.id,
        propertyId: client.propertyId || null,
        propertyNotes: client.propertyNotes || null,
        secondaryContacts: client.secondaryContacts || [],
        accountNumber: client.accountNumber || null,
        contactPhone: client.phone,
        contactName: client.name,
        contactAddress: client.address,
        status: woForm.skipEstimate ? 'Approved' : 'Estimate Requested',
        isNonMember: false,
        photos: [],
        createdAt: serverTimestamp(),
      });
      await onUpdate();
      setShowCreateWO(false);
      setWoForm({ title: '', description: '', serviceType: 'General Repair', priority: 'Normal', skipEstimate: false });
      dialog.alert(woForm.skipEstimate
        ? 'Work order created as Approved! Schedule it in Jobs or Calendar.'
        : 'Work order created! Fill in an estimate and send it to the client.');
    } catch (err) {
      dialog.alert('Error: ' + err.message);
    }
    setSaving(false);
  };

  const handleDeactivateClient = async () => {
    if (!(await dialog.confirm(`Mark ${client.name} as inactive? They will be moved out of your active client list. This can be reversed.`))) return;
    setSaving(true);
    try {
      await updateDoc(doc(db, 'contacts', client.id), {
        stage: 'former',
        deactivatedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      await onUpdate();
      onClose();
    } catch (err) {
      dialog.alert('Error: ' + err.message);
    }
    setSaving(false);
  };

  const handleReactivateClient = async () => {
    if (!(await dialog.confirm(`Reactivate ${client.name}?`))) return;
    try {
      await updateDoc(doc(db, 'contacts', client.id), {
        stage: 'client',
        deactivatedAt: null,
        updatedAt: serverTimestamp(),
      });
      await onUpdate();
      onClose();
    } catch (err) {
      dialog.alert('Error: ' + err.message);
    }
  };

  // ── Contract upload & toggle ──
  const [uploadingContract, setUploadingContract] = useState(false);

  const handleUploadContract = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingContract(true);
    try {
      const fileName = `contracts/${client.id}/${Date.now()}_${file.name}`;
      const storageRef = ref(storage, fileName);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      await updateDoc(doc(db, 'contacts', client.id), {
        contractUrl: url,
        contractFileName: file.name,
        contractUploadedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      await onUpdate();
    } catch (err) {
      dialog.alert('Error uploading contract: ' + err.message);
    }
    setUploadingContract(false);
  };

  const handleToggleContractSigned = async () => {
    try {
      const next = !client.contractSigned;
      await updateDoc(doc(db, 'contacts', client.id), {
        contractSigned: next,
        contractSignedAt: next ? serverTimestamp() : null,
        updatedAt: serverTimestamp(),
      });
      await onUpdate();
    } catch (err) {
      dialog.alert('Error: ' + err.message);
    }
  };

  const handleRemoveContract = async () => {
    if (!(await dialog.confirm('Remove the uploaded contract file? This will also mark the contract as unsigned.'))) return;
    try {
      await updateDoc(doc(db, 'contacts', client.id), {
        contractUrl: null,
        contractFileName: null,
        contractUploadedAt: null,
        contractSigned: false,
        contractSignedAt: null,
        updatedAt: serverTimestamp(),
      });
      await onUpdate();
    } catch (err) {
      dialog.alert('Error: ' + err.message);
    }
  };

  // ── Home Health Report upload ──
  const [uploadingHHR, setUploadingHHR] = useState(false);

  const handleUploadHHR = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      dialog.alert('Please upload a PDF file.');
      return;
    }
    setUploadingHHR(true);
    try {
      const fileName = `health-reports/${client.id}/${Date.now()}_${file.name}`;
      const storageRef = ref(storage, fileName);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      await updateDoc(doc(db, 'contacts', client.id), {
        healthReportUrl: url,
        healthReportFileName: file.name,
        healthReportUploadedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      await onUpdate();
    } catch (err) {
      dialog.alert('Error uploading report: ' + err.message);
    }
    setUploadingHHR(false);
  };

  const handleRemoveHHR = async () => {
    if (!(await dialog.confirm('Remove the uploaded Home Health Report?'))) return;
    try {
      await updateDoc(doc(db, 'contacts', client.id), {
        healthReportUrl: null,
        healthReportFileName: null,
        healthReportUploadedAt: null,
        updatedAt: serverTimestamp(),
      });
      await onUpdate();
    } catch (err) {
      dialog.alert('Error: ' + err.message);
    }
  };

  const completedJobs = clientJobs.filter(j => j.status === 'Completed' || j.status === 'Done');
  const activeJobs = clientJobs.filter(j => j.status !== 'Completed' && j.status !== 'Done');

  const handleSaveProfile = async () => {
    // Validate propertyId
    const newPropId = (form.propertyId || '').trim().toUpperCase();
    if (newPropId && !/^RH-\d{4,}$/i.test(newPropId)) {
      dialog.alert('Property ID must be in format RH-#### (e.g. RH-0001)');
      return;
    }
    setSaving(true);
    try {
      await updateDoc(doc(db, 'contacts', client.id), {
        propertyId: newPropId,
        name: form.name,
        email: form.email,
        phone: form.phone,
        address: form.address,
        propertyNotes: form.propertyNotes,
        notes: form.notes,
        preferredTech: form.preferredTech,
        secondaryContacts,
        tags,
        updatedAt: serverTimestamp(),
      });
      await onUpdate();
      setEditing(false);
    } catch (err) {
      dialog.alert('Error: ' + err.message);
    }
    setSaving(false);
  };

  // Tags handlers
  const handleAddTag = async (tag) => {
    const t = tag || newTag.trim();
    if (!t || tags.includes(t)) return;
    const updated = [...tags, t];
    setTags(updated);
    setNewTag('');
    await updateDoc(doc(db, 'contacts', client.id), { tags: updated, updatedAt: serverTimestamp() });
    await onUpdate();
  };

  const handleRemoveTag = async (tag) => {
    const updated = tags.filter(t => t !== tag);
    setTags(updated);
    await updateDoc(doc(db, 'contacts', client.id), { tags: updated, updatedAt: serverTimestamp() });
    await onUpdate();
  };

  // Activity log handlers
  const handleAddActivity = async () => {
    if (!activityNote.trim()) return;
    const entry = {
      type: activityType,
      note: activityNote.trim(),
      timestamp: new Date().toISOString(),
      by: currentUserName,
    };
    const updated = [entry, ...activities];
    setActivities(updated);
    setActivityNote('');
    await updateDoc(doc(db, 'contacts', client.id), { activities: updated, updatedAt: serverTimestamp() });
    await onUpdate();
  };

  // Properties handlers
  const handleAddProperty = async () => {
    if (!propForm.address) { dialog.alert('Address required.'); return; }
    const updated = [...properties, { ...propForm, id: Date.now().toString() }];
    setProperties(updated);
    await updateDoc(doc(db, 'contacts', client.id), { properties: updated, updatedAt: serverTimestamp() });
    setPropForm({ name: '', address: '', sqft: '', yearBuilt: '', gateCode: '', parkingNotes: '', petInfo: '', notes: '' });
    setShowAddProperty(false);
    await onUpdate();
  };

  const handleDeleteProperty = async (id) => {
    if (!(await dialog.confirm('Remove this property?'))) return;
    const updated = properties.filter(p => p.id !== id);
    setProperties(updated);
    await updateDoc(doc(db, 'contacts', client.id), { properties: updated, updatedAt: serverTimestamp() });
    await onUpdate();
  };

  return (
    <div className="av2-modal-overlay" onClick={onClose}>
      <div className="av2-modal av2-modal-xl" onClick={e => e.stopPropagation()}>
        {/* ── Header ── */}
        <div className="av2-client-modal-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, flex: 1, minWidth: 0 }}>
            <div className="av2-client-avatar av2-client-avatar-lg">
              {(client.name || '?').charAt(0).toUpperCase()}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace', fontSize: 12, fontWeight: 700, color: 'var(--av2-green)', letterSpacing: 0.8, marginBottom: 4 }}>
                {client.propertyId || client.accountNumber || 'RH-????'}
              </div>
              <h2>{client.name}</h2>
              <div className="av2-client-modal-sub">
                {(client.jobCount || clientJobs.length) >= 3 ? (
                  <span className="av2-plan-badge av2-plan-total">Repeat</span>
                ) : (
                  <span className="av2-plan-badge">Active</span>
                )}
                {' · '}
                Property since {client.memberSince?.seconds ? new Date(client.memberSince.seconds * 1000).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : (client.createdAt?.seconds ? new Date(client.createdAt.seconds * 1000).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : 'recently')}
              </div>
            </div>
          </div>
          <button className="av2-modal-close" onClick={onClose}>{Icons.close}</button>
        </div>

        {/* ── Quick Stats Strip ── */}
        <div className="av2-client-stats-strip">
          <div className="av2-client-stat-strip">
            <div className="av2-client-stat-strip-value">${ltv.toLocaleString()}</div>
            <div className="av2-client-stat-strip-label">Lifetime Value</div>
          </div>
          <div className="av2-client-stat-strip">
            <div className="av2-client-stat-strip-value">{clientJobs.length}</div>
            <div className="av2-client-stat-strip-label">Total Jobs</div>
          </div>
          <div className="av2-client-stat-strip">
            <div className="av2-client-stat-strip-value">{activeJobs.length}</div>
            <div className="av2-client-stat-strip-label">Active</div>
          </div>
          <div className="av2-client-stat-strip">
            <div className="av2-client-stat-strip-value">
              {client.lastJob?.createdAt?.seconds
                ? Math.floor((Date.now() - client.lastJob.createdAt.seconds * 1000) / (1000 * 60 * 60 * 24)) + 'd'
                : '—'}
            </div>
            <div className="av2-client-stat-strip-label">Since last job</div>
          </div>
        </div>

        {/* ── Tabs ── */}
        <div className="av2-client-tabs">
          <button className={`av2-client-tab ${tab === 'overview' ? 'active' : ''}`} onClick={() => setTab('overview')}>Overview</button>
          <button className={`av2-client-tab ${tab === 'activity' ? 'active' : ''}`} onClick={() => setTab('activity')}>Activity Log</button>
          <button className={`av2-client-tab ${tab === 'history' ? 'active' : ''}`} onClick={() => setTab('history')}>Jobs</button>
          <button className={`av2-client-tab ${tab === 'billing' ? 'active' : ''}`} onClick={() => setTab('billing')}>Billing</button>
          <button className={`av2-client-tab ${tab === 'comms' ? 'active' : ''}`} onClick={() => setTab('comms')}>Templates</button>
        </div>

        <div className="av2-modal-body" style={{ padding: 0 }}>
          {/* ── Overview Tab ── */}
          {tab === 'overview' && (
            <div style={{ padding: 24 }}>
              {!editing ? (
                <>
                  <div className="av2-detail-grid">
                    <div className="av2-detail-item">
                      <div className="av2-detail-label">Property ID</div>
                      <div className="av2-detail-value" style={{ fontFamily: 'ui-monospace, monospace', fontWeight: 700, color: 'var(--av2-green)' }}>
                        {client.propertyId || client.accountNumber || '—'}
                      </div>
                    </div>
                    <div className="av2-detail-item">
                      <div className="av2-detail-label">Property Since</div>
                      <div className="av2-detail-value">{
                        client.memberSince?.seconds
                          ? new Date(client.memberSince.seconds * 1000).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
                          : (client.createdAt?.seconds ? new Date(client.createdAt.seconds * 1000).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : '—')
                      }</div>
                    </div>
                    <div className="av2-detail-item" style={{ gridColumn: '1 / -1' }}>
                      <div className="av2-detail-label">Address</div>
                      <div className="av2-detail-value">{client.address || '—'}</div>
                    </div>
                    <div className="av2-detail-item">
                      <div className="av2-detail-label">Primary Contact</div>
                      <div className="av2-detail-value">{client.name || '—'}</div>
                    </div>
                    <div className="av2-detail-item">
                      <div className="av2-detail-label">Phone</div>
                      <div className="av2-detail-value">
                        {client.phone ? <a href={`tel:${client.phone}`}>{client.phone}</a> : '—'}
                      </div>
                    </div>
                    <div className="av2-detail-item" style={{ gridColumn: '1 / -1' }}>
                      <div className="av2-detail-label">Email</div>
                      <div className="av2-detail-value" style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        {client.email ? <a href={`mailto:${client.email}`}>{client.email}</a> : '—'}
                        {client.email && (
                          client.emailVerified ? (
                            <span title="Email verified" style={{ fontSize: 10, fontWeight: 700, color: '#1D6B45', background: 'rgba(29,107,69,0.10)', border: '1px solid rgba(29,107,69,0.20)', padding: '2px 8px', borderRadius: 4, letterSpacing: 0.4, textTransform: 'uppercase' }}>
                              Verified
                            </span>
                          ) : (
                            <span title="Email not yet verified" style={{ fontSize: 10, fontWeight: 700, color: '#7A5A18', background: 'rgba(212,136,15,0.10)', border: '1px solid rgba(212,136,15,0.25)', padding: '2px 8px', borderRadius: 4, letterSpacing: 0.4, textTransform: 'uppercase' }}>
                              Pending
                            </span>
                          )
                        )}
                      </div>
                    </div>
                    <div className="av2-detail-item">
                      <div className="av2-detail-label">Preferred Tech</div>
                      <div className="av2-detail-value">{client.preferredTech || 'No preference'}</div>
                    </div>
                  </div>

                  {/* ── Secondary Contacts ── */}
                  {(client.secondaryContacts || []).length > 0 && (
                    <div style={{ marginTop: 24 }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--av2-text-muted)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10 }}>
                        Secondary Contacts
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {(client.secondaryContacts || []).map((sc, i) => (
                          <div key={i} style={{ display: 'flex', gap: 10, padding: '10px 12px', background: 'var(--av2-bg)', border: '1px solid var(--av2-border)', borderRadius: 6, alignItems: 'center', fontSize: 13 }}>
                            <span style={{ fontWeight: 700 }}>{sc.name || '—'}</span>
                            <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--av2-text-muted)', textTransform: 'uppercase', letterSpacing: 0.6, background: 'var(--av2-border)', padding: '2px 8px', borderRadius: 4 }}>{(sc.role || 'other').replace('-', ' ')}</span>
                            {sc.phone && <span><a href={`tel:${sc.phone}`}>{sc.phone}</a></span>}
                            {sc.email && <span><a href={`mailto:${sc.email}`}>{sc.email}</a></span>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* ── Property Notes ── */}
                  {client.propertyNotes && (
                    <div style={{ marginTop: 24 }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--av2-text-muted)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10 }}>
                        Property Notes
                      </div>
                      <div style={{ padding: 14, background: 'var(--av2-warn-bg, #FEF7E0)', border: '1px solid var(--av2-warn-border, #F5DFA0)', borderRadius: 6, fontSize: 13, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                        {client.propertyNotes}
                      </div>
                    </div>
                  )}

                  {/* ── Notes ── */}
                  <div style={{ marginTop: 24 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--av2-text-muted)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10 }}>
                      Notes
                    </div>
                    {client.notes ? (
                      <div style={{ fontSize: 13, color: 'var(--av2-text)', whiteSpace: 'pre-wrap', lineHeight: 1.5, background: 'var(--av2-bg)', border: '1px solid var(--av2-border)', borderRadius: 6, padding: '10px 12px' }}>
                        {client.notes}
                      </div>
                    ) : (
                      <div style={{ fontSize: 13, color: 'var(--av2-text-muted)' }}>No notes</div>
                    )}
                  </div>

                  {/* ── Contract ── */}
                  <div style={{ marginTop: 24 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--av2-text-muted)', textTransform: 'uppercase', letterSpacing: 1 }}>
                        Contract
                      </div>
                      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                        {client.contractSigned ? (
                          <span style={{ fontSize: 11, fontWeight: 700, color: '#007A53', background: 'rgba(0,122,83,0.08)', border: '1px solid rgba(0,122,83,0.18)', borderRadius: 4, padding: '3px 8px' }}>✓ Signed</span>
                        ) : (
                          <span style={{ fontSize: 11, fontWeight: 700, color: '#D4880F', background: '#FEF3CD', border: '1px solid #F5DFA0', borderRadius: 4, padding: '3px 8px' }}>Not signed</span>
                        )}
                      </div>
                    </div>

                    <div style={{ background: 'var(--av2-bg)', border: '1px solid var(--av2-border)', borderRadius: 8, padding: 14 }}>
                      {client.contractUrl ? (
                        <div style={{ marginBottom: 10 }}>
                          <a href={client.contractUrl} target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', background: '#fff', border: '1px solid var(--av2-border)', borderRadius: 6, textDecoration: 'none', color: 'var(--av2-text)' }}>
                            <span style={{ fontSize: 16 }}>📄</span>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {client.contractFileName || 'Executed contract'}
                              </div>
                              {client.contractUploadedAt?.seconds && (
                                <div style={{ fontSize: 11, color: 'var(--av2-text-muted)', marginTop: 2 }}>
                                  Uploaded {new Date(client.contractUploadedAt.seconds * 1000).toLocaleDateString()}
                                </div>
                              )}
                            </div>
                            <span style={{ fontSize: 11, color: '#1A56DB', fontWeight: 600 }}>View →</span>
                          </a>
                        </div>
                      ) : (
                        <div style={{ fontSize: 13, color: 'var(--av2-text-muted)', marginBottom: 10 }}>
                          No contract uploaded yet.
                        </div>
                      )}

                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        <label className="av2-btn-secondary" style={{ cursor: uploadingContract ? 'wait' : 'pointer', fontSize: 12, padding: '6px 12px', margin: 0 }}>
                          {uploadingContract ? 'Uploading...' : (client.contractUrl ? 'Replace File' : 'Upload Contract')}
                          <input
                            type="file"
                            accept="application/pdf,image/*"
                            onChange={handleUploadContract}
                            disabled={uploadingContract}
                            style={{ display: 'none' }}
                          />
                        </label>
                        {client.contractUrl && (
                          <button
                            className={client.contractSigned ? 'av2-btn-secondary' : 'av2-btn-primary'}
                            style={{ fontSize: 12, padding: '6px 12px' }}
                            onClick={handleToggleContractSigned}
                          >
                            {client.contractSigned ? 'Mark as Unsigned' : 'Mark as Signed'}
                          </button>
                        )}
                        {client.contractUrl && (
                          <button
                            className="av2-btn-danger"
                            style={{ fontSize: 12, padding: '6px 12px', marginLeft: 'auto' }}
                            onClick={handleRemoveContract}
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* ── Additional Property (up to 1) ── */}
                  <div style={{ marginTop: 24 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--av2-text-muted)', textTransform: 'uppercase', letterSpacing: 1 }}>
                        Additional Property
                      </div>
                      {properties.length === 0 && !showAddProperty && (
                        <button className="av2-btn-secondary" style={{ fontSize: 11, padding: '6px 12px' }} onClick={() => setShowAddProperty(true)}>
                          {Icons.plus} Add Property
                        </button>
                      )}
                    </div>

                    {properties.length > 0 && (
                      <div className="av2-property-card">
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div style={{ fontSize: 13, color: 'var(--av2-text)' }}>{properties[0].address}</div>
                          <button className="av2-doc-delete" onClick={() => handleDeleteProperty(properties[0].id)}>{Icons.trash}</button>
                        </div>
                      </div>
                    )}

                    {showAddProperty && (
                      <div className="av2-section" style={{ padding: 16, marginTop: 10 }}>
                        <div className="av2-form-field">
                          <label>Address *</label>
                          <input value={propForm.address} onChange={e => setPropForm({ ...propForm, address: e.target.value })} placeholder="Full address" />
                        </div>
                        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                          <button className="av2-btn-secondary" onClick={() => setShowAddProperty(false)}>Cancel</button>
                          <button className="av2-btn-primary" onClick={handleAddProperty}>Add Property</button>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="av2-detail-quick-actions">
                    <button className="av2-btn-primary" onClick={() => setShowCreateWO(true)}>
                      {Icons.plus} Create Work Order
                    </button>
                    <button className="av2-btn-secondary" onClick={() => setEditing(true)} style={{ marginLeft: 'auto' }}>
                      Edit Profile
                    </button>
                    {client.stage === 'former' ? (
                      <button className="av2-btn-secondary" onClick={handleReactivateClient} style={{ color: 'var(--av2-green)' }}>
                        Reactivate
                      </button>
                    ) : (
                      <button className="av2-btn-danger" onClick={handleDeactivateClient}>
                        Mark Inactive
                      </button>
                    )}
                  </div>
                </>
              ) : (
                <>
                  <div className="av2-form-field">
                    <label>Property ID</label>
                    <input
                      value={form.propertyId}
                      onChange={e => setForm({ ...form, propertyId: e.target.value.toUpperCase() })}
                      placeholder="RH-0001"
                      style={{ fontFamily: 'ui-monospace, monospace', textTransform: 'uppercase', maxWidth: 200 }}
                      maxLength={12}
                    />
                  </div>
                  <div className="av2-form-row">
                    <div className="av2-form-field">
                      <label>Primary Contact Name</label>
                      <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
                    </div>
                    <div className="av2-form-field">
                      <label>Phone</label>
                      <input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} />
                    </div>
                  </div>
                  <div className="av2-form-field">
                    <label>Email</label>
                    <input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
                  </div>
                  <div className="av2-form-field">
                    <label>Property Address</label>
                    <input value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} />
                  </div>

                  {/* ── Secondary Contacts ── */}
                  <div className="av2-form-field">
                    <label>Secondary Contacts <span style={{ fontWeight: 400, color: 'var(--av2-text-muted)' }}>(spouse, tenant, property manager, etc.)</span></label>
                    {secondaryContacts.map((sc, i) => (
                      <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 6, alignItems: 'center' }}>
                        <input
                          style={{ flex: 2 }}
                          placeholder="Name"
                          value={sc.name || ''}
                          onChange={e => setSecondaryContacts(secondaryContacts.map((x, j) => j === i ? { ...x, name: e.target.value } : x))}
                        />
                        <select
                          style={{ flex: 1 }}
                          value={sc.role || 'spouse'}
                          onChange={e => setSecondaryContacts(secondaryContacts.map((x, j) => j === i ? { ...x, role: e.target.value } : x))}
                        >
                          <option value="spouse">Spouse</option>
                          <option value="tenant">Tenant</option>
                          <option value="property-manager">Prop. Manager</option>
                          <option value="family">Family</option>
                          <option value="other">Other</option>
                        </select>
                        <input
                          style={{ flex: 1 }}
                          placeholder="Phone"
                          value={sc.phone || ''}
                          onChange={e => setSecondaryContacts(secondaryContacts.map((x, j) => j === i ? { ...x, phone: e.target.value } : x))}
                        />
                        <input
                          style={{ flex: 1 }}
                          placeholder="Email"
                          value={sc.email || ''}
                          onChange={e => setSecondaryContacts(secondaryContacts.map((x, j) => j === i ? { ...x, email: e.target.value } : x))}
                        />
                        <button
                          type="button"
                          className="av2-btn-link"
                          style={{ color: 'var(--av2-danger)', fontSize: 12 }}
                          onClick={() => setSecondaryContacts(secondaryContacts.filter((_, j) => j !== i))}
                        >Remove</button>
                      </div>
                    ))}
                    <button
                      type="button"
                      className="av2-btn-link"
                      style={{ fontSize: 12, marginTop: 4 }}
                      onClick={() => setSecondaryContacts([...secondaryContacts, { name: '', role: 'spouse', phone: '', email: '' }])}
                    >+ Add contact</button>
                  </div>

                  <div className="av2-form-field">
                    <label>Property Notes <span style={{ fontWeight: 400, color: 'var(--av2-text-muted)' }}>(access codes, dogs, shutoff, parking — visible to techs)</span></label>
                    <textarea value={form.propertyNotes} onChange={e => setForm({ ...form, propertyNotes: e.target.value })} rows={3} placeholder="Gate code 4521. Dog in backyard, friendly. Main shutoff in garage." />
                  </div>

                  <div className="av2-form-field">
                    <label>Preferred Tech</label>
                    <select value={form.preferredTech} onChange={e => setForm({ ...form, preferredTech: e.target.value })}>
                      <option value="">No preference</option>
                      {techList.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div className="av2-form-field">
                    <label>Internal Notes</label>
                    <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={4} />
                  </div>

                  {/* ── Home Health Report ── */}
                  <div style={{ marginTop: 16, marginBottom: 14, paddingTop: 16, borderTop: '1px solid var(--av2-border)' }}>
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: 'var(--av2-text)', marginBottom: 4 }}>Home Health Report</label>
                    <div style={{ fontSize: 11, color: 'var(--av2-text-light)', marginBottom: 10 }}>PDF — visible to members in their portal</div>

                    {client.healthReportUrl ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', background: 'rgba(0,122,83,0.08)', border: '1px solid rgba(0,122,83,0.18)', borderRadius: 6 }}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#007A53" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                          <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/>
                          <polyline points="14 2 14 8 20 8"/>
                        </svg>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <a href={client.healthReportUrl} target="_blank" rel="noopener noreferrer" style={{ fontSize: 13, fontWeight: 600, color: '#007A53', textDecoration: 'none', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {client.healthReportFileName || 'health-report.pdf'}
                          </a>
                          {client.healthReportUploadedAt?.seconds && (
                            <div style={{ fontSize: 11, color: 'var(--av2-text-light)' }}>
                              Uploaded {new Date(client.healthReportUploadedAt.seconds * 1000).toLocaleDateString()}
                            </div>
                          )}
                        </div>
                        <label style={{ fontSize: 11, fontWeight: 600, color: '#007A53', cursor: 'pointer', padding: '6px 10px', border: '1px solid #007A53', borderRadius: 6, whiteSpace: 'nowrap' }}>
                          Replace
                          <input type="file" accept="application/pdf" onChange={handleUploadHHR} style={{ display: 'none' }} disabled={uploadingHHR} />
                        </label>
                        <button onClick={handleRemoveHHR} style={{ fontSize: 11, fontWeight: 600, color: '#C0392B', background: 'transparent', border: '1px solid rgba(0,0,0,0.08)', borderRadius: 6, padding: '6px 10px', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                          Remove
                        </button>
                      </div>
                    ) : (
                      <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '14px', background: '#F5F5F7', border: '1px dashed var(--av2-border)', borderRadius: 6, cursor: uploadingHHR ? 'wait' : 'pointer', fontSize: 13, color: 'var(--av2-text-mid)' }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
                          <polyline points="17 8 12 3 7 8"/>
                          <line x1="12" y1="3" x2="12" y2="15"/>
                        </svg>
                        {uploadingHHR ? 'Uploading...' : 'Upload PDF'}
                        <input type="file" accept="application/pdf" onChange={handleUploadHHR} style={{ display: 'none' }} disabled={uploadingHHR} />
                      </label>
                    )}
                  </div>

                  <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                    <button className="av2-btn-secondary" onClick={() => { setEditing(false); setForm({ propertyId: client.propertyId || client.accountNumber || '', name: client.name, email: client.email, phone: client.phone, address: client.address, propertyNotes: client.propertyNotes || '', notes: client.notes || '', preferredTech: client.preferredTech || '' }); setSecondaryContacts(client.secondaryContacts || []); }}>Cancel</button>
                    <button className="av2-btn-primary" onClick={handleSaveProfile} disabled={saving}>
                      {saving ? 'Saving...' : 'Save'}
                    </button>
                  </div>
                </>
              )}
            </div>
          )}

          {/* ── Activity Tab ── */}
          {tab === 'activity' && (
            <div style={{ padding: 24 }}>
              {/* Add activity form */}
              <div className="av2-activity-add">
                <div className="av2-form-row" style={{ marginBottom: 8 }}>
                  <div className="av2-form-field" style={{ marginBottom: 0 }}>
                    <select value={activityType} onChange={e => setActivityType(e.target.value)} style={{ padding: '8px 10px', fontSize: 12 }}>
                      <option value="note">📝 Note</option>
                      <option value="call">📞 Phone Call</option>
                      <option value="text">💬 Text</option>
                      <option value="email">📧 Email</option>
                      <option value="visit">🏠 Site Visit</option>
                      <option value="meeting">🤝 Meeting</option>
                    </select>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input
                    value={activityNote}
                    onChange={e => setActivityNote(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleAddActivity()}
                    placeholder="Log a call, note, visit, or any interaction..."
                    style={{ flex: 1, padding: '10px 12px', border: '1px solid var(--av2-border)', borderRadius: 8, fontFamily: "var(--av2-font)", fontSize: 13, outline: 'none' }}
                  />
                  <button className="av2-btn-primary" onClick={handleAddActivity} disabled={!activityNote.trim()} style={{ whiteSpace: 'nowrap' }}>
                    Add
                  </button>
                </div>
              </div>

              {/* Activity timeline */}
              {activities.length === 0 ? (
                <div className="av2-empty-state" style={{ marginTop: 20 }}>
                  <p>No activity logged yet. Use the form above to log calls, notes, visits, and other interactions.</p>
                </div>
              ) : (
                <div className="av2-activity-timeline">
                  {activities.map((a, i) => {
                    const icons = { note: '📝', call: '📞', text: '💬', email: '📧', visit: '🏠', meeting: '🤝' };
                    const d = a.timestamp ? new Date(a.timestamp) : null;
                    return (
                      <div key={i} className="av2-activity-item">
                        <div className="av2-activity-icon">{icons[a.type] || '📝'}</div>
                        <div className="av2-activity-content">
                          <div className="av2-activity-note">{a.note}</div>
                          <div className="av2-activity-meta">
                            {a.by || 'Admin'} · {d ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—'}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* ── Service History Tab ── */}
          {tab === 'history' && (
            <div style={{ padding: 24 }}>
              {clientJobs.length === 0 ? (
                <div className="av2-empty-state">
                  <p>No jobs yet.</p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {clientJobs.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0)).map(job => {
                    const date = job.createdAt?.seconds ? new Date(job.createdAt.seconds * 1000) : null;
                    return (
                      <div
                        key={job.id}
                        className="av2-job-card"
                        onClick={() => setSelectedJob(job)}
                        style={{ cursor: 'pointer' }}
                      >
                        <div className="av2-job-card-header">
                          <div className="av2-job-card-title">{job.title || 'Untitled'}</div>
                          <span className={`av2-badge av2-badge-${(job.status || '').toLowerCase().replace(/ /g, '-')}`}>
                            {job.status}
                          </span>
                        </div>
                        <div className="av2-job-card-footer" style={{ marginTop: 6 }}>
                          <span className="av2-job-date">
                            {date ? date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
                            {job.assignedTo && ` · ${job.assignedTo}`}
                            {job.woNumber && ` · ${job.woNumber}`}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* ── Communication / Templates Tab ── */}
          {tab === 'comms' && (
            <CommsTab
              client={client}
              clientJobs={clientJobs}
              clientInvoices={(invoices || []).filter(i => i.clientId === client.id || i.clientEmail === client.email)}
              currentUserName={currentUserName}
              onUpdate={onUpdate}
            />
          )}

          {/* ── Billing Tab ── */}
          {tab === 'billing' && (
            <div style={{ padding: 24 }}>
              {(() => {
                const clientInvoices = (invoices || []).filter(i => i.clientId === client.id || i.clientEmail === client.email);
                const totalInvoiced = clientInvoices.reduce((sum, i) => sum + (i.total || 0), 0);
                const totalPaid = clientInvoices.filter(i => i.status === 'paid').reduce((sum, i) => sum + (i.total || 0), 0);
                const totalUnpaid = clientInvoices.filter(i => i.status === 'unpaid').reduce((sum, i) => sum + (i.total || 0), 0);

                return (
                  <>
                    <div className="av2-billing-summary">
                      <div className="av2-billing-summary-item">
                        <div className="av2-billing-summary-label">Total Invoiced</div>
                        <div className="av2-billing-summary-value">${totalInvoiced.toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
                      </div>
                      <div className="av2-billing-summary-item">
                        <div className="av2-billing-summary-label">Paid</div>
                        <div className="av2-billing-summary-value" style={{ color: 'var(--av2-green)' }}>${totalPaid.toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
                      </div>
                      <div className="av2-billing-summary-item">
                        <div className="av2-billing-summary-label">Outstanding</div>
                        <div className="av2-billing-summary-value" style={{ color: totalUnpaid > 0 ? 'var(--av2-danger)' : 'var(--av2-green)' }}>${totalUnpaid.toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
                      </div>
                    </div>

                    {clientInvoices.length === 0 ? (
                      <div className="av2-empty-state" style={{ marginTop: 20 }}>
                        <p>No invoices yet. Invoices are auto-generated when jobs are completed.</p>
                      </div>
                    ) : (
                      <div className="av2-table-wrap">
                        <table className="av2-table">
                          <thead>
                            <tr><th>Invoice</th><th>Job</th><th>Hours</th><th>Total</th><th>Status</th></tr>
                          </thead>
                          <tbody>
                            {clientInvoices.map(inv => {
                              const date = inv.createdAt?.seconds ? new Date(inv.createdAt.seconds * 1000) : null;
                              return (
                                <tr key={inv.id}>
                                  <td>
                                    <div style={{ fontWeight: 700, fontSize: 12 }}>{inv.invoiceNumber}</div>
                                    <div style={{ fontSize: 11, color: 'var(--av2-text-muted)' }}>{date ? date.toLocaleDateString() : '—'}</div>
                                  </td>
                                  <td>{inv.workOrderTitle}</td>
                                  <td>{inv.hours}h × ${inv.rate}</td>
                                  <td style={{ fontWeight: 700 }}>${(inv.total || 0).toFixed(2)}</td>
                                  <td>
                                    <span className={`av2-badge ${inv.status === 'paid' ? 'av2-badge-completed' : 'av2-badge-pending'}`}>
                                      {inv.status === 'paid' ? 'Paid' : 'Unpaid'}
                                    </span>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          )}
        </div>

        {/* Create Work Order Modal */}
        {showCreateWO && (
          <div className="av2-modal-overlay" style={{ zIndex: 1100 }} onClick={() => setShowCreateWO(false)}>
            <div className="av2-modal" onClick={e => e.stopPropagation()}>
              <div className="av2-modal-header">
                <h2>New Work Order</h2>
                <button className="av2-modal-close" onClick={() => setShowCreateWO(false)}>{Icons.close}</button>
              </div>
              <div className="av2-modal-body">
                <div style={{ background: 'var(--av2-bg)', borderRadius: 8, padding: 12, marginBottom: 16 }}>
                  <div style={{ fontSize: 12, color: 'var(--av2-text-muted)' }}>Client</div>
                  <div style={{ fontSize: 14, fontWeight: 700 }}>{client.name}</div>
                  <div style={{ fontSize: 11, color: 'var(--av2-text-light)', marginTop: 2 }}>{client.address || 'No address on file'}</div>
                </div>
                <div className="av2-form-field">
                  <label>Job Title *</label>
                  <input value={woForm.title} onChange={e => setWoForm({ ...woForm, title: e.target.value })} placeholder="e.g. Fix leaky kitchen faucet" />
                </div>
                <div className="av2-form-row">
                  <div className="av2-form-field">
                    <label>Priority</label>
                    <select value={woForm.priority} onChange={e => setWoForm({ ...woForm, priority: e.target.value })}>
                      <option value="Normal">Normal</option>
                      <option value="High">High</option>
                      <option value="Urgent">Urgent</option>
                    </select>
                  </div>
                </div>
                <div className="av2-form-field">
                  <label>Description</label>
                  <textarea value={woForm.description} onChange={e => setWoForm({ ...woForm, description: e.target.value })} rows={3} placeholder="What needs to be done?" />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
                  <input
                    type="checkbox"
                    id="skipEstimate"
                    checked={woForm.skipEstimate}
                    onChange={e => setWoForm({ ...woForm, skipEstimate: e.target.checked })}
                    style={{ width: 16, height: 16, cursor: 'pointer' }}
                  />
                  <label htmlFor="skipEstimate" style={{ fontSize: 12, color: 'var(--av2-text-light)', cursor: 'pointer', textTransform: 'none', letterSpacing: 0, fontWeight: 500 }}>
                    Skip estimate — go straight to scheduling (e.g., quoted over the phone)
                  </label>
                </div>
              </div>
              <div className="av2-modal-footer">
                <button className="av2-btn-secondary" onClick={() => setShowCreateWO(false)}>Cancel</button>
                <button className="av2-btn-primary" onClick={handleCreateWorkOrder} disabled={saving || !woForm.title}>
                  {saving ? 'Creating...' : 'Create Work Order'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Job Detail Modal */}
      {selectedJob && (
        <JobDetail
          job={selectedJob}
          workOrders={workOrders}
          contacts={[]}
          employees={employees}
          invoices={invoices}
          onClose={() => setSelectedJob(null)}
          onUpdate={async () => { await onUpdate(); }}
        />
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════
   JOBS PIPELINE
   ══════════════════════════════════════════════ */
const JOB_STAGES = [
  { key: 'approved', label: 'Ready to Schedule', statuses: ['Approved'], color: '#1E6091' },
  { key: 'scheduled', label: 'Scheduled', statuses: ['Scheduled'], color: '#3BA672' },
  { key: 'in-progress', label: 'In Progress', statuses: ['In Progress'], color: '#D4880F' },
  { key: 'completed', label: 'Completed', statuses: ['Completed', 'Done'], color: '#007A53' },
];

function JobsPage({ contacts, workOrders, employees, invoices = [], loadData, targetWorkOrderId, clearTarget }) {
  const [selectedJob, setSelectedJob] = useState(null);
  const [view, setView] = useState('pipeline'); // 'pipeline' | 'list'
  const [filterTech, setFilterTech] = useState('all');
  const [filterService, setFilterService] = useState('all');
  const [search, setSearch] = useState('');
  const [expandedStages, setExpandedStages] = useState({});

  // Deep-link: auto-open the target work order if it's in this page's data
  useEffect(() => {
    if (!targetWorkOrderId) return;
    const wo = workOrders.find(w => w.id === targetWorkOrderId);
    if (wo) {
      setSelectedJob(wo);
      clearTarget?.();
    }
  }, [targetWorkOrderId, workOrders, clearTarget]);

  const getJobsForStage = (stage) => {
    return workOrders.filter(wo => {
      if (!stage.statuses.includes(wo.status)) return false;
      if (filterTech !== 'all' && wo.assignedTo !== filterTech) return false;
      if (search && !(
        (wo.title || '').toLowerCase().includes(search.toLowerCase()) ||
        (wo.clientName || wo.contactName || '').toLowerCase().includes(search.toLowerCase()) ||
        (wo.woNumber || '').includes(search)
      )) return false;
      return true;
    });
  };

  // Get unique service types for filter
  const techList = employees.filter(e => e.status !== 'terminated').map(e => e.name);

  // Filter all jobs for list view
  const filteredJobs = workOrders.filter(wo => {
    if (filterTech !== 'all' && wo.assignedTo !== filterTech) return false;
    if (search && !(
      (wo.title || '').toLowerCase().includes(search.toLowerCase()) ||
      (wo.clientName || wo.contactName || '').toLowerCase().includes(search.toLowerCase()) ||
      (wo.woNumber || '').includes(search)
    )) return false;
    return true;
  }).sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));

  return (
    <div className="av2-page">
      <div className="av2-page-header">
        <h1>Jobs</h1>
        <div style={{ display: 'flex', gap: 12 }}>
          <div className="av2-view-toggle">
            <button className={view === 'pipeline' ? 'active' : ''} onClick={() => setView('pipeline')}>Pipeline</button>
            <button className={view === 'list' ? 'active' : ''} onClick={() => setView('list')}>List</button>
          </div>
          <button className="av2-btn-secondary" onClick={loadData}>
            {Icons.refresh} Refresh
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="av2-filters-bar">
        <div className="av2-search-box">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
          <input placeholder="Search by title, client, or WO#..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <select value={filterTech} onChange={e => setFilterTech(e.target.value)} className="av2-select">
          <option value="all">All Techs</option>
          {techList.map(t => <option key={t} value={t}>{t}</option>)}
          <option value="">Unassigned</option>
        </select>
      </div>

      {/* Pipeline View */}
      {view === 'pipeline' && (
        <>
          {/* Stat strip */}
          <div className="av2-stage-strip" style={{ gridTemplateColumns: `repeat(${JOB_STAGES.length}, 1fr)` }}>
            {JOB_STAGES.map(stage => {
              const count = getJobsForStage(stage).length;
              return (
                <div
                  key={stage.key}
                  className={`av2-stage-stat ${expandedStages[stage.key] === false ? '' : 'active'}`}
                  onClick={() => setExpandedStages(prev => ({ ...prev, [stage.key]: prev[stage.key] === false ? true : false }))}
                >
                  <span className="av2-stage-stat-dot" style={{ background: stage.color }} />
                  <div className="av2-stage-stat-content">
                    <div className="av2-stage-stat-count">{count}</div>
                    <div className="av2-stage-stat-label">{stage.label}</div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Stage groups */}
          <div className="av2-stage-groups">
            {JOB_STAGES.map(stage => {
              const jobs = getJobsForStage(stage);
              const collapsed = expandedStages[stage.key] === false;
              return (
                <div key={stage.key} className={`av2-stage-group ${collapsed ? 'collapsed' : ''}`}>
                  <div
                    className="av2-stage-group-header"
                    onClick={() => setExpandedStages(prev => ({ ...prev, [stage.key]: prev[stage.key] === false ? true : false }))}
                  >
                    <span className="av2-stage-group-dot" style={{ background: stage.color }} />
                    <span className="av2-stage-group-title">{stage.label}</span>
                    <span className="av2-stage-group-count">{jobs.length}</span>
                    <svg className="av2-stage-group-chevron" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </div>
                  <div className="av2-stage-group-body">
                    {jobs.length === 0 ? (
                      <div className="av2-row-card-empty">No jobs in this stage</div>
                    ) : (
                      jobs.map(job => {
                        const dateStr = job.scheduledDate
                          ? new Date(job.scheduledDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                          : '—';
                        return (
                          <div key={job.id} className="av2-row-card" onClick={() => setSelectedJob(job)}>
                            <span className="av2-row-card-ribbon" style={{ background: stage.color }} />
                            {job.woNumber && <div className="av2-row-card-num">{job.woNumber}</div>}
                            <div className="av2-row-card-main">
                              <div className="av2-row-card-title">{job.title || 'Untitled'}</div>
                              <div className="av2-row-card-meta">
                                <span>{job.clientName || job.contactName || 'No client'}</span>
                                {job.accountNumber && (
                                  <>
                                    <span className="av2-row-card-meta-dot" />
                                    <span className="av2-acct-badge" style={{ fontSize: 11 }}>{job.accountNumber}</span>
                                  </>
                                )}
                                {job.isNonMember && (
                                  <>
                                    <span className="av2-row-card-meta-dot" />
                                    <span className="av2-nm-badge">One-Time</span>
                                  </>
                                )}
                              </div>
                            </div>
                            <div className="av2-row-card-trail">
                              {job.assignedTo
                                ? <span style={{ color: 'var(--av2-text-light)', fontWeight: 600 }}>{job.assignedTo}</span>
                                : <span style={{ fontStyle: 'italic' }}>Unassigned</span>}
                            </div>
                            <div className="av2-row-card-trail">{dateStr}</div>
                            <svg className="av2-row-card-chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="9 18 15 12 9 6" />
                            </svg>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* List View */}
      {view === 'list' && (
        <div className="av2-section" style={{ padding: 0 }}>
          <div className="av2-table-wrap">
            <table className="av2-table">
              <thead>
                <tr>
                  <th>WO#</th>
                  <th>Acct#</th>
                  <th>Title</th>
                  <th>Client</th>
                  <th>Tech</th>
                  <th>Scheduled</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredJobs.length === 0 ? (
                  <tr><td colSpan={8} style={{ textAlign: 'center', padding: 40, color: 'var(--av2-text-muted)' }}>No jobs match your filters.</td></tr>
                ) : filteredJobs.map(job => (
                  <tr key={job.id} style={{ cursor: 'pointer' }} onClick={() => setSelectedJob(job)}>
                    <td style={{ fontWeight: 600, color: 'var(--av2-text-muted)', fontSize: 12 }}>{job.woNumber || '—'}</td>
                    <td>{job.accountNumber ? <span className="av2-acct-badge">{job.accountNumber}</span> : '—'}</td>
                    <td style={{ fontWeight: 600 }}>{job.title}</td>
                    <td>{job.clientName || job.contactName || '—'}</td>
                    <td>{job.assignedTo || <span style={{ color: 'var(--av2-text-muted)', fontStyle: 'italic' }}>Unassigned</span>}</td>
                    <td>{job.scheduledDate ? new Date(job.scheduledDate).toLocaleDateString() : '—'}</td>
                    <td><span className={`av2-badge av2-badge-${(job.status || '').toLowerCase().replace(/ /g, '-')}`}>{job.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {selectedJob && (
        <JobDetail
          job={selectedJob}
          workOrders={workOrders}
          contacts={contacts}
          employees={employees}
          invoices={invoices}
          onClose={() => setSelectedJob(null)}
          onUpdate={async () => { await loadData(); }}
        />
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════
   JOB DETAIL MODAL
   ══════════════════════════════════════════════ */
function JobDetail({ job, workOrders = [], contacts, employees, invoices = [], onClose, onUpdate }) {
  const [saving, setSaving] = useState(false);
  const [assignTech, setAssignTech] = useState(job.assignedTo || '');
  const [scheduledDate, setScheduledDate] = useState(job.scheduledDate || '');
  const [selectedSlot, setSelectedSlot] = useState(job.scheduledTime || '');
  const [duration, setDuration] = useState(job.estimatedDuration || (job.estimate?.hours ? Math.ceil(job.estimate.hours) : 1));
  const [materialList, setMaterialList] = useState(job.materialList || '');
  const [estForm, setEstForm] = useState({
    hours: job.estimate?.hours || '',
    rate: job.estimate?.rate || '120',
    materials: job.estimate?.materials || '',
    notes: job.estimate?.notes || '',
  });
  const [sendingEstimate, setSendingEstimate] = useState(false);

  const techList = employees.filter(e => e.status !== 'terminated').map(e => e.name);

  const handleAssign = async () => {
    if (!assignTech || !scheduledDate || !selectedSlot) {
      dialog.alert('Please select a tech, date, and time slot.');
      return;
    }
    setSaving(true);
    try {
      await updateDoc(doc(db, 'workOrders', job.id), {
        assignedTo: assignTech,
        scheduledDate: scheduledDate || null,
        scheduledTime: selectedSlot || null,
        estimatedDuration: Number(duration) || 1,
        materialList: materialList || '',
        status: 'Scheduled',
      });
      await onUpdate();
      onClose();
    } catch (err) {
      dialog.alert('Error: ' + err.message);
    }
    setSaving(false);
  };

  /* ── Time slot availability ── */
  const TIME_SLOTS = ['08:00','09:00','10:00','11:00','12:00','13:00','14:00','15:00','16:00','17:00'];
  const slotLabel = (t) => {
    const h = parseInt(t.split(':')[0]);
    return h === 0 ? '12 AM' : h < 12 ? `${h} AM` : h === 12 ? '12 PM' : `${h - 12} PM`;
  };

  // Find which slots are booked for this tech on this date
  const getBookedSlots = () => {
    if (!assignTech || !scheduledDate) return new Set();
    const booked = new Set();
    workOrders.filter(wo =>
      wo.id !== job.id &&
      wo.assignedTo === assignTech &&
      wo.scheduledDate === scheduledDate &&
      wo.scheduledTime &&
      wo.status !== 'Completed' && wo.status !== 'Done' && wo.status !== 'Canceled' && wo.status !== 'Declined'
    ).forEach(wo => {
      const startIdx = TIME_SLOTS.indexOf(wo.scheduledTime);
      if (startIdx >= 0) {
        const dur = wo.estimatedDuration || 1;
        for (let i = 0; i < dur && startIdx + i < TIME_SLOTS.length; i++) {
          booked.add(TIME_SLOTS[startIdx + i]);
        }
      }
    });
    return booked;
  };

  const bookedSlots = getBookedSlots();

  // Check if selected slot + duration would conflict
  const wouldConflict = (slot) => {
    const startIdx = TIME_SLOTS.indexOf(slot);
    for (let i = 0; i < duration && startIdx + i < TIME_SLOTS.length; i++) {
      if (bookedSlots.has(TIME_SLOTS[startIdx + i])) return true;
    }
    return false;
  };

  // Check if slot + duration would go past 6pm
  const wouldOverflow = (slot) => {
    const startIdx = TIME_SLOTS.indexOf(slot);
    return startIdx + duration > TIME_SLOTS.length;
  };

  const handleStatusChange = async (newStatus) => {
    if (!(await dialog.confirm(`Change status to "${newStatus}"?`))) return;

    const updates = { status: newStatus };
    if (newStatus === 'Completed' || newStatus === 'Done') {
      // Prompt for hours worked — required for accurate member hour-bank tracking
      const existing = job.hoursWorked ? String(job.hoursWorked) : '';
      const promptMsg = existing
        ? `Hours worked on this job:\n(currently ${existing}h on file — leave as-is or update)`
        : 'Hours worked on this job:';
      const input = await dialog.prompt(promptMsg, existing);
      if (input === null) return; // user cancelled
      const hours = Number(input);
      if (!Number.isFinite(hours) || hours < 0) {
        dialog.alert('Please enter a valid number of hours (e.g. 1.5).');
        return;
      }
      updates.hoursWorked = hours;
      updates.completedAt = serverTimestamp();
    }

    setSaving(true);
    try {
      await updateDoc(doc(db, 'workOrders', job.id), updates);
      await onUpdate();
      onClose();
    } catch (err) {
      dialog.alert('Error: ' + err.message);
    }
    setSaving(false);
  };

  const handleDeleteWorkOrder = async () => {
    const label = job.woNumber ? job.woNumber : (job.title || 'this work order');
    if (!(await dialog.confirm(`Delete ${label}? This cannot be undone.`))) return;
    setSaving(true);
    try {
      await deleteDoc(doc(db, 'workOrders', job.id));
      await onUpdate();
      onClose();
    } catch (err) {
      dialog.alert('Error: ' + err.message);
    }
    setSaving(false);
  };

  const handleSaveEstimate = async () => {
    if (!estForm.hours) {
      dialog.alert('Please fill in hours.');
      return;
    }
    setSaving(true);
    try {
      const hours = Number(estForm.hours);
      const rate = 120;
      const materials = Number(estForm.materials) || 0;
      const total = hours * rate + materials;

      await updateDoc(doc(db, 'workOrders', job.id), {
        estimate: { hours, rate, materials, total, notes: estForm.notes },
      });
      await onUpdate();
      dialog.alert('Estimate saved. Click "Send Estimate" to send it to the client.');
    } catch (err) {
      dialog.alert('Error: ' + err.message);
    }
    setSaving(false);
  };

  const handleSendEstimate = async () => {
    if (!estForm.hours) {
      dialog.alert('Fill in hours first.');
      return;
    }
    setSendingEstimate(true);
    try {
      // Auto-save estimate first
      const hours = Number(estForm.hours);
      const rate = 120;
      const materials = Number(estForm.materials) || 0;
      const total = hours * rate + materials;

      // Generate approval token if one doesn't already exist
      const approvalToken = job.approvalToken || (typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID()
        : 'tok-' + Math.random().toString(36).slice(2) + Date.now().toString(36));

      await updateDoc(doc(db, 'workOrders', job.id), {
        estimate: { hours, rate, materials, total, notes: estForm.notes },
        status: 'Estimate Sent',
        approvalToken: approvalToken,
        estimateSentAt: serverTimestamp(),
      });

      // Build the approval URL based on current origin
      const baseUrl = (typeof window !== 'undefined' && window.location && window.location.origin) || '';
      const approvalUrl = `${baseUrl}/estimate/${approvalToken}`;

      // Copy to clipboard automatically
      try {
        await navigator.clipboard.writeText(approvalUrl);
        dialog.alert(
          `Estimate ready to send.\n\nApproval link (copied to clipboard):\n\n${approvalUrl}\n\nNext: open the Templates tab on the client's property card → "Estimate Sent" → copy the email and paste this link in.`
        );
      } catch {
        dialog.alert(
          `Estimate ready to send.\n\nApproval link:\n\n${approvalUrl}\n\nCopy this link and paste it in your email to the client. Open the Templates tab on the client's property card → "Estimate Sent" for the full email copy.`
        );
      }
      await onUpdate();
    } catch (err) {
      dialog.alert('Error: ' + err.message);
    }
    setSendingEstimate(false);
  };

  /* ── Collapsible section state ── */
  const [openSections, setOpenSections] = useState({ request: true, estimate: false, schedule: false, completed: false });
  const toggleSection = (key) => setOpenSections(prev => ({ ...prev, [key]: !prev[key] }));

  const SectionHeader = ({ sectionKey, number, title, icon, color, badge }) => (
    <div
      onClick={() => toggleSection(sectionKey)}
      style={{
        display: 'flex', alignItems: 'center', gap: 10, padding: '12px 0', cursor: 'pointer',
        borderBottom: openSections[sectionKey] ? '1px solid var(--av2-border)' : 'none',
        marginBottom: openSections[sectionKey] ? 12 : 0,
        userSelect: 'none',
      }}
    >
      <div style={{ width: 24, height: 24, borderRadius: '50%', background: color || 'var(--av2-green)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, flexShrink: 0 }}>
        {number}
      </div>
      <div style={{ flex: 1, fontSize: 14, fontWeight: 700, color: 'var(--av2-text)' }}>
        {icon && <span style={{ marginRight: 6 }}>{icon}</span>}{title}
      </div>
      {badge && <span className={`av2-badge av2-badge-${badge.toLowerCase().replace(/ /g, '-')}`} style={{ fontSize: 10 }}>{badge}</span>}
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--av2-text-muted)" strokeWidth="2" style={{ transform: openSections[sectionKey] ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>
        <polyline points="6 9 12 15 18 9" />
      </svg>
    </div>
  );

  const createdDate = job.createdAt?.seconds ? new Date(job.createdAt.seconds * 1000) : null;

  return (
    <div className="av2-modal-overlay" onClick={onClose}>
      <div className="av2-modal av2-modal-large" onClick={e => e.stopPropagation()}>
        <div className="av2-modal-header">
          <div style={{ flex: 1 }}>
            <h2>{job.title}</h2>
            <div className="av2-modal-sub">
              {job.woNumber && <span>{job.woNumber} · </span>}
              {job.accountNumber && <span className="av2-acct-badge" style={{ marginRight: 6 }}>{job.accountNumber}</span>}
              <span className={`av2-badge av2-badge-${(job.status || '').toLowerCase().replace(/ /g, '-')}`}>{job.status}</span>
              {job.isNonMember && <span className="av2-nm-badge" style={{ marginLeft: 8 }}>One-Time</span>}
            </div>
          </div>
          <button className="av2-modal-close" onClick={onClose}>{Icons.close}</button>
        </div>

        <div className="av2-modal-body">

          {/* ═══ SECTION 1: Request Details ═══ */}
          <div className="av2-job-section" style={{ paddingBottom: 0 }}>
            <SectionHeader sectionKey="request" number="1" title="Request Details" icon="📋" color="#1E6091" />
            {openSections.request && (
              <div style={{ paddingBottom: 16 }}>
                {/* Client info row */}
                <div className="av2-detail-grid">
                  <div className="av2-detail-item">
                    <div className="av2-detail-label">Property ID</div>
                    <div className="av2-detail-value">{(job.propertyId || job.accountNumber) ? <span className="av2-acct-badge" style={{ fontFamily: 'ui-monospace, monospace' }}>{job.propertyId || job.accountNumber}</span> : '—'}</div>
                  </div>
                  <div className="av2-detail-item">
                    <div className="av2-detail-label">Client</div>
                    <div className="av2-detail-value">{job.clientName || job.contactName || '—'}</div>
                  </div>
                  <div className="av2-detail-item">
                    <div className="av2-detail-label">Phone</div>
                    <div className="av2-detail-value">
                      {(job.contactPhone || job.clientPhone) ?
                        <a href={`tel:${job.contactPhone || job.clientPhone}`}>{job.contactPhone || job.clientPhone}</a>
                        : '—'}
                    </div>
                  </div>
                  <div className="av2-detail-item">
                    <div className="av2-detail-label">Email</div>
                    <div className="av2-detail-value">
                      {(job.contactEmail || job.clientEmail) ?
                        <a href={`mailto:${job.contactEmail || job.clientEmail}`}>{job.contactEmail || job.clientEmail}</a>
                        : '—'}
                    </div>
                  </div>
                  <div className="av2-detail-item">
                    <div className="av2-detail-label">Address</div>
                    <div className="av2-detail-value">{job.contactAddress || '—'}</div>
                  </div>
                  <div className="av2-detail-item">
                    <div className="av2-detail-label">Priority</div>
                    <div className="av2-detail-value">{job.priority || 'Normal'}</div>
                  </div>
                  <div className="av2-detail-item">
                    <div className="av2-detail-label">Submitted</div>
                    <div className="av2-detail-value">{createdDate ? createdDate.toLocaleDateString() : '—'}</div>
                  </div>
                </div>
                {job.description && (
                  <div style={{ marginTop: 12 }}>
                    <div className="av2-detail-label">Description</div>
                    <div className="av2-detail-notes-body">{job.description}</div>
                  </div>
                )}
                {job.photos && job.photos.length > 0 && (
                  <div style={{ marginTop: 12 }}>
                    <div className="av2-detail-label" style={{ marginBottom: 6 }}>Photos ({job.photos.length})</div>
                    <div className="av2-photo-grid">
                      {job.photos.map((url, i) => (
                        <a key={i} href={url} target="_blank" rel="noopener noreferrer" className="av2-photo-thumb">
                          <img src={url} alt={`Photo ${i + 1}`} />
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* ═══ SECTION 2: Estimate ═══ */}
          <div className="av2-job-section" style={{ paddingBottom: 0 }}>
            <SectionHeader
              sectionKey="estimate" number="2" title="Estimate" icon="💰" color="#8E44AD"
              badge={job.estimate ? (job.status === 'Estimate Sent' ? 'Sent' : 'Saved') : null}
            />
            {openSections.estimate && (
              <div style={{ paddingBottom: 16 }}>
                {/* Show estimate display if sent */}
                {job.estimate && (job.status === 'Estimate Sent' || job.status === 'Approved' || job.status === 'Scheduled' || job.status === 'In Progress' || job.status === 'Completed' || job.status === 'Done') && (
                  <div className="av2-estimate-display" style={{ marginBottom: 12 }}>
                    <div>Labor: {job.estimate.hours}hr × ${job.estimate.rate}/hr = <strong>${job.estimate.hours * job.estimate.rate}</strong></div>
                    <div>Materials: <strong>${job.estimate.materials}</strong></div>
                    <div style={{ marginTop: 8, fontSize: 16, color: 'var(--av2-green)' }}>Total: <strong>${job.estimate.total}</strong></div>
                    {job.estimate.notes && <div style={{ marginTop: 8, fontSize: 12, color: 'var(--av2-text-mid)' }}>{job.estimate.notes}</div>}
                  </div>
                )}

                {/* Editable form if not completed */}
                {job.status !== 'Completed' && job.status !== 'Done' && (
                  <>
                    <div className="av2-form-row">
                      <div className="av2-form-field"><label>Hours</label><input type="number" step="0.5" value={estForm.hours} onChange={e => setEstForm({ ...estForm, hours: e.target.value })} placeholder="2" /></div>
                      <div className="av2-form-field">
                        <label>Rate ($/hr)</label>
                        <input
                          type="number"
                          value={120}
                          readOnly
                          disabled
                          title={job.isNonMember ? 'Standard rate (fixed)' : 'Standard rate (fixed)'}
                          style={{ background: 'var(--av2-bg)', color: 'var(--av2-text-muted)', cursor: 'not-allowed' }}
                        />
                        <div style={{ fontSize: 10, color: 'var(--av2-text-muted)', marginTop: 3 }}>
                          {'Standard rate'}
                        </div>
                      </div>
                      <div className="av2-form-field"><label>Materials ($)</label><input type="number" value={estForm.materials} onChange={e => setEstForm({ ...estForm, materials: e.target.value })} placeholder="0" /></div>
                    </div>
                    <div className="av2-form-field"><label>Notes for client</label><textarea value={estForm.notes} onChange={e => setEstForm({ ...estForm, notes: e.target.value })} placeholder="Any details the client should know..." rows={2} /></div>
                    {estForm.hours && (
                      <div className="av2-estimate-total">Total: <strong>${(Number(estForm.hours) * (120) + Number(estForm.materials || 0)).toFixed(2)}</strong></div>
                    )}
                    <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
                      <button className="av2-btn-secondary" onClick={handleSaveEstimate} disabled={saving}>{saving ? 'Saving...' : 'Save Estimate'}</button>
                      {(job.estimate || estForm.hours) && (
                        <button className="av2-btn-primary" onClick={handleSendEstimate} disabled={sendingEstimate}>
                          {sendingEstimate ? 'Generating link...' : (job.approvalToken ? '🔄 Regenerate Approval Link' : '✉️ Generate Approval Link')}
                        </button>
                      )}
                      {job.approvalToken && (
                        <button
                          className="av2-btn-secondary"
                          onClick={async () => {
                            const baseUrl = (typeof window !== 'undefined' && window.location?.origin) || '';
                            const url = `${baseUrl}/estimate/${job.approvalToken}`;
                            try {
                              await navigator.clipboard.writeText(url);
                              dialog.alert(`Copied to clipboard:\n${url}`);
                            } catch {
                              dialog.alert(url);
                            }
                          }}
                        >📋 Copy Approval Link</button>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          {/* ═══ SECTION 3: Schedule ═══ */}
          <div className="av2-job-section" style={{ paddingBottom: 0 }}>
            <SectionHeader
              sectionKey="schedule" number="3" title="Schedule" icon="📅" color="#3BA672"
              badge={job.scheduledDate ? `${new Date(job.scheduledDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}` : null}
            />
            {openSections.schedule && (
              <div style={{ paddingBottom: 16 }}>
                {/* Schedule form */}
                {(job.status === 'Approved' || job.status === 'Scheduled' || job.status === 'In Progress') && (
                  <>
                    {/* Row 1: Tech + Date */}
                    <div className="av2-form-row">
                      <div className="av2-form-field"><label>Assign Tech</label>
                        <select value={assignTech} onChange={e => { setAssignTech(e.target.value); setSelectedSlot(''); }}>
                          <option value="">Select tech...</option>
                          {techList.map(t => <option key={t} value={t}>{t}</option>)}
                        </select>
                      </div>
                      <div className="av2-form-field"><label>Date</label>
                        <input type="date" value={scheduledDate} onChange={e => { setScheduledDate(e.target.value); setSelectedSlot(''); }} />
                      </div>
                      <div className="av2-form-field"><label>Duration (hours)</label>
                        <select value={duration} onChange={e => { setDuration(Number(e.target.value)); setSelectedSlot(''); }}>
                          {[1,2,3,4,5,6,7,8].map(h => <option key={h} value={h}>{h}h</option>)}
                        </select>
                      </div>
                    </div>

                    {/* Time slot grid */}
                    {assignTech && scheduledDate && (
                      <div style={{ marginTop: 14 }}>
                        <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--av2-text-muted)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>
                          Available slots for {assignTech} on {new Date(scheduledDate + 'T12:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 6 }}>
                          {TIME_SLOTS.map(slot => {
                            const isBooked = bookedSlots.has(slot);
                            const conflicts = wouldConflict(slot);
                            const overflows = wouldOverflow(slot);
                            const isSelected = selectedSlot === slot;
                            const isDisabled = isBooked || overflows;

                            // Show which slots this selection would occupy
                            const startIdx = TIME_SLOTS.indexOf(selectedSlot);
                            const slotIdx = TIME_SLOTS.indexOf(slot);
                            const isInRange = selectedSlot && slotIdx >= startIdx && slotIdx < startIdx + duration;

                            return (
                              <button
                                key={slot}
                                onClick={() => !isDisabled && !conflicts && setSelectedSlot(slot)}
                                disabled={isDisabled}
                                style={{
                                  padding: '8px 4px',
                                  borderRadius: 6,
                                  border: isSelected ? '2px solid var(--av2-green)' : '1px solid var(--av2-border)',
                                  background: isBooked ? '#FEE2E2' : isInRange ? 'rgba(0,122,83,0.08)' : conflicts ? '#FEF3CD' : isSelected ? 'rgba(0,122,83,0.08)' : '#fff',
                                  color: isBooked ? '#C0392B' : isSelected ? 'var(--av2-green)' : isInRange ? '#007A53' : conflicts ? '#D4880F' : 'var(--av2-text)',
                                  fontSize: 12,
                                  fontWeight: isSelected || isInRange ? 700 : 500,
                                  cursor: isDisabled ? 'not-allowed' : conflicts ? 'pointer' : 'pointer',
                                  opacity: isBooked ? 0.6 : 1,
                                  fontFamily: 'var(--av2-sans, Montserrat, sans-serif)',
                                  textDecoration: isBooked ? 'line-through' : 'none',
                                }}
                                title={isBooked ? 'Booked' : conflicts ? 'Partial conflict with duration' : overflows ? 'Would go past 6 PM' : ''}
                              >
                                {slotLabel(slot)}
                              </button>
                            );
                          })}
                        </div>

                        {/* Legend */}
                        <div style={{ display: 'flex', gap: 16, marginTop: 10, fontSize: 10, color: 'var(--av2-text-muted)' }}>
                          <span><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: 'rgba(0,122,83,0.08)', border: '1px solid rgba(0,122,83,0.18)', marginRight: 4, verticalAlign: 'middle' }} />Selected</span>
                          <span><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: '#FEE2E2', border: '1px solid #FECACA', marginRight: 4, verticalAlign: 'middle' }} />Booked</span>
                          <span><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: '#fff', border: '1px solid var(--av2-border)', marginRight: 4, verticalAlign: 'middle' }} />Available</span>
                        </div>

                        {selectedSlot && (
                          <div style={{ marginTop: 12, padding: 10, background: 'rgba(0,122,83,0.08)', borderRadius: 8, border: '1px solid rgba(0,122,83,0.18)', fontSize: 13 }}>
                            <strong>{assignTech}</strong> · {new Date(scheduledDate + 'T12:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })} · {slotLabel(selectedSlot)} — {(() => {
                              const endIdx = TIME_SLOTS.indexOf(selectedSlot) + duration;
                              const endSlot = endIdx < TIME_SLOTS.length ? TIME_SLOTS[endIdx] : '18:00';
                              return slotLabel(endSlot);
                            })()} ({duration}h)
                          </div>
                        )}
                      </div>
                    )}

                    {!assignTech && !scheduledDate && (
                      <div style={{ fontSize: 13, color: 'var(--av2-text-muted)', fontStyle: 'italic', marginTop: 8 }}>
                        Select a tech and date to see available time slots.
                      </div>
                    )}

                    {/* Material list for tech */}
                    <div className="av2-form-field" style={{ marginTop: 14 }}>
                      <label>Material List for Tech</label>
                      <textarea
                        value={materialList}
                        onChange={e => setMaterialList(e.target.value)}
                        placeholder="List materials the tech needs to bring or pick up before the job (e.g. 3/4in copper elbows x4, replacement faucet, plumber's tape)..."
                        rows={3}
                        style={{ resize: 'vertical' }}
                      />
                    </div>

                    <button className="av2-btn-primary" onClick={handleAssign} disabled={saving || !selectedSlot} style={{ marginTop: 14 }}>
                      {saving ? 'Saving...' : 'Save Schedule'}
                    </button>
                  </>
                )}

                {/* Show schedule info if already scheduled but not editable */}
                {(job.status === 'Completed' || job.status === 'Done') && job.scheduledDate && (
                  <div className="av2-detail-grid">
                    <div className="av2-detail-item">
                      <div className="av2-detail-label">Technician</div>
                      <div className="av2-detail-value">{job.assignedTo || '—'}</div>
                    </div>
                    <div className="av2-detail-item">
                      <div className="av2-detail-label">Date</div>
                      <div className="av2-detail-value">{new Date(job.scheduledDate).toLocaleDateString()}{job.scheduledTime && ` @ ${slotLabel(job.scheduledTime)}`}</div>
                    </div>
                    <div className="av2-detail-item">
                      <div className="av2-detail-label">Duration</div>
                      <div className="av2-detail-value">{job.estimatedDuration || '—'}h</div>
                    </div>
                  </div>
                )}

                {/* Not yet approved message */}
                {(job.status === 'Estimate Requested' || job.status === 'Pending' || job.status === 'Estimate Sent') && (
                  <div style={{ fontSize: 13, color: 'var(--av2-text-muted)', fontStyle: 'italic' }}>
                    Awaiting estimate approval before scheduling.
                  </div>
                )}
              </div>
            )}
          </div>

          {/* ═══ SECTION 4: Completed Repair Details ═══ */}
          {(() => {
            const matchedInvoice = invoices.find(inv => inv.workOrderId === job.id);
            const isJobDone = job.status === 'Completed' || job.status === 'Done';
            const invoiceStatus = matchedInvoice?.status;
            const isPaid = invoiceStatus === 'paid';
            const isInvoiceSent = invoiceStatus === 'sent' || invoiceStatus === 'unpaid';
            const isInvoiceDraft = invoiceStatus === 'draft';

            // Badge priority: Paid > Invoice Sent > Invoice Draft > Done
            let badgeLabel = null;
            if (isJobDone) {
              if (isPaid) badgeLabel = 'Paid';
              else if (isInvoiceSent) badgeLabel = 'Invoice Sent';
              else if (isInvoiceDraft) badgeLabel = 'Invoice Draft';
              else badgeLabel = 'Done';
            }

            const sectionColor = isPaid ? '#007A53' : isJobDone ? '#007A53' : '#007A53';
            const receipts = matchedInvoice?.receipts || [];

            return (
          <div className="av2-job-section" style={{ paddingBottom: 0 }}>
            <SectionHeader
              sectionKey="completed" number="4" title="Completed Repair Details" icon="✅" color={sectionColor}
              badge={badgeLabel}
            />
            {openSections.completed && (
              <div style={{ paddingBottom: 16 }}>
                {isJobDone ? (
                  <>
                    {/* Payment status banner */}
                    {matchedInvoice && (
                      <div style={{
                        background: isPaid ? 'rgba(0,122,83,0.08)' : '#FEF3CD',
                        border: `1px solid ${isPaid ? 'rgba(0,122,83,0.18)' : '#F5DFA0'}`,
                        borderRadius: 8,
                        padding: '10px 14px',
                        marginBottom: 14,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <span style={{ fontSize: 18 }}>{isPaid ? '✅' : '💰'}</span>
                          <div>
                            <div style={{ fontSize: 13, fontWeight: 700, color: isPaid ? '#007A53' : '#D4880F' }}>
                              {isPaid ? 'Paid' : isInvoiceSent ? 'Invoice Sent — Awaiting Payment' : 'Invoice Draft'}
                            </div>
                            <div style={{ fontSize: 11, color: 'var(--av2-text-muted)', marginTop: 2 }}>
                              {matchedInvoice.invoiceNumber} · ${(matchedInvoice.total || 0).toFixed(2)}
                              {isPaid && matchedInvoice.paidAt?.seconds && (
                                <span> · Paid {new Date(matchedInvoice.paidAt.seconds * 1000).toLocaleDateString()}</span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Pre-repair site conditions */}
                    {(job.siteConditionNotes || (job.siteConditionPhotos && job.siteConditionPhotos.length > 0)) && (
                      <div style={{ background: '#F0F4FF', border: '1px solid #D4DFF7', borderRadius: 8, padding: 12, marginBottom: 14 }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: '#1A56DB', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>📋 Pre-Repair Site Conditions</div>
                        <div style={{ fontSize: 11, color: 'var(--av2-text-muted)', marginBottom: 6 }}>
                          By {job.siteConditionDocumentedBy || 'tech'} {job.siteConditionDocumentedAt?.seconds ? `on ${new Date(job.siteConditionDocumentedAt.seconds * 1000).toLocaleDateString()}` : ''}
                        </div>
                        {job.siteConditionNotes && <div className="av2-detail-notes-body" style={{ marginBottom: 8 }}>{job.siteConditionNotes}</div>}
                        {job.siteConditionPhotos && job.siteConditionPhotos.length > 0 && (
                          <div className="av2-photo-grid">{job.siteConditionPhotos.map((url, i) => (
                            <a key={i} href={url} target="_blank" rel="noopener noreferrer" className="av2-photo-thumb"><img src={url} alt={`Site ${i + 1}`} /></a>
                          ))}</div>
                        )}
                      </div>
                    )}

                    {/* Post-repair completion data */}
                    <div className="av2-detail-grid">
                      <div className="av2-detail-item">
                        <div className="av2-detail-label">Completed By</div>
                        <div className="av2-detail-value">{job.completedBy || '—'}</div>
                      </div>
                      <div className="av2-detail-item">
                        <div className="av2-detail-label">Hours Worked</div>
                        <div className="av2-detail-value">{job.hoursWorked || '—'}</div>
                      </div>
                      <div className="av2-detail-item">
                        <div className="av2-detail-label">Completed</div>
                        <div className="av2-detail-value">{job.completedAt?.seconds ? new Date(job.completedAt.seconds * 1000).toLocaleDateString() : '—'}</div>
                      </div>
                    </div>
                    {job.completionNotes && (
                      <div style={{ marginTop: 10 }}>
                        <div className="av2-detail-label">Tech Notes</div>
                        <div className="av2-detail-notes-body">{job.completionNotes}</div>
                      </div>
                    )}
                    {job.materialsUsed && job.materialsUsed.length > 0 && (
                      <div style={{ marginTop: 10 }}>
                        <div className="av2-detail-label">Materials {job.accountNumber && <span style={{ color: '#1A56DB', fontWeight: 500 }}>({job.accountNumber})</span>}</div>
                        <div style={{ marginTop: 4 }}>
                          {job.materialsUsed.map((m, i) => <div key={i} style={{ fontSize: 13, color: 'var(--av2-text)', padding: '2px 0' }}>• {m}</div>)}
                        </div>
                      </div>
                    )}
                    {job.jobPhotos && job.jobPhotos.length > 0 && (
                      <div style={{ marginTop: 10 }}>
                        <div className="av2-detail-label" style={{ marginBottom: 6 }}>After Photos ({job.jobPhotos.length})</div>
                        <div className="av2-photo-grid">
                          {job.jobPhotos.map((url, i) => (
                            <a key={i} href={url} target="_blank" rel="noopener noreferrer" className="av2-photo-thumb"><img src={url} alt={`After ${i + 1}`} /></a>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Receipts from the invoice */}
                    {receipts.length > 0 && (
                      <div style={{ marginTop: 14 }}>
                        <div className="av2-detail-label" style={{ marginBottom: 6 }}>🧾 Receipts ({receipts.length})</div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                          {receipts.map((r, i) => (
                            <a key={i} href={r.url} target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: 'var(--av2-bg)', border: '1px solid var(--av2-border)', borderRadius: 6, textDecoration: 'none', color: 'var(--av2-text)' }}>
                              <span style={{ fontSize: 16 }}>🧾</span>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.name}</div>
                                <div style={{ fontSize: 11, color: 'var(--av2-text-muted)' }}>
                                  {r.uploadedAt ? new Date(r.uploadedAt).toLocaleDateString() : ''}
                                </div>
                              </div>
                              <span style={{ fontSize: 11, color: '#1A56DB', fontWeight: 600 }}>View →</span>
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div style={{ fontSize: 13, color: 'var(--av2-text-muted)', fontStyle: 'italic' }}>
                    Repair not yet completed.
                  </div>
                )}
              </div>
            )}
          </div>
            );
          })()}

        </div>

        <div className="av2-modal-footer av2-modal-footer-split">
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {(job.status === 'Estimate Requested' || job.status === 'Pending' || job.status === 'Estimate Sent') && (
              <button className="av2-btn-primary" onClick={() => handleStatusChange('Approved')}>
                Approve Without Estimate
              </button>
            )}
            {job.status === 'Scheduled' && (
              <button className="av2-btn-secondary" onClick={() => handleStatusChange('In Progress')}>
                Mark In Progress
              </button>
            )}
            {job.status === 'In Progress' && (
              <button className="av2-btn-primary" onClick={() => handleStatusChange('Completed')}>
                Mark Complete
              </button>
            )}
            {job.status !== 'Completed' && job.status !== 'Done' && job.status !== 'Declined' && job.status !== 'Canceled' && (
              <>
                <button className="av2-btn-secondary" style={{ color: '#D4880F', borderColor: '#D4880F' }} onClick={() => handleStatusChange('Declined')}>
                  Decline
                </button>
                <button className="av2-btn-secondary" style={{ color: '#C0392B', borderColor: '#C0392B' }} onClick={() => handleStatusChange('Canceled')}>
                  Cancel
                </button>
              </>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="av2-btn-danger" onClick={handleDeleteWorkOrder} disabled={saving} style={{ fontSize: 12 }}>
              🗑 Delete
            </button>
            <button className="av2-btn-secondary" onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════
   INVOICES PAGE — 4-Column Pipeline
   ══════════════════════════════════════════════ */
const INVOICE_STAGES = [
  { key: 'to-invoice', label: 'To Be Invoiced', statuses: ['to-invoice', 'draft'], color: '#D4880F' },
  { key: 'sent', label: 'Invoices Sent', statuses: ['sent', 'unpaid'], color: '#8E44AD' },
  { key: 'paid', label: 'Invoices Paid', statuses: ['paid'], color: '#007A53' },
  { key: 'overdue', label: 'Outstanding', statuses: ['overdue'], color: '#C0392B' },
];

/* ══════════════════════════════════════════════
   INVOICE DETAIL MODAL
   Handles three states:
   - Completed job (no invoice yet) → edit → Create Invoice
   - Draft invoice → edit → Send
   - Sent/Paid/Overdue invoice → read-only → Mark Paid / Unpaid
   ══════════════════════════════════════════════ */
function InvoiceDetailModal({ item, contacts, onClose, onCreateInvoice, onSendInvoice, onMarkPaid, onMarkUnpaid, onDelete, onUpdate, sending, saving }) {
  // Determine the mode
  const isCompletedJob = item._type === 'job';
  const isDraft = !isCompletedJob && item.status === 'draft';
  const isSent = !isCompletedJob && (item.status === 'sent' || item.status === 'unpaid');
  const isPaid = !isCompletedJob && item.status === 'paid';
  const isOverdue = item._stage === 'overdue';

  const defaultRate = 120;

  // Editing state
  const [editing, setEditing] = useState(false);
  const [hours, setHours] = useState(
    isCompletedJob ? (item.hoursWorked || item.estimate?.hours || '') : (item.hours || '')
  );
  // Rate is always derived from member status — never manually editable
  const rate = defaultRate;
  const [materialsCost, setMaterialsCost] = useState(
    isCompletedJob ? (item.estimate?.materials || 0) : (item.materialsCost || 0)
  );
  const [notes, setNotes] = useState(isCompletedJob ? '' : (item.notes || ''));
  const [receiptFiles, setReceiptFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [localSaving, setLocalSaving] = useState(false);

  const jobHours = Number(hours) || 0;
  const billableHours = jobHours;
  const laborTotal = billableHours * (Number(rate) || 0);
  const total = laborTotal + (Number(materialsCost) || 0);

  const existingReceipts = item.receipts || [];

  const handleReceiptSelect = (e) => {
    setReceiptFiles([...receiptFiles, ...Array.from(e.target.files)]);
  };

  // Create invoice from completed job
  const handleCreate = async () => {
    let receiptURLs = [];
    if (receiptFiles.length > 0) {
      setUploading(true);
      for (const file of receiptFiles) {
        const fileName = `${Date.now()}_${file.name}`;
        const storageRef = ref(storage, `receipts/${item.accountNumber || item.clientId || 'general'}/${fileName}`);
        await uploadBytes(storageRef, file);
        const url = await getDownloadURL(storageRef);
        receiptURLs.push({ name: file.name, url, uploadedAt: new Date().toISOString() });
      }
      setUploading(false);
    }

    const override = {
      ...item,
      hoursWorked: jobHours,
      estimate: { hours: jobHours, rate: Number(rate) || defaultRate, materials: Number(materialsCost) || 0 },
      _invoiceNotes: notes,
      _receipts: receiptURLs,
      _billableHours: billableHours,
      _laborTotal: laborTotal,
      _total: total,
    };
    onCreateInvoice(override);
    onClose();
  };

  // Save draft invoice edits
  const handleSaveDraft = async () => {
    setLocalSaving(true);
    try {
      let newReceipts = [];
      if (receiptFiles.length > 0) {
        setUploading(true);
        for (const file of receiptFiles) {
          const fileName = `${Date.now()}_${file.name}`;
          const storageRef = ref(storage, `receipts/${item.accountNumber || item.clientId || 'general'}/${fileName}`);
          await uploadBytes(storageRef, file);
          const url = await getDownloadURL(storageRef);
          newReceipts.push({ name: file.name, url, uploadedAt: new Date().toISOString() });
        }
        setUploading(false);
      }

      const draftTotal = (Number(hours) || 0) * (Number(rate) || 0) + (Number(materialsCost) || 0);

      await updateDoc(doc(db, 'invoices', item.id), {
        hours: Number(hours) || 0,
        rate: Number(rate) || 0,
        materialsCost: Number(materialsCost) || 0,
        total: draftTotal,
        receipts: [...existingReceipts, ...newReceipts],
        notes: notes,
      });
      setReceiptFiles([]);
      await onUpdate();
      setEditing(false);
    } catch (err) {
      dialog.alert('Error: ' + err.message);
    }
    setLocalSaving(false);
  };

  // Headline title varies by state
  const title = isCompletedJob
    ? (item.title || 'Untitled')
    : item.invoiceNumber;
  const subtitle = isCompletedJob
    ? 'Completed job — ready to invoice'
    : item.workOrderTitle;

  // Status badge
  const badgeLabel = isCompletedJob ? 'To Invoice'
    : isDraft ? 'Draft'
    : isPaid ? 'Paid'
    : isOverdue ? 'Overdue'
    : 'Sent';
  const badgeClass = isPaid ? 'av2-badge-completed'
    : isOverdue ? 'av2-badge-estimate-requested'
    : 'av2-badge-pending';

  return (
    <div className="av2-modal-overlay" onClick={onClose}>
      <div className="av2-modal av2-modal-large" onClick={e => e.stopPropagation()}>
        <div className="av2-modal-header">
          <div style={{ flex: 1 }}>
            <h2>{title}</h2>
            <div className="av2-modal-sub">
              {item.accountNumber && <span className="av2-acct-badge" style={{ marginRight: 6 }}>{item.accountNumber}</span>}
              <span className={`av2-badge ${badgeClass}`}>{badgeLabel}</span>
              {item.isNonMember && <span className="av2-nm-badge" style={{ marginLeft: 6 }}>One-Time</span>}
            </div>
          </div>
          <button className="av2-modal-close" onClick={onClose}>{Icons.close}</button>
        </div>

        <div className="av2-modal-body">
          {/* ── Details ── */}
          <div className="av2-job-section">
            <h3 className="av2-job-section-title">Details</h3>
            <div className="av2-detail-grid">
              <div className="av2-detail-item">
                <div className="av2-detail-label">Property ID</div>
                <div className="av2-detail-value">{(item.propertyId || item.accountNumber) ? <span className="av2-acct-badge" style={{ fontFamily: 'ui-monospace, monospace' }}>{item.propertyId || item.accountNumber}</span> : '—'}</div>
              </div>
              <div className="av2-detail-item">
                <div className="av2-detail-label">Client</div>
                <div className="av2-detail-value">{item.clientName || item.contactName || '—'}</div>
              </div>
              <div className="av2-detail-item">
                <div className="av2-detail-label">Job</div>
                <div className="av2-detail-value">{isCompletedJob ? item.title : item.workOrderTitle}</div>
              </div>
              {!isCompletedJob && (
                <div className="av2-detail-item">
                  <div className="av2-detail-label">Completed By</div>
                  <div className="av2-detail-value">{item.completedBy || '—'}</div>
                </div>
              )}
              {!isCompletedJob && (
                <div className="av2-detail-item">
                  <div className="av2-detail-label">Due Date</div>
                  <div className="av2-detail-value" style={{ color: isOverdue ? '#C0392B' : 'inherit' }}>
                    {item.dueDate ? new Date(item.dueDate).toLocaleDateString() : '—'}
                  </div>
                </div>
              )}
              {isCompletedJob && item.completedAt?.seconds && (
                <div className="av2-detail-item">
                  <div className="av2-detail-label">Completed On</div>
                  <div className="av2-detail-value">{new Date(item.completedAt.seconds * 1000).toLocaleDateString()}</div>
                </div>
              )}
              {isPaid && item.paidAt?.seconds && (
                <div className="av2-detail-item">
                  <div className="av2-detail-label">Paid On</div>
                  <div className="av2-detail-value">{new Date(item.paidAt.seconds * 1000).toLocaleDateString()}</div>
                </div>
              )}
            </div>
          </div>

          {/* ── Breakdown (read-only) OR Edit Form ── */}
          {!editing ? (
            <div className="av2-job-section">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <h3 className="av2-job-section-title" style={{ margin: 0 }}>Breakdown</h3>
                {(isCompletedJob || isDraft) && (
                  <button className="av2-btn-secondary" style={{ fontSize: 12, padding: '6px 12px' }} onClick={() => setEditing(true)}>
                    ✏️ Edit
                  </button>
                )}
              </div>
              <div style={{ background: 'var(--av2-bg)', borderRadius: 8, padding: 16, border: '1px solid var(--av2-border)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, marginBottom: 8 }}>
                  <span>Labor: {isCompletedJob ? jobHours : item.hours}h × ${isCompletedJob ? Number(rate) : item.rate}/hr</span>
                  <strong>${((isCompletedJob ? jobHours : item.hours) * (isCompletedJob ? Number(rate) : item.rate)).toFixed(2)}</strong>
                </div>
                {((isCompletedJob ? Number(materialsCost) : item.materialsCost) > 0) && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, marginBottom: 8 }}>
                    <span>Materials</span>
                    <strong>${Number(isCompletedJob ? materialsCost : item.materialsCost).toFixed(2)}</strong>
                  </div>
                )}
                <div style={{ borderTop: '1px solid var(--av2-border)', paddingTop: 10, marginTop: 10, display: 'flex', justifyContent: 'space-between', fontSize: 18 }}>
                  <span style={{ fontWeight: 700 }}>Total</span>
                  <span style={{ fontFamily: "var(--av2-font)", color: 'var(--av2-green)' }}>${(isCompletedJob ? total : (item.total || 0)).toFixed(2)}</span>
                </div>
              </div>
            </div>
          ) : (
            /* ── Edit form (completed jobs + drafts) ── */
            <div className="av2-job-section">
              <h3 className="av2-job-section-title">Edit Invoice</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div style={{ display: 'flex', gap: 10 }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--av2-text-muted)', textTransform: 'uppercase', letterSpacing: .5, display: 'block', marginBottom: 4 }}>Hours</label>
                    <input type="number" step="0.25" value={hours} onChange={e => setHours(e.target.value)} placeholder="0" style={{ width: '100%', padding: '8px 10px', border: '1px solid var(--av2-border)', borderRadius: 6, fontSize: 14, fontWeight: 600 }} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--av2-text-muted)', textTransform: 'uppercase', letterSpacing: .5, display: 'block', marginBottom: 4 }}>Rate ($/hr)</label>
                    <input
                      type="number"
                      value={120}
                      readOnly
                      disabled
                      title={item.isNonMember ? 'Standard rate (fixed)' : 'Standard rate (fixed)'}
                      style={{ width: '100%', padding: '8px 10px', border: '1px solid var(--av2-border)', borderRadius: 6, fontSize: 14, fontWeight: 600, background: 'var(--av2-bg)', color: 'var(--av2-text-muted)', cursor: 'not-allowed' }}
                    />
                    <div style={{ fontSize: 10, color: 'var(--av2-text-muted)', marginTop: 3 }}>
                      {'Standard rate'}
                    </div>
                  </div>
                </div>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--av2-text-muted)', textTransform: 'uppercase', letterSpacing: .5, display: 'block', marginBottom: 4 }}>Materials ($)</label>
                  <input type="number" value={materialsCost} onChange={e => setMaterialsCost(e.target.value)} placeholder="0" style={{ width: '100%', padding: '8px 10px', border: '1px solid var(--av2-border)', borderRadius: 6, fontSize: 14 }} />
                </div>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--av2-text-muted)', textTransform: 'uppercase', letterSpacing: .5, display: 'block', marginBottom: 4 }}>Notes</label>
                  <input type="text" value={notes} onChange={e => setNotes(e.target.value)} placeholder="Optional notes for invoice..." style={{ width: '100%', padding: '8px 10px', border: '1px solid var(--av2-border)', borderRadius: 6, fontSize: 13 }} />
                </div>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--av2-text-muted)', textTransform: 'uppercase', letterSpacing: .5, display: 'block', marginBottom: 4 }}>Add Receipts</label>
                  <input type="file" multiple accept="image/*,.pdf" onChange={handleReceiptSelect} style={{ width: '100%', padding: '6px 0', fontSize: 12 }} />
                  {receiptFiles.length > 0 && <div style={{ fontSize: 12, color: 'var(--av2-green)', marginTop: 4, fontWeight: 600 }}>{receiptFiles.length} new receipt{receiptFiles.length !== 1 ? 's' : ''} selected</div>}
                </div>
                <div style={{ background: 'var(--av2-bg)', borderRadius: 8, padding: 14, border: '1px solid var(--av2-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontWeight: 700, fontSize: 14 }}>Total</span>
                  <span style={{ fontFamily: "var(--av2-font)", fontSize: 22, color: 'var(--av2-green)' }}>${total.toFixed(2)}</span>
                </div>
              </div>
            </div>
          )}

          {/* ── Materials Purchased (read-only, sent/paid invoices only) ── */}
          {!isCompletedJob && !editing && item.materialsUsed && item.materialsUsed.length > 0 && (
            <div className="av2-job-section">
              <h3 className="av2-job-section-title">Materials Purchased</h3>
              <div style={{ fontSize: 13 }}>
                {item.materialsUsed.map((m, i) => (
                  <div key={i} style={{ padding: '4px 0', borderBottom: '1px solid var(--av2-border)' }}>• {m}</div>
                ))}
              </div>
              {item.accountNumber && (
                <div style={{ fontSize: 12, color: '#1A56DB', marginTop: 8 }}>
                  Billed to Home Depot account: {item.accountNumber}
                </div>
              )}
            </div>
          )}

          {/* ── Receipts ── */}
          {existingReceipts.length > 0 && (
            <div className="av2-job-section">
              <h3 className="av2-job-section-title">🧾 Receipts ({existingReceipts.length})</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {existingReceipts.map((r, i) => (
                  <a key={i} href={r.url} target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: 'var(--av2-bg)', border: '1px solid var(--av2-border)', borderRadius: 6, textDecoration: 'none', color: 'var(--av2-text)' }}>
                    <span style={{ fontSize: 18 }}>🧾</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.name}</div>
                      <div style={{ fontSize: 11, color: 'var(--av2-text-muted)' }}>
                        {r.uploadedAt ? new Date(r.uploadedAt).toLocaleDateString() : ''}
                      </div>
                    </div>
                    <span style={{ fontSize: 11, color: '#1A56DB', fontWeight: 600 }}>View →</span>
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* ── Footer Actions (context-aware) ── */}
        <div className="av2-modal-footer av2-modal-footer-split">
          <div style={{ display: 'flex', gap: 8 }}>
            {editing && isCompletedJob && (
              <button className="av2-btn-primary" onClick={handleCreate} disabled={uploading}>
                {uploading ? 'Uploading...' : 'Create Invoice'}
              </button>
            )}
            {editing && isDraft && (
              <button className="av2-btn-primary" onClick={handleSaveDraft} disabled={localSaving || uploading}>
                {uploading ? 'Uploading...' : localSaving ? 'Saving...' : 'Save'}
              </button>
            )}
            {editing && (
              <button className="av2-btn-secondary" onClick={() => setEditing(false)}>Cancel</button>
            )}
            {!editing && isCompletedJob && (
              <button className="av2-btn-primary" onClick={() => setEditing(true)}>
                Create Invoice
              </button>
            )}
            {!editing && isDraft && (
              <button className="av2-btn-primary" onClick={() => { onSendInvoice(item); onClose(); }} disabled={sending}>
                {sending ? 'Sending...' : '📱 Send Invoice'}
              </button>
            )}
            {!editing && (isSent || isOverdue) && (
              <button className="av2-btn-primary" onClick={() => { onMarkPaid(item); onClose(); }} disabled={saving}>
                Mark Paid
              </button>
            )}
            {!editing && isPaid && (
              <button className="av2-btn-secondary" onClick={() => { onMarkUnpaid(item); onClose(); }} disabled={saving}>
                Mark Unpaid
              </button>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {!editing && !isCompletedJob && onDelete && (
              <button
                className="av2-btn-danger"
                onClick={() => { onDelete(item); }}
                disabled={saving}
                style={{ fontSize: 12 }}
              >
                🗑 Delete
              </button>
            )}
            <button className="av2-btn-secondary" onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    </div>
  );
}


function InvoicesPage({ invoices, workOrders, contacts, loadData, targetInvoiceId, clearTarget }) {
  const [selectedItem, setSelectedItem] = useState(null);
  const [view, setView] = useState('pipeline');
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [expandedStages, setExpandedStages] = useState({});

  // Deep-link: auto-open the target invoice if it's in this page's data
  useEffect(() => {
    if (!targetInvoiceId) return;
    const inv = invoices.find(i => i.id === targetInvoiceId);
    if (inv) {
      setSelectedItem(inv);
      clearTarget?.();
    }
  }, [targetInvoiceId, invoices, clearTarget]);

  // Completed jobs that don't have an invoice yet = "To Be Invoiced"
  const completedJobs = workOrders.filter(w =>
    (w.status === 'Completed' || w.status === 'Done') &&
    !invoices.some(inv => inv.workOrderId === w.id)
  );

  // Tag overdue invoices (sent/unpaid + past due date)
  const enrichedInvoices = invoices.map(inv => {
    const isOverdue = (inv.status === 'sent' || inv.status === 'unpaid') && inv.dueDate && new Date(inv.dueDate) < new Date();
    return { ...inv, _stage: inv.status === 'paid' ? 'paid' : isOverdue ? 'overdue' : (inv.status === 'sent' || inv.status === 'unpaid') ? 'sent' : inv.status };
  });

  // Build items for each stage
  const getItemsForStage = (stage) => {
    if (stage.key === 'to-invoice') {
      // Combine: completed jobs without invoices + draft invoices
      const uninvoicedJobs = completedJobs
        .filter(j => !search || (j.title || '').toLowerCase().includes(search.toLowerCase()) || (j.accountNumber || '').toLowerCase().includes(search.toLowerCase()))
        .map(j => ({ ...j, _type: 'job' }));
      const draftInvoices = enrichedInvoices
        .filter(inv => inv.status === 'draft')
        .filter(inv => !search || (inv.invoiceNumber || '').toLowerCase().includes(search.toLowerCase()) || (inv.accountNumber || '').toLowerCase().includes(search.toLowerCase()) || (inv.workOrderTitle || '').toLowerCase().includes(search.toLowerCase()))
        .map(inv => ({ ...inv, _type: 'invoice' }));
      return [...uninvoicedJobs, ...draftInvoices].sort((a, b) => (a.completedAt?.seconds || a.createdAt?.seconds || 0) - (b.completedAt?.seconds || b.createdAt?.seconds || 0));
    }
    return enrichedInvoices
      .filter(inv => {
        if (!stage.statuses.includes(inv._stage)) return false;
        if (search && !(
          (inv.invoiceNumber || '').toLowerCase().includes(search.toLowerCase()) ||
          (inv.workOrderTitle || '').toLowerCase().includes(search.toLowerCase()) ||
          (inv.accountNumber || '').toLowerCase().includes(search.toLowerCase())
        )) return false;
        return true;
      })
      .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
  };

  // Totals
  const sentTotal = enrichedInvoices.filter(i => i._stage === 'sent').reduce((s, i) => s + (i.total || 0), 0);
  const paidTotal = enrichedInvoices.filter(i => i._stage === 'paid').reduce((s, i) => s + (i.total || 0), 0);
  const overdueTotal = enrichedInvoices.filter(i => i._stage === 'overdue').reduce((s, i) => s + (i.total || 0), 0);

  // Create invoice from completed job
  const handleCreateInvoice = async (job) => {
    const invoiceRate = 120;
    const invoiceHours = job.estimate?.hours || job.hoursWorked || 0;
    const invoiceMaterials = job.estimate?.materials || 0;

    const billableHours = job._billableHours !== undefined ? job._billableHours : invoiceHours;
    const laborTotal = job._laborTotal !== undefined ? job._laborTotal : billableHours * invoiceRate;
    const total = job._total !== undefined ? job._total : laborTotal + invoiceMaterials;

    // Use the work order number as the invoice identifier (single ID per job)
    const invNum = job.woNumber || `NO-WO-${Date.now()}`;

    // Due date = Net 5
    const dueDate = new Date();
    dueDate.setDate(dueDate.getDate() + 5);

    const invoiceData = {
      invoiceNumber: invNum,
      woNumber: job.woNumber || null,
      workOrderId: job.id,
      workOrderTitle: job.title,
      clientId: job.clientId || null,
      clientName: job.clientName || job.contactName || '',
      clientEmail: job.clientEmail || job.contactEmail || '',
      propertyId: job.propertyId || null,
      accountNumber: job.accountNumber || null,
      isNonMember: job.isNonMember || false,
      hours: invoiceHours,
      rate: invoiceRate,
      billableHours: billableHours,
      laborTotal: laborTotal,
      materialsCost: invoiceMaterials,
      materialsUsed: job.materialsUsed || [],
      receipts: job._receipts || [],
      notes: job._invoiceNotes || '',
      total: total,
      status: 'draft',
      dueDate: dueDate.toISOString().split('T')[0],
      createdAt: serverTimestamp(),
      completedAt: job.completedAt || null,
      completedBy: job.completedBy || '',
    };

    try {
      await addDoc(collection(db, 'invoices'), invoiceData);
      await loadData();
    } catch (err) {
      dialog.alert('Error creating invoice: ' + err.message);
    }
  };

  // Send invoice via SMS + Stripe payment link
  const handleSendInvoice = async (inv) => {
    if (!(await dialog.confirm(`Send invoice ${inv.invoiceNumber} ($${(inv.total || 0).toFixed(2)}) to client via text?`))) return;
    setSending(true);
    try {
      const resp = await fetch('https://us-central1-reliable-handyman.cloudfunctions.net/sendInvoice', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ invoiceId: inv.id }),
      });
      const data = await resp.json();
      if (data.success) {
        dialog.alert('Invoice sent via text with payment link!');
        await loadData();
      } else {
        dialog.alert('Error: ' + (data.error || 'Failed to send'));
      }
    } catch (err) {
      dialog.alert('Error: ' + err.message);
    }
    setSending(false);
  };

  // Mark invoice paid
  const handleMarkPaid = async (inv) => {
    if (!(await dialog.confirm(`Mark ${inv.invoiceNumber} as paid?`))) return;
    setSaving(true);
    try {
      await updateDoc(doc(db, 'invoices', inv.id), { status: 'paid', paidAt: serverTimestamp() });
      await loadData();
    } catch (err) { dialog.alert('Error: ' + err.message); }
    setSaving(false);
  };

  // Mark invoice unpaid (undo)
  const handleMarkUnpaid = async (inv) => {
    setSaving(true);
    try {
      await updateDoc(doc(db, 'invoices', inv.id), { status: 'sent', paidAt: null });
      await loadData();
    } catch (err) { dialog.alert('Error: ' + err.message); }
    setSaving(false);
  };

  const handleDeleteInvoice = async (inv) => {
    if (!(await dialog.confirm(`Delete invoice ${inv.invoiceNumber || ''}? This cannot be undone.`))) return;
    setSaving(true);
    try {
      await deleteDoc(doc(db, 'invoices', inv.id));
      setSelectedItem(null);
      await loadData();
    } catch (err) { dialog.alert('Error: ' + err.message); }
    setSaving(false);
  };

  return (
    <div className="av2-page">
      <div className="av2-page-header">
        <h1>Invoices</h1>
        <div style={{ display: 'flex', gap: 12 }}>
          <div className="av2-view-toggle">
            <button className={view === 'pipeline' ? 'active' : ''} onClick={() => setView('pipeline')}>Pipeline</button>
            <button className={view === 'list' ? 'active' : ''} onClick={() => setView('list')}>List</button>
          </div>
          <button className="av2-btn-secondary" onClick={loadData}>
            {Icons.refresh} Refresh
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="av2-filters-bar">
        <div className="av2-search-box">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
          <input placeholder="Search by client, invoice #, account #, or job..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
      </div>

      {/* ═══ Pipeline View ═══ */}
      {view === 'pipeline' && (
        <>
          {/* Stat strip */}
          <div className="av2-stage-strip" style={{ gridTemplateColumns: `repeat(${INVOICE_STAGES.length}, 1fr)` }}>
            {INVOICE_STAGES.map(stage => {
              const count = getItemsForStage(stage).length;
              return (
                <div
                  key={stage.key}
                  className={`av2-stage-stat ${expandedStages[stage.key] === false ? '' : 'active'}`}
                  onClick={() => setExpandedStages(prev => ({ ...prev, [stage.key]: prev[stage.key] === false ? true : false }))}
                >
                  <span className="av2-stage-stat-dot" style={{ background: stage.color }} />
                  <div className="av2-stage-stat-content">
                    <div className="av2-stage-stat-count">{count}</div>
                    <div className="av2-stage-stat-label">{stage.label}</div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Stage groups */}
          <div className="av2-stage-groups">
            {INVOICE_STAGES.map(stage => {
              const items = getItemsForStage(stage);
              const collapsed = expandedStages[stage.key] === false;
              return (
                <div key={stage.key} className={`av2-stage-group ${collapsed ? 'collapsed' : ''}`}>
                  <div
                    className="av2-stage-group-header"
                    onClick={() => setExpandedStages(prev => ({ ...prev, [stage.key]: prev[stage.key] === false ? true : false }))}
                  >
                    <span className="av2-stage-group-dot" style={{ background: stage.color }} />
                    <span className="av2-stage-group-title">{stage.label}</span>
                    <span className="av2-stage-group-count">{items.length}</span>
                    <svg className="av2-stage-group-chevron" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </div>
                  <div className="av2-stage-group-body">
                    {items.length === 0 ? (
                      <div className="av2-row-card-empty">{stage.key === 'to-invoice' ? 'No completed jobs to invoice' : 'None'}</div>
                    ) : (
                      items.map(item => {
                        const isJob = item._type === 'job';
                        const isDraft = !isJob && item.status === 'draft';

                        // Compute display amount
                        let amount = 0;
                        if (isJob) {
                          const jobRate = item.estimate?.rate || (120);
                          const jobHours = item.hoursWorked || item.estimate?.hours || 0;
                          const jobMaterials = item.estimate?.materials || 0;
                          amount = jobHours * jobRate + jobMaterials;
                        } else {
                          amount = item.total || 0;
                        }

                        // Title
                        const cardTitle = isJob ? (item.title || 'Untitled') : item.invoiceNumber;
                        const cardSubtitle = isJob
                          ? (item.clientName || item.contactName || 'No client')
                          : (item.workOrderTitle || item.clientName || '');

                        // Date
                        const dueDate = item.dueDate ? new Date(item.dueDate) : null;
                        const daysUntilDue = dueDate ? Math.ceil((dueDate - new Date()) / (1000 * 60 * 60 * 24)) : null;

                        let dateStr = '—';
                        let trailClass = '';
                        if (isJob && item.completedAt?.seconds) {
                          dateStr = `Completed ${new Date(item.completedAt.seconds * 1000).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
                        } else if (isDraft && item.createdAt?.seconds) {
                          dateStr = `Drafted ${new Date(item.createdAt.seconds * 1000).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
                        } else if (stage.key === 'sent' && daysUntilDue !== null) {
                          dateStr = `Due ${daysUntilDue > 0 ? `in ${daysUntilDue}d` : 'today'}`;
                          if (daysUntilDue !== null && daysUntilDue <= 7) trailClass = 'warn';
                        } else if (stage.key === 'overdue' && daysUntilDue !== null) {
                          dateStr = `${Math.abs(daysUntilDue)}d overdue`;
                          trailClass = 'danger';
                        } else if (stage.key === 'paid' && item.paidAt?.seconds) {
                          dateStr = `Paid ${new Date(item.paidAt.seconds * 1000).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
                        }

                        const amountClass = stage.key === 'overdue' ? 'danger' : (stage.key === 'paid' ? 'success' : '');

                        return (
                          <div key={item.id} className="av2-row-card" onClick={() => setSelectedItem(item)}>
                            <span className="av2-row-card-ribbon" style={{ background: stage.color }} />
                            <div className="av2-row-card-num">
                              {isJob ? (item.woNumber || '—') : item.invoiceNumber}
                            </div>
                            <div className="av2-row-card-main">
                              <div className="av2-row-card-title">{cardTitle}</div>
                              <div className="av2-row-card-meta">
                                <span>{cardSubtitle}</span>
                                {item.accountNumber && (
                                  <>
                                    <span className="av2-row-card-meta-dot" />
                                    <span className="av2-acct-badge" style={{ fontSize: 11 }}>{item.accountNumber}</span>
                                  </>
                                )}
                                {item.isNonMember && (
                                  <>
                                    <span className="av2-row-card-meta-dot" />
                                    <span className="av2-nm-badge">One-Time</span>
                                  </>
                                )}
                                {isDraft && (
                                  <>
                                    <span className="av2-row-card-meta-dot" />
                                    <span style={{ fontSize: 11, color: 'var(--av2-warn)', fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase' }}>Draft</span>
                                  </>
                                )}
                              </div>
                            </div>
                            <div className={`av2-row-card-amount ${amountClass}`}>${amount.toFixed(0)}</div>
                            <div className={`av2-row-card-trail ${trailClass}`}>{dateStr}</div>
                            <svg className="av2-row-card-chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="9 18 15 12 9 6" />
                            </svg>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* ═══ List View ═══ */}
      {view === 'list' && (
        <div className="av2-section" style={{ padding: 0 }}>
          <div className="av2-table-wrap">
            <table className="av2-table">
              <thead>
                <tr>
                  <th>Invoice #</th>
                  <th>Acct#</th>
                  <th>Job</th>
                  <th>Hours</th>
                  <th>Total</th>
                  <th>Due Date</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {enrichedInvoices.length === 0 ? (
                  <tr><td colSpan={8} style={{ textAlign: 'center', padding: 40, color: 'var(--av2-text-muted)' }}>No invoices yet. Complete a job and create an invoice from the pipeline.</td></tr>
                ) : enrichedInvoices
                  .filter(inv => !search || (inv.invoiceNumber || '').toLowerCase().includes(search.toLowerCase()) || (inv.accountNumber || '').toLowerCase().includes(search.toLowerCase()) || (inv.workOrderTitle || '').toLowerCase().includes(search.toLowerCase()))
                  .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0))
                  .map(inv => {
                    const isOverdue = inv._stage === 'overdue';
                    return (
                      <tr key={inv.id} style={{ cursor: 'pointer' }} onClick={() => setSelectedItem(inv)}>
                        <td style={{ fontWeight: 700, fontSize: 12 }}>{inv.invoiceNumber}</td>
                        <td>{inv.accountNumber ? <span className="av2-acct-badge" style={{ fontSize: 12 }}>{inv.accountNumber}</span> : '—'}</td>
                        <td style={{ fontSize: 12 }}>{inv.workOrderTitle}</td>
                        <td>{inv.hours}h × ${inv.rate}</td>
                        <td style={{ fontWeight: 700, fontFamily: "var(--av2-font)", fontSize: 16 }}>
                          ${(inv.total || 0).toFixed(2)}
                        </td>
                        <td style={{ fontSize: 12, color: isOverdue ? '#C0392B' : 'var(--av2-text-muted)' }}>
                          {inv.dueDate ? new Date(inv.dueDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—'}
                          {isOverdue && <span style={{ fontWeight: 700 }}> (overdue)</span>}
                        </td>
                        <td>
                          <span className={`av2-badge ${inv.status === 'paid' ? 'av2-badge-completed' : isOverdue ? 'av2-badge-estimate-requested' : 'av2-badge-pending'}`}>
                            {inv.status === 'paid' ? 'Paid' : isOverdue ? 'Overdue' : 'Sent'}
                          </span>
                        </td>
                        <td>
                          {inv.status !== 'paid' ? (
                            <button className="av2-btn-primary" style={{ fontSize: 11, padding: '6px 12px' }} onClick={(e) => { e.stopPropagation(); handleMarkPaid(inv); }}>
                              Mark Paid
                            </button>
                          ) : (
                            <button className="av2-btn-secondary" style={{ fontSize: 11, padding: '6px 10px' }} onClick={(e) => { e.stopPropagation(); handleMarkUnpaid(inv); }}>
                              Undo
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ═══ Invoice Detail Modal ═══ */}
      {selectedItem && (
        <InvoiceDetailModal
          item={selectedItem}
          contacts={contacts}
          onClose={() => setSelectedItem(null)}
          onCreateInvoice={handleCreateInvoice}
          onSendInvoice={handleSendInvoice}
          onMarkPaid={handleMarkPaid}
          onMarkUnpaid={handleMarkUnpaid}
          onDelete={handleDeleteInvoice}
          onUpdate={loadData}
          sending={sending}
          saving={saving}
        />
      )}

    </div>
  );
}

/* ══════════════════════════════════════════════
   CALENDAR PAGE
   ══════════════════════════════════════════════ */
function CalendarPage({ workOrders, contacts = [], employees, invoices = [], loadData }) {
  const [selectedDate, setSelectedDate] = useState(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  });
  const [selectedJob, setSelectedJob] = useState(null);

  const techList = employees.filter(e => e.status !== 'terminated');

  // Multi-select tech filter — default: all techs visible
  const [visibleTechs, setVisibleTechs] = useState(() => new Set(techList.map(t => t.name)));

  // Keep visibleTechs in sync if the employee list changes after mount
  useEffect(() => {
    setVisibleTechs(prev => {
      const techNames = new Set(techList.map(t => t.name));
      // If prev is empty or contains names that no longer exist, just reset to all
      if (prev.size === 0) return techNames;
      const filtered = new Set([...prev].filter(n => techNames.has(n)));
      return filtered.size === 0 ? techNames : filtered;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employees.length]);

  const toggleTech = (name) => {
    setVisibleTechs(prev => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name); else next.add(name);
      return next;
    });
  };

  const showAllTechs = () => setVisibleTechs(new Set(techList.map(t => t.name)));
  const hideAllTechs = () => setVisibleTechs(new Set());

  const formatDate = (d) => d.toISOString().split('T')[0];

  // All jobs scheduled for the selected day (before tech filtering)
  const dayJobs = workOrders.filter(wo => {
    if (!wo.scheduledDate) return false;
    if (wo.status === 'Completed' || wo.status === 'Done') return false;
    return wo.scheduledDate === formatDate(selectedDate);
  });

  // Group jobs by tech (only for visible techs); collect unassigned separately
  const jobsByTech = {};
  const unassignedJobs = [];
  dayJobs.forEach(j => {
    if (!j.assignedTo) {
      unassignedJobs.push(j);
      return;
    }
    if (!visibleTechs.has(j.assignedTo)) return;
    if (!jobsByTech[j.assignedTo]) jobsByTech[j.assignedTo] = [];
    jobsByTech[j.assignedTo].push(j);
  });
  // Sort each tech's jobs by time
  Object.keys(jobsByTech).forEach(k => {
    jobsByTech[k].sort((a, b) => (a.scheduledTime || '00:00').localeCompare(b.scheduledTime || '00:00'));
  });
  unassignedJobs.sort((a, b) => (a.scheduledTime || '00:00').localeCompare(b.scheduledTime || '00:00'));

  // Column list: one per visible tech, in the order employees are stored
  const visibleTechColumns = techList.filter(t => visibleTechs.has(t.name));

  const prevDay = () => {
    const d = new Date(selectedDate);
    d.setDate(d.getDate() - 1);
    setSelectedDate(d);
  };
  const nextDay = () => {
    const d = new Date(selectedDate);
    d.setDate(d.getDate() + 1);
    setSelectedDate(d);
  };
  const goToday = () => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    setSelectedDate(d);
  };

  const isToday = (() => {
    const t = new Date();
    return selectedDate.getDate() === t.getDate() && selectedDate.getMonth() === t.getMonth() && selectedDate.getFullYear() === t.getFullYear();
  })();

  // Deterministic tech color
  const getTechColor = (techName) => {
    if (!techName) return '#86868B';
    const colors = ['#007A53', '#1E6091', '#8E44AD', '#D4880F', '#C0392B', '#16A085', '#27AE60'];
    const hash = techName.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0);
    return colors[hash % colors.length];
  };

  return (
    <div className="av2-page">
      <div className="av2-page-header">
        <h1>Calendar</h1>
        <button className="av2-btn-secondary" onClick={loadData}>
          {Icons.refresh} Refresh
        </button>
      </div>

      {/* Day Navigator */}
      <div className="av2-cal-nav">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button className="av2-cal-nav-btn" onClick={prevDay}>‹</button>
          <button className="av2-cal-today" onClick={goToday}>Today</button>
          <button className="av2-cal-nav-btn" onClick={nextDay}>›</button>
          <input
            type="date"
            value={formatDate(selectedDate)}
            onChange={e => {
              const [y, m, d] = e.target.value.split('-').map(Number);
              const nd = new Date(y, m - 1, d);
              nd.setHours(0, 0, 0, 0);
              setSelectedDate(nd);
            }}
            style={{ marginLeft: 8, padding: '6px 8px', border: '1px solid var(--av2-border)', borderRadius: 6, fontSize: 13 }}
          />
        </div>
        <div className="av2-cal-title">
          {selectedDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
          {isToday && <span style={{ marginLeft: 10, fontSize: 11, color: 'var(--av2-green)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1 }}>Today</span>}
        </div>
      </div>

      {/* Tech multi-select filter */}
      <div style={{ background: 'var(--av2-bg)', border: '1px solid var(--av2-border)', borderRadius: 8, padding: 12, marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10, flexWrap: 'wrap' }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--av2-text-muted)', textTransform: 'uppercase', letterSpacing: 1 }}>
            Show Techs
          </div>
          <button className="av2-btn-secondary" style={{ fontSize: 11, padding: '4px 10px' }} onClick={showAllTechs}>All</button>
          <button className="av2-btn-secondary" style={{ fontSize: 11, padding: '4px 10px' }} onClick={hideAllTechs}>None</button>
          <div style={{ fontSize: 12, color: 'var(--av2-text-muted)', marginLeft: 'auto' }}>
            {visibleTechs.size} of {techList.length} visible
          </div>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {techList.map(tech => {
            const active = visibleTechs.has(tech.name);
            const color = getTechColor(tech.name);
            const count = dayJobs.filter(j => j.assignedTo === tech.name).length;
            return (
              <label
                key={tech.id}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer',
                  padding: '6px 12px', borderRadius: 6, fontSize: 13,
                  background: active ? '#fff' : 'transparent',
                  border: `1px solid ${active ? color : 'var(--av2-border)'}`,
                  color: active ? 'var(--av2-text)' : 'var(--av2-text-muted)',
                  fontWeight: active ? 600 : 400,
                }}
              >
                <input
                  type="checkbox"
                  checked={active}
                  onChange={() => toggleTech(tech.name)}
                  style={{ margin: 0 }}
                />
                <span style={{ width: 10, height: 10, borderRadius: '50%', background: color, display: 'inline-block' }} />
                {tech.name}
                {count > 0 && <span style={{ fontSize: 11, color: 'var(--av2-text-muted)', fontWeight: 500 }}>({count})</span>}
              </label>
            );
          })}
        </div>
      </div>

      {/* Unassigned jobs banner (always shown when there are unassigned for the day) */}
      {unassignedJobs.length > 0 && (
        <div style={{ background: '#FEF3CD', border: '1px solid #F5DFA0', borderRadius: 8, padding: 14, marginBottom: 16 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#D4880F', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10 }}>
            Unassigned ({unassignedJobs.length}) — click a job to assign a tech
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {unassignedJobs.map(job => (
              <div
                key={job.id}
                onClick={() => setSelectedJob(job)}
                style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', background: '#fff', border: '1px solid var(--av2-border)', borderRadius: 6, cursor: 'pointer' }}
              >
                {job.scheduledTime && <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--av2-text)', minWidth: 54 }}>{job.scheduledTime}</span>}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{job.title}</div>
                  <div style={{ fontSize: 11, color: 'var(--av2-text-muted)' }}>
                    {(job.propertyId || job.accountNumber) && (
                      <span style={{ fontFamily: 'ui-monospace, monospace', color: 'var(--av2-green)', fontWeight: 700, marginRight: 6 }}>{job.propertyId || job.accountNumber}</span>
                    )}
                    {job.clientName || job.contactName}
                  </div>
                </div>
                <span className={`av2-badge av2-badge-${(job.status || '').toLowerCase().replace(/ /g, '-')}`} style={{ fontSize: 9 }}>{job.status}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Day view: one column per visible tech */}
      {visibleTechColumns.length === 0 ? (
        <div className="av2-section" style={{ padding: 40, textAlign: 'center' }}>
          <div style={{ fontSize: 14, color: 'var(--av2-text-muted)' }}>No techs selected. Toggle techs above to see their jobs.</div>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: `repeat(${visibleTechColumns.length}, minmax(240px, 1fr))`, gap: 12, overflowX: 'auto' }}>
          {visibleTechColumns.map(tech => {
            const techJobs = jobsByTech[tech.name] || [];
            const color = getTechColor(tech.name);
            return (
              <div key={tech.id} style={{ background: 'var(--av2-bg)', border: '1px solid var(--av2-border)', borderRadius: 8, minHeight: 300 }}>
                <div style={{ padding: '12px 14px', borderBottom: `3px solid ${color}`, background: '#fff', borderTopLeftRadius: 8, borderTopRightRadius: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                    <span style={{ width: 10, height: 10, borderRadius: '50%', background: color, flexShrink: 0 }} />
                    <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--av2-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{tech.name}</div>
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--av2-text-muted)', fontWeight: 600 }}>
                    {techJobs.length} {techJobs.length === 1 ? 'job' : 'jobs'}
                  </div>
                </div>
                <div style={{ padding: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {techJobs.length === 0 ? (
                    <div style={{ fontSize: 12, color: 'var(--av2-text-muted)', textAlign: 'center', padding: '20px 0' }}>No jobs scheduled</div>
                  ) : (
                    techJobs.map(job => (
                      <div
                        key={job.id}
                        onClick={() => setSelectedJob(job)}
                        style={{
                          background: '#fff',
                          border: '1px solid var(--av2-border)',
                          borderLeft: `3px solid ${color}`,
                          borderRadius: 6,
                          padding: '10px 12px',
                          cursor: 'pointer',
                        }}
                      >
                        {job.scheduledTime && (
                          <div style={{ fontSize: 11, fontWeight: 700, color: color, marginBottom: 4 }}>
                            {job.scheduledTime}
                            {job.estimatedDuration && <span style={{ color: 'var(--av2-text-muted)', fontWeight: 500, marginLeft: 6 }}>· {job.estimatedDuration}h</span>}
                          </div>
                        )}
                        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--av2-text)', marginBottom: 2 }}>{job.title}</div>
                        <div style={{ fontSize: 11, color: 'var(--av2-text-muted)', marginBottom: 6 }}>
                          {(job.propertyId || job.accountNumber) && (
                            <span style={{ fontFamily: 'ui-monospace, monospace', color: 'var(--av2-green)', fontWeight: 700, marginRight: 6 }}>
                              {job.propertyId || job.accountNumber}
                            </span>
                          )}
                          {job.clientName || job.contactName || 'No client'}
                        </div>
                        <span className={`av2-badge av2-badge-${(job.status || '').toLowerCase().replace(/ /g, '-')}`} style={{ fontSize: 9 }}>{job.status}</span>
                      </div>
                    ))
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {selectedJob && (
        <JobDetail
          job={selectedJob}
          workOrders={workOrders}
          contacts={contacts}
          employees={employees}
          invoices={invoices}
          onClose={() => setSelectedJob(null)}
          onUpdate={async () => { await loadData(); }}
        />
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════
   EMPLOYEES PAGE
   ══════════════════════════════════════════════ */
function EmployeesPage({ employees, workOrders, loadData }) {
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [showAddEmployee, setShowAddEmployee] = useState(false);
  const [search, setSearch] = useState('');
  const [inviteSending, setInviteSending] = useState(null);
  const [empForm, setEmpForm] = useState({ name: '', phone: '', email: '', role: 'basic', hourlyRate: '', address: '', emergencyContact: '', emergencyPhone: '', startDate: '' });
  const [saving, setSaving] = useState(false);

  const activeEmps = employees.filter(e => e.status !== 'terminated');
  const inactiveEmps = employees.filter(e => e.status === 'terminated');

  const filtered = activeEmps.filter(e =>
    (e.name || '').toLowerCase().includes(search.toLowerCase()) ||
    (e.email || '').toLowerCase().includes(search.toLowerCase()) ||
    (e.phone || '').includes(search)
  );

  const handleAddEmployee = async () => {
    if (!empForm.name || !empForm.phone) {
      dialog.alert('Name and phone are required.');
      return;
    }
    setSaving(true);
    try {
      await addDoc(collection(db, 'employees'), {
        ...empForm,
        status: 'active',
        onboarded: false,
        createdAt: serverTimestamp(),
      });
      await loadData();
      setEmpForm({ name: '', phone: '', email: '', role: 'basic', hourlyRate: '', address: '', emergencyContact: '', emergencyPhone: '', startDate: '' });
      setShowAddEmployee(false);
    } catch (err) {
      dialog.alert('Error: ' + err.message);
    }
    setSaving(false);
  };

  const handleSendInvite = async (emp) => {
    setInviteSending(emp.id);
    try {
      const resp = await fetch('https://us-central1-reliable-handyman.cloudfunctions.net/sendEmployeeInvite', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ employeeId: emp.id }),
      });
      const data = await resp.json();
      if (data.success) {
        dialog.alert(`Invite sent to ${emp.name} at ${emp.phone}`);
        await loadData();
      } else {
        dialog.alert('Error: ' + (data.error || 'Could not send invite'));
      }
    } catch (err) {
      dialog.alert('Error: ' + err.message);
    }
    setInviteSending(null);
  };

  return (
    <div className="av2-page">
      <div className="av2-page-header">
        <h1>Employees</h1>
        <div style={{ display: 'flex', gap: 12 }}>
          <button className="av2-btn-secondary" onClick={loadData}>{Icons.refresh} Refresh</button>
          <button className="av2-btn-primary" onClick={() => { setEmpForm({ name: '', phone: '', email: '', role: 'basic', hourlyRate: '', address: '', emergencyContact: '', emergencyPhone: '', startDate: new Date().toISOString().split('T')[0] }); setShowAddEmployee(true); }}>
            {Icons.plus} Add Employee
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="av2-filters-bar">
        <div className="av2-search-box">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
          <input placeholder="Search employees..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
      </div>

      {/* Stats */}
      <div className="av2-client-stats">
        <div className="av2-stat-chip">
          <div className="av2-stat-chip-value">{activeEmps.length}</div>
          <div className="av2-stat-chip-label">Active</div>
        </div>
        <div className="av2-stat-chip">
          <div className="av2-stat-chip-value">{activeEmps.filter(e => e.onboarded).length}</div>
          <div className="av2-stat-chip-label">Onboarded</div>
        </div>
        <div className="av2-stat-chip">
          <div className="av2-stat-chip-value">{activeEmps.filter(e => !e.onboarded).length}</div>
          <div className="av2-stat-chip-label">Pending Invite</div>
        </div>
        <div className="av2-stat-chip">
          <div className="av2-stat-chip-value">{activeEmps.filter(e => e.role === 'admin').length}</div>
          <div className="av2-stat-chip-label">Admins</div>
        </div>
      </div>

      {/* Active Employees Table */}
      <div className="av2-section" style={{ padding: 0 }}>
        <div className="av2-section-header" style={{ padding: '16px 20px' }}>
          <h3>Active Employees ({filtered.length})</h3>
        </div>
        {filtered.length === 0 ? (
          <div className="av2-empty-state">
            <p>No employees yet. Click "Add Employee" to get started.</p>
          </div>
        ) : (
          <div className="av2-table-wrap">
            <table className="av2-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Role</th>
                  <th>Phone</th>
                  <th>Rate</th>
                  <th>Start Date</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(emp => (
                  <tr key={emp.id} style={{ cursor: 'pointer' }} onClick={() => setSelectedEmployee(emp)}>
                    <td style={{ fontWeight: 700 }}>{emp.name || '—'}</td>
                    <td><span className={`av2-plan-badge ${emp.role === 'admin' ? 'av2-plan-total' : 'av2-plan-starter'}`}>{emp.role === 'admin' ? 'Admin' : 'Tech'}</span></td>
                    <td>{emp.phone || '—'}</td>
                    <td style={{ fontWeight: 600 }}>${emp.hourlyRate || '—'}/hr</td>
                    <td>{emp.startDate || '—'}</td>
                    <td>
                      <span className={`av2-badge ${emp.onboarded ? 'av2-badge-completed' : 'av2-badge-pending'}`}>
                        {emp.onboarded ? 'Active' : 'Invited'}
                      </span>
                    </td>
                    <td onClick={e => e.stopPropagation()}>
                      {!emp.onboarded && (
                        <button
                          className="av2-btn-secondary"
                          style={{ fontSize: 11, padding: '6px 10px' }}
                          disabled={inviteSending === emp.id}
                          onClick={() => handleSendInvite(emp)}
                        >
                          {inviteSending === emp.id ? 'Sending...' : emp.inviteSentAt ? 'Resend' : 'Send Invite'}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Inactive Employees */}
      {inactiveEmps.length > 0 && (
        <div className="av2-section" style={{ padding: 0, marginTop: 20, opacity: 0.7 }}>
          <div className="av2-section-header" style={{ padding: '16px 20px' }}>
            <h3>Inactive ({inactiveEmps.length})</h3>
          </div>
          <div className="av2-table-wrap">
            <table className="av2-table">
              <thead>
                <tr><th>Name</th><th>Role</th><th>Phone</th><th>End Date</th></tr>
              </thead>
              <tbody>
                {inactiveEmps.map(emp => (
                  <tr key={emp.id}>
                    <td>{emp.name}</td>
                    <td>{emp.role === 'admin' ? 'Admin' : 'Tech'}</td>
                    <td>{emp.phone || '—'}</td>
                    <td>{emp.endDate || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Add Employee Modal */}
      {showAddEmployee && (
        <div className="av2-modal-overlay" onClick={() => setShowAddEmployee(false)}>
          <div className="av2-modal" onClick={e => e.stopPropagation()}>
            <div className="av2-modal-header">
              <h2>Add Employee</h2>
              <button className="av2-modal-close" onClick={() => setShowAddEmployee(false)}>{Icons.close}</button>
            </div>
            <div className="av2-modal-body">
              <div className="av2-form-field">
                <label>Full Name *</label>
                <input value={empForm.name} onChange={e => setEmpForm({ ...empForm, name: e.target.value })} placeholder="John Smith" />
              </div>
              <div className="av2-form-row">
                <div className="av2-form-field">
                  <label>Phone *</label>
                  <input value={empForm.phone} onChange={e => setEmpForm({ ...empForm, phone: e.target.value })} placeholder="(516) 555-1234" />
                </div>
                <div className="av2-form-field">
                  <label>Email</label>
                  <input type="email" value={empForm.email} onChange={e => setEmpForm({ ...empForm, email: e.target.value })} />
                </div>
              </div>
              <div className="av2-form-row">
                <div className="av2-form-field">
                  <label>Role</label>
                  <select value={empForm.role} onChange={e => setEmpForm({ ...empForm, role: e.target.value })}>
                    <option value="basic">Technician</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
                <div className="av2-form-field">
                  <label>Hourly Rate ($)</label>
                  <input type="number" value={empForm.hourlyRate} onChange={e => setEmpForm({ ...empForm, hourlyRate: e.target.value })} placeholder="25" />
                </div>
              </div>
              <div className="av2-form-field">
                <label>Address</label>
                <input value={empForm.address} onChange={e => setEmpForm({ ...empForm, address: e.target.value })} />
              </div>
              <div className="av2-form-row">
                <div className="av2-form-field">
                  <label>Emergency Contact</label>
                  <input value={empForm.emergencyContact} onChange={e => setEmpForm({ ...empForm, emergencyContact: e.target.value })} />
                </div>
                <div className="av2-form-field">
                  <label>Emergency Phone</label>
                  <input value={empForm.emergencyPhone} onChange={e => setEmpForm({ ...empForm, emergencyPhone: e.target.value })} />
                </div>
              </div>
              <div className="av2-form-field">
                <label>Start Date</label>
                <input type="date" value={empForm.startDate} onChange={e => setEmpForm({ ...empForm, startDate: e.target.value })} />
              </div>
            </div>
            <div className="av2-modal-footer">
              <button className="av2-btn-secondary" onClick={() => setShowAddEmployee(false)}>Cancel</button>
              <button className="av2-btn-primary" onClick={handleAddEmployee} disabled={saving || !empForm.name || !empForm.phone}>
                {saving ? 'Saving...' : 'Add Employee'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Employee Detail Modal */}
      {selectedEmployee && (
        <EmployeeDetail
          employee={selectedEmployee}
          workOrders={workOrders}
          onClose={() => setSelectedEmployee(null)}
          onUpdate={async () => { await loadData(); }}
        />
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════
   EMPLOYEE DETAIL MODAL
   ══════════════════════════════════════════════ */
function EmployeeDetail({ employee, workOrders, onClose, onUpdate }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    name: employee.name || '',
    phone: employee.phone || '',
    email: employee.email || '',
    role: employee.role || 'basic',
    hourlyRate: employee.hourlyRate || '',
    address: employee.address || '',
    emergencyContact: employee.emergencyContact || '',
    emergencyPhone: employee.emergencyPhone || '',
    startDate: employee.startDate || '',
  });
  const [saving, setSaving] = useState(false);

  const empJobs = workOrders.filter(wo => wo.assignedTo === employee.name);
  const activeJobs = empJobs.filter(j => j.status !== 'Completed' && j.status !== 'Done');
  const completedJobs = empJobs.filter(j => j.status === 'Completed' || j.status === 'Done');

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateDoc(doc(db, 'employees', employee.id), form);
      await onUpdate();
      setEditing(false);
    } catch (err) {
      dialog.alert('Error: ' + err.message);
    }
    setSaving(false);
  };

  const handleDeactivate = async () => {
    if (!(await dialog.confirm(`Deactivate ${employee.name}? They will lose portal access.`))) return;
    try {
      await updateDoc(doc(db, 'employees', employee.id), {
        status: 'terminated',
        endDate: new Date().toISOString().split('T')[0],
      });
      await onUpdate();
      onClose();
    } catch (err) {
      dialog.alert('Error: ' + err.message);
    }
  };

  return (
    <div className="av2-modal-overlay" onClick={onClose}>
      <div className="av2-modal av2-modal-large" onClick={e => e.stopPropagation()}>
        <div className="av2-client-modal-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, flex: 1, minWidth: 0 }}>
            <div className="av2-client-avatar av2-client-avatar-lg">{(employee.name || '?').charAt(0).toUpperCase()}</div>
            <div>
              <h2>{employee.name}</h2>
              <div className="av2-client-modal-sub">
                <span className={`av2-plan-badge ${employee.role === 'admin' ? 'av2-plan-total' : 'av2-plan-starter'}`}>
                  {employee.role === 'admin' ? 'Admin' : 'Technician'}
                </span>
                {' · '}${employee.hourlyRate || '—'}/hr
              </div>
            </div>
          </div>
          <button className="av2-modal-close" onClick={onClose}>{Icons.close}</button>
        </div>

        {/* Stats strip */}
        <div className="av2-client-stats-strip">
          <div className="av2-client-stat-strip">
            <div className="av2-client-stat-strip-value">{empJobs.length}</div>
            <div className="av2-client-stat-strip-label">Total Jobs</div>
          </div>
          <div className="av2-client-stat-strip">
            <div className="av2-client-stat-strip-value">{activeJobs.length}</div>
            <div className="av2-client-stat-strip-label">Active</div>
          </div>
          <div className="av2-client-stat-strip">
            <div className="av2-client-stat-strip-value">{completedJobs.length}</div>
            <div className="av2-client-stat-strip-label">Completed</div>
          </div>
          <div className="av2-client-stat-strip">
            <div className="av2-client-stat-strip-value">{employee.onboarded ? '✓' : '—'}</div>
            <div className="av2-client-stat-strip-label">Onboarded</div>
          </div>
        </div>

        <div className="av2-modal-body">
          {!editing ? (
            <>
              <div className="av2-detail-grid">
                <div className="av2-detail-item">
                  <div className="av2-detail-label">Phone</div>
                  <div className="av2-detail-value">
                    {employee.phone ? <a href={`tel:${employee.phone}`}>{employee.phone}</a> : '—'}
                  </div>
                </div>
                <div className="av2-detail-item">
                  <div className="av2-detail-label">Email</div>
                  <div className="av2-detail-value">{employee.email || '—'}</div>
                </div>
                <div className="av2-detail-item">
                  <div className="av2-detail-label">Start Date</div>
                  <div className="av2-detail-value">{employee.startDate || '—'}</div>
                </div>
                <div className="av2-detail-item">
                  <div className="av2-detail-label">Address</div>
                  <div className="av2-detail-value">{employee.address || '—'}</div>
                </div>
                <div className="av2-detail-item">
                  <div className="av2-detail-label">Emergency Contact</div>
                  <div className="av2-detail-value">{employee.emergencyContact || '—'}</div>
                </div>
                <div className="av2-detail-item">
                  <div className="av2-detail-label">Emergency Phone</div>
                  <div className="av2-detail-value">
                    {employee.emergencyPhone ? <a href={`tel:${employee.emergencyPhone}`}>{employee.emergencyPhone}</a> : '—'}
                  </div>
                </div>
              </div>

              {/* Recent Jobs */}
              {empJobs.length > 0 && (
                <div style={{ marginTop: 20 }}>
                  <h4 style={{ fontSize: 11, fontWeight: 700, color: 'var(--av2-text-muted)', textTransform: 'uppercase', letterSpacing: 1, margin: '0 0 12px 0' }}>Recent Jobs</h4>
                  <div className="av2-table-wrap">
                    <table className="av2-table">
                      <thead><tr><th>Job</th><th>Client</th><th>Status</th></tr></thead>
                      <tbody>
                        {empJobs.slice(0, 10).map(job => (
                          <tr key={job.id}>
                            <td style={{ fontWeight: 600 }}>{job.title}</td>
                            <td>{job.clientName || job.contactName || '—'}</td>
                            <td><span className={`av2-badge av2-badge-${(job.status || '').toLowerCase().replace(/ /g, '-')}`}>{job.status}</span></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          ) : (
            <>
              <div className="av2-form-row">
                <div className="av2-form-field">
                  <label>Name</label>
                  <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
                </div>
                <div className="av2-form-field">
                  <label>Phone</label>
                  <input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} />
                </div>
              </div>
              <div className="av2-form-field">
                <label>Email</label>
                <input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
              </div>
              <div className="av2-form-row">
                <div className="av2-form-field">
                  <label>Role</label>
                  <select value={form.role} onChange={e => setForm({ ...form, role: e.target.value })}>
                    <option value="basic">Technician</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
                <div className="av2-form-field">
                  <label>Hourly Rate ($)</label>
                  <input type="number" value={form.hourlyRate} onChange={e => setForm({ ...form, hourlyRate: e.target.value })} />
                </div>
              </div>
              <div className="av2-form-field">
                <label>Address</label>
                <input value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} />
              </div>
              <div className="av2-form-row">
                <div className="av2-form-field">
                  <label>Emergency Contact</label>
                  <input value={form.emergencyContact} onChange={e => setForm({ ...form, emergencyContact: e.target.value })} />
                </div>
                <div className="av2-form-field">
                  <label>Emergency Phone</label>
                  <input value={form.emergencyPhone} onChange={e => setForm({ ...form, emergencyPhone: e.target.value })} />
                </div>
              </div>
              <div className="av2-form-field">
                <label>Start Date</label>
                <input type="date" value={form.startDate} onChange={e => setForm({ ...form, startDate: e.target.value })} />
              </div>
            </>
          )}
        </div>

        <div className="av2-modal-footer av2-modal-footer-split">
          <div>
            {!editing && (
              <button className="av2-btn-danger" onClick={handleDeactivate}>
                {Icons.trash} Deactivate
              </button>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {!editing ? (
              <button className="av2-btn-primary" onClick={() => setEditing(true)}>Edit</button>
            ) : (
              <>
                <button className="av2-btn-secondary" onClick={() => { setEditing(false); setForm({ name: employee.name || '', phone: employee.phone || '', email: employee.email || '', role: employee.role || 'basic', hourlyRate: employee.hourlyRate || '', address: employee.address || '', emergencyContact: employee.emergencyContact || '', emergencyPhone: employee.emergencyPhone || '', startDate: employee.startDate || '' }); }}>Cancel</button>
                <button className="av2-btn-primary" onClick={handleSave} disabled={saving}>
                  {saving ? 'Saving...' : 'Save'}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════
   PAYROLL PAGE
   ══════════════════════════════════════════════ */
function PayrollPage({ employees, timePunches, billingEntries }) {
  const [periodType, setPeriodType] = useState('current-month');
  const activeEmps = employees.filter(e => e.status !== 'terminated');

  // Calculate period dates
  const getPeriodRange = () => {
    const now = new Date();
    switch (periodType) {
      case 'current-month': {
        const start = new Date(now.getFullYear(), now.getMonth(), 1);
        const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        return { start, end, label: start.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }) };
      }
      case 'last-month': {
        const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const end = new Date(now.getFullYear(), now.getMonth(), 0);
        return { start, end, label: start.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }) };
      }
      case 'current-week': {
        const start = new Date(now);
        start.setDate(start.getDate() - start.getDay());
        start.setHours(0, 0, 0, 0);
        const end = new Date(start);
        end.setDate(end.getDate() + 6);
        end.setHours(23, 59, 59, 999);
        return { start, end, label: `Week of ${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}` };
      }
      case 'last-week': {
        const start = new Date(now);
        start.setDate(start.getDate() - start.getDay() - 7);
        start.setHours(0, 0, 0, 0);
        const end = new Date(start);
        end.setDate(end.getDate() + 6);
        end.setHours(23, 59, 59, 999);
        return { start, end, label: `Week of ${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}` };
      }
      default: {
        const start = new Date(now.getFullYear(), now.getMonth(), 1);
        return { start, end: now, label: 'Current' };
      }
    }
  };

  const { start: periodStart, end: periodEnd, label: periodLabel } = getPeriodRange();

  const getClockHours = (empId) => {
    return timePunches.filter(p => {
      if (p.employeeId !== empId || p.type !== 'out' || !p.hoursWorked) return false;
      const ts = p.timestamp?.seconds ? new Date(p.timestamp.seconds * 1000) : null;
      return ts && ts >= periodStart && ts <= periodEnd;
    }).reduce((sum, p) => sum + p.hoursWorked, 0);
  };

  const getJobHours = (empName) => {
    return billingEntries.filter(e => {
      const ts = e.createdAt?.seconds ? new Date(e.createdAt.seconds * 1000) : null;
      return e.techName === empName && ts && ts >= periodStart && ts <= periodEnd;
    }).reduce((sum, e) => sum + (e.hoursWorked || 0), 0);
  };

  const empData = activeEmps.map(emp => {
    const clockHrs = getClockHours(emp.id);
    const jobHrs = getJobHours(emp.name);
    const hrs = clockHrs || jobHrs;
    const rate = Number(emp.hourlyRate) || 0;
    const gross = hrs * rate;
    return { ...emp, clockHrs, jobHrs, hrs, rate, gross };
  });

  const totalHours = empData.reduce((sum, e) => sum + e.hrs, 0);
  const totalGross = empData.reduce((sum, e) => sum + e.gross, 0);

  const exportCSV = () => {
    const rows = [
      ['Name', 'Role', 'Hourly Rate', 'Clock Hours', 'Job Hours', 'Total Hours', 'Gross Pay'],
      ...empData.map(e => [
        e.name,
        e.role === 'admin' ? 'Admin' : 'Technician',
        e.rate,
        e.clockHrs.toFixed(2),
        e.jobHrs.toFixed(2),
        e.hrs.toFixed(2),
        e.gross.toFixed(2),
      ]),
    ];
    const csv = rows.map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `payroll-${periodLabel.replace(/[^a-zA-Z0-9]/g, '-')}.csv`;
    a.click();
  };

  return (
    <div className="av2-page">
      <div className="av2-page-header">
        <h1>Payroll</h1>
        <div style={{ display: 'flex', gap: 12 }}>
          <select value={periodType} onChange={e => setPeriodType(e.target.value)} className="av2-select">
            <option value="current-month">Current Month</option>
            <option value="last-month">Last Month</option>
            <option value="current-week">Current Week</option>
            <option value="last-week">Last Week</option>
          </select>
          <button className="av2-btn-primary" onClick={exportCSV}>
            ⬇ Export CSV
          </button>
        </div>
      </div>

      {/* Period info banner */}
      <div className="av2-payroll-banner">
        <div>
          <div className="av2-payroll-period-label">Pay Period</div>
          <div className="av2-payroll-period-value">{periodLabel}</div>
        </div>
        <div className="av2-payroll-totals">
          <div>
            <div className="av2-payroll-period-label">Total Hours</div>
            <div className="av2-payroll-period-value">{totalHours.toFixed(1)}</div>
          </div>
          <div>
            <div className="av2-payroll-period-label">Total Gross</div>
            <div className="av2-payroll-period-value" style={{ color: 'var(--av2-green)' }}>${totalGross.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
          </div>
        </div>
      </div>

      {/* Payroll Table */}
      <div className="av2-section" style={{ padding: 0 }}>
        <div className="av2-table-wrap">
          <table className="av2-table">
            <thead>
              <tr>
                <th>Employee</th>
                <th>Role</th>
                <th>Rate</th>
                <th>Clock Hours</th>
                <th>Job Hours</th>
                <th>Total Hours</th>
                <th>Gross Pay</th>
              </tr>
            </thead>
            <tbody>
              {empData.length === 0 ? (
                <tr><td colSpan={7} style={{ textAlign: 'center', padding: 40, color: 'var(--av2-text-muted)' }}>No employees found.</td></tr>
              ) : empData.map(emp => (
                <tr key={emp.id}>
                  <td style={{ fontWeight: 700 }}>{emp.name}</td>
                  <td><span className={`av2-plan-badge ${emp.role === 'admin' ? 'av2-plan-total' : 'av2-plan-starter'}`}>{emp.role === 'admin' ? 'Admin' : 'Tech'}</span></td>
                  <td>${emp.rate}/hr</td>
                  <td>{emp.clockHrs.toFixed(1)}h</td>
                  <td>{emp.jobHrs.toFixed(1)}h</td>
                  <td style={{ fontWeight: 600 }}>{emp.hrs.toFixed(1)}h</td>
                  <td style={{ fontWeight: 700, color: 'var(--av2-green)' }}>${emp.gross.toFixed(2)}</td>
                </tr>
              ))}
              {empData.length > 0 && (
                <tr style={{ background: 'var(--av2-bg)', fontWeight: 700 }}>
                  <td colSpan={5} style={{ textAlign: 'right' }}>Total</td>
                  <td style={{ fontWeight: 700 }}>{totalHours.toFixed(1)}h</td>
                  <td style={{ color: 'var(--av2-green)', fontWeight: 700 }}>${totalGross.toFixed(2)}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="av2-payroll-note">
        <strong>Note:</strong> This is a summary for your payroll provider. Use these numbers to enter hours in Gusto, ADP, or your preferred payroll system. We track hours only — actual payroll processing, tax withholding, and direct deposit are handled by your payroll provider.
      </div>
    </div>
  );
}


/* ══════════════════════════════════════════════
   REPORTS PAGE
   ══════════════════════════════════════════════ */
function ReportsPage({ contacts, workOrders, employees, billingEntries, invoices = [] }) {
  const [period, setPeriod] = useState('month'); // month | quarter | year | all

  const now = new Date();
  const periodStart = (() => {
    switch (period) {
      case 'month': return new Date(now.getFullYear(), now.getMonth(), 1);
      case 'quarter': return new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 1);
      case 'year': return new Date(now.getFullYear(), 0, 1);
      default: return new Date(2000, 0, 1);
    }
  })();
  const periodLabel = (() => {
    switch (period) {
      case 'month': return now.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      case 'quarter': return `Q${Math.floor(now.getMonth() / 3) + 1} ${now.getFullYear()}`;
      case 'year': return `${now.getFullYear()}`;
      default: return 'All Time';
    }
  })();

  // Filter data to period
  const periodJobs = workOrders.filter(w => {
    const ts = w.createdAt?.seconds ? new Date(w.createdAt.seconds * 1000) : null;
    return ts && ts >= periodStart;
  });
  const periodBilling = billingEntries.filter(e => {
    const ts = e.createdAt?.seconds ? new Date(e.createdAt.seconds * 1000) : null;
    return ts && ts >= periodStart;
  });
  const periodLeads = contacts.filter(c => {
    if (c.stage !== 'lead' && c.stage !== 'client') return false;
    const ts = c.createdAt?.seconds ? new Date(c.createdAt.seconds * 1000) : null;
    return ts && ts >= periodStart;
  });

  // Revenue
  const revenue = periodBilling.reduce((sum, e) => sum + ((e.hoursWorked || 0) * 120), 0);
  const totalHours = periodBilling.reduce((sum, e) => sum + (e.hoursWorked || 0), 0);
  const completedJobs = periodJobs.filter(j => j.status === 'Completed' || j.status === 'Done');
  const avgTicket = completedJobs.length > 0 ? revenue / completedJobs.length : 0;

  // MRR (all active clients)
  const activeClients = contacts.filter(c => c.stage === 'client');
  const avgJobValue = periodJobs.length > 0
    ? periodJobs.reduce((sum, j) => sum + ((j.estimate?.hours || j.hoursWorked || 0) * 120 + (j.estimate?.materials || 0)), 0) / periodJobs.length
    : 0;

  // Conversion rate
  const periodNewLeads = periodLeads.filter(c => c.stage === 'lead').length;
  const periodNewClients = periodLeads.filter(c => c.stage === 'client').length;
  const conversionRate = (periodNewLeads + periodNewClients) > 0
    ? Math.round((periodNewClients / (periodNewLeads + periodNewClients)) * 100)
    : 0;

  // Lead sources
  const leadSources = {};
  periodLeads.forEach(c => {
    const src = c.source || 'direct';
    leadSources[src] = (leadSources[src] || 0) + 1;
  });
  const topSources = Object.entries(leadSources).sort((a, b) => b[1] - a[1]);

  // Service type distribution
  const serviceTypes = {};
  periodJobs.forEach(j => {
    const svc = j.serviceType || 'Other';
    serviceTypes[svc] = (serviceTypes[svc] || 0) + 1;
  });
  const topServices = Object.entries(serviceTypes).sort((a, b) => b[1] - a[1]);

  // Tech performance
  const techStats = employees.filter(e => e.status !== 'terminated').map(emp => {
    const empBilling = periodBilling.filter(e => e.techName === emp.name);
    const empJobs = periodJobs.filter(j => j.assignedTo === emp.name);
    const empCompleted = empJobs.filter(j => j.status === 'Completed' || j.status === 'Done');
    const hours = empBilling.reduce((sum, e) => sum + (e.hoursWorked || 0), 0);
    const billedRevenue = hours * 120;
    const rate = Number(emp.hourlyRate) || 0;
    const cost = hours * rate;
    const profit = billedRevenue - cost;
    const margin = billedRevenue > 0 ? (profit / billedRevenue) * 100 : 0;
    return {
      name: emp.name,
      role: emp.role,
      hours,
      jobs: empJobs.length,
      completed: empCompleted.length,
      billedRevenue,
      cost,
      profit,
      margin,
    };
  }).filter(t => t.jobs > 0 || t.hours > 0).sort((a, b) => b.billedRevenue - a.billedRevenue);

  // ── Accounts Receivable (A/R) — unpaid invoices, all-time ──
  const unpaidInvoices = (invoices || []).filter(i => i.status !== 'paid');
  const totalOutstanding = unpaidInvoices.reduce((sum, i) => sum + (i.total || 0), 0);
  const oldestUnpaid = unpaidInvoices.reduce((oldest, inv) => {
    const ts = inv.createdAt?.seconds || 0;
    if (!ts) return oldest;
    if (!oldest || ts < oldest.ts) return { ts, invoiceNumber: inv.invoiceNumber, total: inv.total || 0, name: inv.clientName };
    return oldest;
  }, null);
  const oldestAgeDays = oldestUnpaid
    ? Math.floor((Date.now() / 1000 - oldestUnpaid.ts) / 86400)
    : 0;

  // Aging buckets (unpaid invoices by age)
  const agingBuckets = { current: 0, '30d': 0, '60d': 0, '90d+': 0 };
  unpaidInvoices.forEach(inv => {
    const ts = inv.createdAt?.seconds;
    if (!ts) return;
    const ageDays = (Date.now() / 1000 - ts) / 86400;
    const amt = inv.total || 0;
    if (ageDays <= 30) agingBuckets.current += amt;
    else if (ageDays <= 60) agingBuckets['30d'] += amt;
    else if (ageDays <= 90) agingBuckets['60d'] += amt;
    else agingBuckets['90d+'] += amt;
  });

  // ── Monthly revenue trend (last 12 months, all sources) ──
  const monthlyRevenue = [];
  for (let i = 11; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const monthStart = new Date(d.getFullYear(), d.getMonth(), 1);
    const monthEnd = new Date(d.getFullYear(), d.getMonth() + 1, 1);
    const monthRev = billingEntries.reduce((sum, e) => {
      const ts = e.createdAt?.seconds ? new Date(e.createdAt.seconds * 1000) : null;
      if (!ts || ts < monthStart || ts >= monthEnd) return sum;
      return sum + ((e.hoursWorked || 0) * 120);
    }, 0);
    monthlyRevenue.push({
      label: d.toLocaleDateString('en-US', { month: 'short' }),
      year: d.getFullYear(),
      amount: monthRev,
      isCurrent: i === 0,
    });
  }
  const maxMonthRev = Math.max(1, ...monthlyRevenue.map(m => m.amount));

  // Job status breakdown
  const statusCounts = {};
  periodJobs.forEach(j => {
    statusCounts[j.status || 'Unknown'] = (statusCounts[j.status || 'Unknown'] || 0) + 1;
  });

  // Total LTV across all clients
  const totalLTV = activeClients.reduce((sum, c) => {
    const clientBilling = billingEntries.filter(b => b.clientEmail === c.email || b.clientId === c.id);
    return sum + clientBilling.reduce((s, e) => s + ((e.hoursWorked || 0) * 80), 0);
  }, 0);

  const maxSource = Math.max(1, ...topSources.map(s => s[1]));
  const maxService = Math.max(1, ...topServices.map(s => s[1]));

  return (
    <div className="av2-page">
      <div className="av2-page-header">
        <h1>Reports</h1>
        <select value={period} onChange={e => setPeriod(e.target.value)} className="av2-select">
          <option value="month">This Month</option>
          <option value="quarter">This Quarter</option>
          <option value="year">This Year</option>
          <option value="all">All Time</option>
        </select>
      </div>

      {/* Period banner */}
      <div className="av2-report-banner">
        <div>
          <div className="av2-payroll-period-label">Reporting Period</div>
          <div className="av2-payroll-period-value">{periodLabel}</div>
        </div>
        <div className="av2-payroll-totals">
          <div>
            <div className="av2-payroll-period-label">Revenue</div>
            <div className="av2-payroll-period-value" style={{ color: 'var(--av2-green)' }}>${revenue.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</div>
          </div>
          <div>
            <div className="av2-payroll-period-label">Avg Job</div>
            <div className="av2-payroll-period-value">${Math.round(avgJobValue).toLocaleString()}</div>
          </div>
        </div>
      </div>

      {/* Primary KPIs */}
      <div className="av2-kpi-grid">
        <div className="av2-kpi-card">
          <div className="av2-kpi-label">Jobs Completed</div>
          <div className="av2-kpi-value">{completedJobs.length}</div>
          <div className="av2-kpi-sub">{totalHours.toFixed(0)} hours worked</div>
        </div>
        <div className="av2-kpi-card">
          <div className="av2-kpi-label">Avg Ticket</div>
          <div className="av2-kpi-value">${avgTicket.toFixed(0)}</div>
          <div className="av2-kpi-sub">Per completed job</div>
        </div>
        <div className="av2-kpi-card">
          <div className="av2-kpi-label">New Leads</div>
          <div className="av2-kpi-value">{periodNewLeads + periodNewClients}</div>
          <div className="av2-kpi-sub">{conversionRate}% converted</div>
        </div>
        <div className="av2-kpi-card">
          <div className="av2-kpi-label">Total Client LTV</div>
          <div className="av2-kpi-value">${totalLTV.toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
          <div className="av2-kpi-sub">{activeClients.length} active clients</div>
        </div>
      </div>

      {/* Two column charts */}
      <div className="av2-two-col" style={{ marginBottom: 20 }}>
        {/* Lead Sources */}
        <div className="av2-section">
          <div className="av2-section-header">
            <h3>Lead Sources</h3>
            <span className="av2-section-count">{(periodNewLeads + periodNewClients)}</span>
          </div>
          {topSources.length === 0 ? (
            <div className="av2-empty-state"><p>No leads in this period.</p></div>
          ) : (
            <div className="av2-bar-chart">
              {topSources.map(([source, count]) => (
                <div key={source} className="av2-bar-row">
                  <div className="av2-bar-label">{source}</div>
                  <div className="av2-bar-track">
                    <div className="av2-bar-fill" style={{ width: `${(count / maxSource) * 100}%`, background: 'var(--av2-green)' }} />
                  </div>
                  <div className="av2-bar-value">{count}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Service Mix */}
        <div className="av2-section">
          <div className="av2-section-header">
            <h3>Service Mix</h3>
            <span className="av2-section-count">{periodJobs.length}</span>
          </div>
          {topServices.length === 0 ? (
            <div className="av2-empty-state"><p>No jobs in this period.</p></div>
          ) : (
            <div className="av2-bar-chart">
              {topServices.slice(0, 8).map(([service, count]) => (
                <div key={service} className="av2-bar-row">
                  <div className="av2-bar-label">{service}</div>
                  <div className="av2-bar-track">
                    <div className="av2-bar-fill" style={{ width: `${(count / maxService) * 100}%`, background: '#1E6091' }} />
                  </div>
                  <div className="av2-bar-value">{count}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Accounts Receivable */}
      <div className="av2-section" style={{ padding: 0, marginBottom: 20 }}>
        <div className="av2-section-header" style={{ padding: '16px 20px' }}>
          <h3>Accounts Receivable</h3>
          <span className="av2-section-count">{unpaidInvoices.length} unpaid</span>
        </div>
        <div style={{ padding: 20 }}>
          <div className="av2-stats-row" style={{ gridTemplateColumns: 'repeat(4, 1fr)', marginBottom: 16 }}>
            <div className="av2-stat">
              <div className="av2-stat-label">Total Outstanding</div>
              <div className="av2-stat-value" style={{ color: totalOutstanding > 0 ? 'var(--av2-danger)' : 'var(--av2-green)' }}>
                ${totalOutstanding.toLocaleString(undefined, { maximumFractionDigits: 0 })}
              </div>
            </div>
            <div className="av2-stat">
              <div className="av2-stat-label">Current (0-30d)</div>
              <div className="av2-stat-value">${agingBuckets.current.toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
            </div>
            <div className="av2-stat">
              <div className="av2-stat-label">31-60 days</div>
              <div className="av2-stat-value" style={{ color: agingBuckets['30d'] > 0 ? '#D4880F' : undefined }}>
                ${agingBuckets['30d'].toLocaleString(undefined, { maximumFractionDigits: 0 })}
              </div>
            </div>
            <div className="av2-stat">
              <div className="av2-stat-label">60+ days</div>
              <div className="av2-stat-value" style={{ color: (agingBuckets['60d'] + agingBuckets['90d+']) > 0 ? 'var(--av2-danger)' : undefined }}>
                ${(agingBuckets['60d'] + agingBuckets['90d+']).toLocaleString(undefined, { maximumFractionDigits: 0 })}
              </div>
            </div>
          </div>

          {oldestUnpaid && oldestAgeDays > 30 && (
            <div style={{ padding: 12, background: 'var(--av2-warn-bg, #FEF7E0)', border: '1px solid var(--av2-warn-border, #F5DFA0)', borderRadius: 6, fontSize: 13, color: '#7A5A18' }}>
              <strong>⚠️ Oldest unpaid:</strong> {oldestUnpaid.invoiceNumber} — {oldestUnpaid.name} — ${oldestUnpaid.total.toFixed(2)} — <strong>{oldestAgeDays} days</strong> overdue
            </div>
          )}
          {unpaidInvoices.length === 0 && (
            <div style={{ padding: 16, textAlign: 'center', color: 'var(--av2-text-muted)', fontSize: 13 }}>
              All invoices are paid. 🎉
            </div>
          )}
        </div>
      </div>

      {/* Revenue Trend — last 12 months */}
      <div className="av2-section" style={{ padding: 0, marginBottom: 20 }}>
        <div className="av2-section-header" style={{ padding: '16px 20px' }}>
          <h3>Revenue Trend</h3>
          <span className="av2-section-count">Last 12 months</span>
        </div>
        <div style={{ padding: 20 }}>
          {monthlyRevenue.every(m => m.amount === 0) ? (
            <div className="av2-empty-state"><p>No revenue logged yet. As you complete jobs and log billing entries, the trend will populate here.</p></div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, height: 180, marginBottom: 8 }}>
              {monthlyRevenue.map((m, i) => {
                const heightPct = maxMonthRev > 0 ? (m.amount / maxMonthRev) * 100 : 0;
                return (
                  <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, minWidth: 0 }}>
                    <div style={{ fontSize: 10, color: 'var(--av2-text-muted)', fontWeight: 600 }}>
                      {m.amount > 0 ? `$${m.amount >= 1000 ? `${(m.amount / 1000).toFixed(1)}k` : m.amount.toFixed(0)}` : ''}
                    </div>
                    <div style={{
                      width: '100%',
                      height: `${Math.max(heightPct, 2)}%`,
                      background: m.isCurrent ? 'var(--av2-green)' : 'var(--av2-green-light, #B5D9C5)',
                      borderRadius: '4px 4px 0 0',
                      transition: 'height .3s',
                      minHeight: 2,
                    }} />
                    <div style={{ fontSize: 10, color: 'var(--av2-text-muted)', fontWeight: m.isCurrent ? 700 : 400 }}>
                      {m.label}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Tech Performance */}
      <div className="av2-section" style={{ padding: 0, marginBottom: 20 }}>
        <div className="av2-section-header" style={{ padding: '16px 20px' }}>
          <h3>Tech Performance</h3>
          <span className="av2-section-count">{techStats.length}</span>
        </div>
        {techStats.length === 0 ? (
          <div className="av2-empty-state"><p>No tech activity in this period.</p></div>
        ) : (
          <div className="av2-table-wrap">
            <table className="av2-table">
              <thead>
                <tr>
                  <th>Tech</th>
                  <th>Jobs</th>
                  <th>Completed</th>
                  <th>Hours</th>
                  <th>Billed</th>
                  <th>Labor Cost</th>
                  <th>Profit</th>
                  <th>Margin</th>
                </tr>
              </thead>
              <tbody>
                {techStats.map(t => (
                  <tr key={t.name}>
                    <td style={{ fontWeight: 700 }}>{t.name}</td>
                    <td>{t.jobs}</td>
                    <td>{t.completed}</td>
                    <td>{t.hours.toFixed(1)}h</td>
                    <td style={{ fontWeight: 600 }}>${t.billedRevenue.toFixed(0)}</td>
                    <td>${t.cost.toFixed(0)}</td>
                    <td style={{ fontWeight: 700, color: t.profit >= 0 ? 'var(--av2-green)' : 'var(--av2-danger)' }}>
                      ${t.profit.toFixed(0)}
                    </td>
                    <td style={{ fontWeight: 600, color: t.margin >= 50 ? 'var(--av2-green)' : t.margin >= 25 ? 'var(--av2-warn)' : 'var(--av2-danger)' }}>
                      {t.margin.toFixed(0)}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pipeline Overview */}
      <div className="av2-two-col">
        <div className="av2-section">
          <div className="av2-section-header">
            <h3>Pipeline Snapshot</h3>
          </div>
          <div className="av2-stats-row" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
            <div className="av2-stat">
              <div className="av2-stat-label">Active Leads</div>
              <div className="av2-stat-value">{contacts.filter(c => c.stage === 'lead').length}</div>
            </div>
            <div className="av2-stat">
              <div className="av2-stat-label">Active Properties</div>
              <div className="av2-stat-value">{activeClients.length}</div>
            </div>
            <div className="av2-stat">
              <div className="av2-stat-label">Repeat (3+ jobs)</div>
              <div className="av2-stat-value">{activeClients.filter(c => (c.jobCount || 0) >= 3).length}</div>
            </div>
          </div>
        </div>

        {/* Job Status */}
        <div className="av2-section">
          <div className="av2-section-header">
            <h3>Jobs by Status</h3>
          </div>
          {Object.keys(statusCounts).length === 0 ? (
            <div className="av2-empty-state"><p>No jobs yet.</p></div>
          ) : (
            <div className="av2-status-chips">
              {Object.entries(statusCounts).sort((a, b) => b[1] - a[1]).map(([status, count]) => (
                <div key={status} className="av2-status-chip">
                  <span className={`av2-badge av2-badge-${(status || '').toLowerCase().replace(/ /g, '-')}`}>{status}</span>
                  <span className="av2-status-chip-count">{count}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="av2-payroll-note" style={{ marginTop: 24 }}>
        <strong>Reporting basis:</strong> Revenue is calculated from logged billing entries at $120/hr rate. Tech margins use logged labor cost (hours × hourly rate) against billed revenue.
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════
   EMAIL TEMPLATES MODULE
   Replaces the old SMS-based CommsTab.
   8 transactional email templates for property lifecycle.
   Copy-to-clipboard only — no send functionality.
   ══════════════════════════════════════════════ */

const EMAIL_TEMPLATES = [
  {
    id: 'intake-confirmation',
    name: 'Intake Confirmation',
    description: 'Sent right after intaking a new property — confirms we have their info and outlines next steps.',
    when: 'After property intake',
    subject: 'Thanks for reaching out — Reliable Handyman',
    body: `Hi {firstName},

Thanks for reaching out to Reliable Handyman — we've got your information on file at {address} (Property {propertyId}).

Here's what to expect:
- We'll review your request and reach back out within a few hours during business hours to talk through the work.
- For larger projects we'll set up a quick site visit (free, no obligation) so we can give you an accurate estimate.
- For smaller jobs we may be able to quote and schedule directly from your description and photos.

If anything is urgent or you'd rather just talk it through, give us a call at (516) 386-9923.

Talk soon,
{senderName}
Reliable Handyman`,
  },
  {
    id: 'estimate-sent',
    name: 'Estimate Sent',
    description: 'Sent with a written quote. The {approvalLink} variable inserts the one-click approval URL — make sure you\'ve clicked "Generate Approval Link" on the estimate first.',
    when: 'When sending an estimate',
    subject: 'Your estimate from Reliable Handyman — {jobTitle}',
    body: `Hi {firstName},

Here's the estimate for the work we discussed at {address}:

Project: {jobTitle}
Total: {totalAmount}

Review and approve here (one click, no login needed):
{approvalLink}

A few notes:
- This estimate is valid for 30 days.
- Materials are billed at cost — you'll see itemized receipts after the work is done.
- Labor is billed at $120/hr in Long Island, $150/hr in NYC. 1-hour minimum, then in whole-hour blocks. Discounts available for projects of 8+ hours. No trip charges.
- We'll schedule the work once you approve — usually within 3-7 days depending on the time of year.

Let me know if you have any questions or want to adjust anything in the scope.

Thanks,
{senderName}
Reliable Handyman
(516) 386-9923`,
  },
  {
    id: 'estimate-followup',
    name: 'Estimate Follow-Up',
    description: 'Friendly nudge 3-5 days after an unresponded estimate.',
    when: 'Estimate sent, no response after a few days',
    subject: 'Following up on your estimate — {jobTitle}',
    body: `Hi {firstName},

Just circling back on the estimate we sent for {jobTitle} at {address}. Wanted to make sure it landed in your inbox and check if you had any questions.

If you'd like to move forward, just reply and we'll book a slot. If you'd like to discuss any part of the scope or pricing, happy to jump on a quick call — (516) 386-9923.

If now isn't the right time, no pressure — just let me know and I'll close out the file.

Thanks,
{senderName}
Reliable Handyman`,
  },
  {
    id: 'appointment-confirmation',
    name: 'Appointment Confirmation',
    description: 'Sent after a job is scheduled. Confirms tech, date, time, scope.',
    when: 'After scheduling a confirmed job',
    subject: 'Appointment confirmed — {jobTitle} on {scheduledDate}',
    body: `Hi {firstName},

Confirming we've got you scheduled:

Project: {jobTitle}
Date: {scheduledDate}
Time: {scheduledTime}
Address: {address}

{techName} will be the tech on this one. They'll text you when they're on the way the day of.

If you need to reschedule for any reason, please give us at least 24 hours notice if possible — just call or text (516) 386-9923.

See you {scheduledDate},
{senderName}
Reliable Handyman`,
  },
  {
    id: 'day-of-reminder',
    name: 'Day-Of Reminder',
    description: 'Sent the morning of the appointment with arrival window.',
    when: 'Morning of the scheduled appointment',
    subject: 'Reminder: we\'re coming today — {jobTitle}',
    body: `Hi {firstName},

Just a quick reminder that {techName} will be at {address} today around {scheduledTime} for {jobTitle}.

They'll text you when they're on their way.

A couple of things that help us start fast:
- Please make sure the work area is accessible and any pets are situated.
- If you need to point out the main shutoff or anything specific, please be available for the first few minutes.

See you soon,
{senderName}
Reliable Handyman
(516) 386-9923`,
  },
  {
    id: 'work-complete',
    name: 'Work Order Complete',
    description: 'Sent same-day after job completion. Recaps what was done.',
    when: 'After a job is marked complete',
    subject: 'All wrapped up — {jobTitle}',
    body: `Hi {firstName},

We wrapped up {jobTitle} at {address} today.

Summary of the work:
{jobTitle}

If anything isn't holding up the way it should, please let me know within the next few days and we'll come back to make it right — our labor is backed by a 1-year workmanship guarantee.

Your invoice for {totalAmount} is attached / will arrive shortly. Payment is due within 5 days.

Thanks for trusting us with your home,
{senderName}
Reliable Handyman
(516) 386-9923`,
  },
  {
    id: 'payment-confirmation',
    name: 'Payment Confirmation',
    description: 'Sent after payment is received. Closes out the job loop.',
    when: 'After payment is received',
    subject: 'Payment received — thank you',
    body: `Hi {firstName},

Just confirming we received your payment of {totalAmount} for {jobTitle}. Receipt below for your records.

We genuinely appreciate the business. If anything related to this job comes up over the next year, our workmanship guarantee has you covered — just give us a call.

And if a friend or neighbor on your block ever needs a hand around the house, we'd be grateful for the referral.

Thanks again,
{senderName}
Reliable Handyman
(516) 386-9923`,
  },
  {
    id: 'post-job-checkin',
    name: 'Post-Job Check-In',
    description: 'Sent 5-7 days after job completion. Reinforces the workmanship guarantee.',
    when: '5-7 days after job completion',
    subject: 'Quick check-in — how\'s everything holding up?',
    body: `Hi {firstName},

It's been about a week since we finished {jobTitle} at {address}. Wanted to check in and make sure everything is holding up the way you'd expect.

If anything is off — even a small thing — let us know and we'll come back and make it right. That's what the 1-year workmanship guarantee is for.

If everything is good, no need to reply. But if you have a second to leave us a quick Google review, it would genuinely help.

Thanks again,
{senderName}
Reliable Handyman
(516) 386-9923`,
  },
];

function CommsTab({ client, clientJobs, clientInvoices, currentUserName, onUpdate }) {
  const [selectedTemplate, setSelectedTemplate] = useState(EMAIL_TEMPLATES[0]);
  const [selectedJobId, setSelectedJobId] = useState('');
  const [selectedInvoiceId, setSelectedInvoiceId] = useState('');
  const [copyState, setCopyState] = useState({ subject: false, body: false });

  // Pick the most recent job by default
  useEffect(() => {
    if (!selectedJobId && (clientJobs || []).length > 0) {
      const sorted = [...clientJobs].sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
      setSelectedJobId(sorted[0].id);
    }
  }, [clientJobs, selectedJobId]);

  useEffect(() => {
    if (!selectedInvoiceId && (clientInvoices || []).length > 0) {
      const sorted = [...clientInvoices].sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
      setSelectedInvoiceId(sorted[0].id);
    }
  }, [clientInvoices, selectedInvoiceId]);

  const firstName = (client.name || 'there').split(' ')[0];
  const senderName = (currentUserName || 'Alex').split(' ')[0];
  const propertyId = client.propertyId || client.accountNumber || 'RH-????';

  const selectedJob = (clientJobs || []).find(j => j.id === selectedJobId);
  const selectedInvoice = (clientInvoices || []).find(i => i.id === selectedInvoiceId);

  // Build variable substitutions
  const formatDate = (ts) => {
    if (!ts) return '[date TBD]';
    let d;
    if (ts.seconds) {
      d = new Date(ts.seconds * 1000);
    } else if (typeof ts === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(ts)) {
      // Parse as local date to avoid UTC midnight shifting it back one day
      const [y, m, day] = ts.split('-').map(Number);
      d = new Date(y, m - 1, day);
    } else {
      d = new Date(ts);
    }
    return d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
  };
  const formatTime = (timeStr) => {
    if (!timeStr || timeStr === '[time]') return '[time TBD]';
    // Convert "14:00" → "2:00 PM"
    if (/^\d{2}:\d{2}$/.test(timeStr)) {
      const [h, m] = timeStr.split(':').map(Number);
      const period = h >= 12 ? 'PM' : 'AM';
      const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
      return `${h12}:${m.toString().padStart(2, '0')} ${period}`;
    }
    return timeStr;
  };
  const formatMoney = (n) => {
    if (n == null) return '[amount TBD]';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);
  };

  const fillTemplate = (text) => {
    const baseUrl = (typeof window !== 'undefined' && window.location?.origin) || '';
    const approvalLink = selectedJob?.approvalToken
      ? `${baseUrl}/estimate/${selectedJob.approvalToken}`
      : '[click "Generate Approval Link" on the estimate first]';
    return (text || '')
      .replace(/{firstName}/g, firstName)
      .replace(/{fullName}/g, client.name || '')
      .replace(/{senderName}/g, senderName)
      .replace(/{propertyId}/g, propertyId)
      .replace(/{address}/g, client.address || '[address]')
      .replace(/{jobTitle}/g, selectedJob?.title || '[job title]')
      .replace(/{scheduledDate}/g, formatDate(selectedJob?.scheduledAt || selectedJob?.scheduledDate))
      .replace(/{scheduledTime}/g, formatTime(selectedJob?.scheduledTime || '[time]'))
      .replace(/{techName}/g, selectedJob?.assignedTo || selectedJob?.preferredTech || client.preferredTech || 'our tech')
      .replace(/{totalAmount}/g, formatMoney(selectedInvoice?.total ?? selectedJob?.estimate?.total ?? null))
      .replace(/{approvalLink}/g, approvalLink);
  };

  const filledSubject = fillTemplate(selectedTemplate.subject);
  const filledBody = fillTemplate(selectedTemplate.body);

  const copyText = async (text, which) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopyState(prev => ({ ...prev, [which]: true }));
      setTimeout(() => setCopyState(prev => ({ ...prev, [which]: false })), 1800);
    } catch (err) {
      console.error('Copy failed', err);
    }
  };

  const copyBoth = () => {
    copyText(`Subject: ${filledSubject}\n\n${filledBody}`, 'body');
  };

  // Variables this template uses (for the missing-data warning)
  const usedVars = (selectedTemplate.subject + ' ' + selectedTemplate.body).match(/\{(\w+)\}/g) || [];
  const needsJob = usedVars.some(v => ['{jobTitle}', '{scheduledDate}', '{scheduledTime}', '{techName}'].includes(v));
  const needsInvoice = usedVars.some(v => ['{totalAmount}'].includes(v));

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'grid', gridTemplateColumns: '260px 1fr', gap: 20 }}>

        {/* ── Template list ── */}
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--av2-text-muted)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10 }}>
            Templates
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {EMAIL_TEMPLATES.map(tmpl => (
              <button
                key={tmpl.id}
                onClick={() => setSelectedTemplate(tmpl)}
                style={{
                  textAlign: 'left',
                  padding: '10px 12px',
                  background: selectedTemplate.id === tmpl.id ? 'var(--av2-green)' : 'var(--av2-bg)',
                  color: selectedTemplate.id === tmpl.id ? '#fff' : 'var(--av2-text)',
                  border: `1px solid ${selectedTemplate.id === tmpl.id ? 'var(--av2-green)' : 'var(--av2-border)'}`,
                  borderRadius: 6,
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'all .15s',
                }}
              >
                <div>{tmpl.name}</div>
                <div style={{
                  fontSize: 10,
                  fontWeight: 400,
                  color: selectedTemplate.id === tmpl.id ? 'rgba(255,255,255,0.75)' : 'var(--av2-text-muted)',
                  marginTop: 2,
                }}>
                  {tmpl.when}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* ── Template editor / preview ── */}
        <div>
          <div style={{ marginBottom: 14 }}>
            <h3 style={{ margin: 0, fontSize: 18 }}>{selectedTemplate.name}</h3>
            <div style={{ fontSize: 12, color: 'var(--av2-text-muted)', marginTop: 4 }}>
              {selectedTemplate.description}
            </div>
          </div>

          {/* Job / Invoice picker (only show what's needed) */}
          {(needsJob || needsInvoice) && (
            <div style={{ display: 'flex', gap: 10, marginBottom: 14, padding: 12, background: 'var(--av2-bg)', border: '1px solid var(--av2-border)', borderRadius: 6 }}>
              {needsJob && (
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--av2-text-muted)', textTransform: 'uppercase', letterSpacing: 0.6, display: 'block', marginBottom: 4 }}>
                    Pull from job:
                  </label>
                  <select
                    value={selectedJobId}
                    onChange={e => setSelectedJobId(e.target.value)}
                    style={{ width: '100%', padding: '6px 8px', fontSize: 12, border: '1px solid var(--av2-border)', borderRadius: 4 }}
                  >
                    <option value="">— Select a job —</option>
                    {(clientJobs || []).sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0)).map(j => (
                      <option key={j.id} value={j.id}>
                        {j.title || 'Untitled'} {j.status ? `(${j.status})` : ''}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              {needsInvoice && (
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--av2-text-muted)', textTransform: 'uppercase', letterSpacing: 0.6, display: 'block', marginBottom: 4 }}>
                    Pull from invoice:
                  </label>
                  <select
                    value={selectedInvoiceId}
                    onChange={e => setSelectedInvoiceId(e.target.value)}
                    style={{ width: '100%', padding: '6px 8px', fontSize: 12, border: '1px solid var(--av2-border)', borderRadius: 4 }}
                  >
                    <option value="">— Select an invoice —</option>
                    {(clientInvoices || []).sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0)).map(inv => (
                      <option key={inv.id} value={inv.id}>
                        {inv.invoiceNumber} {inv.total ? `· ${formatMoney(inv.total)}` : ''}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          )}

          {/* Subject */}
          <div style={{ marginBottom: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
              <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--av2-text-muted)', textTransform: 'uppercase', letterSpacing: 0.6 }}>
                Subject
              </label>
              <button
                onClick={() => copyText(filledSubject, 'subject')}
                style={{
                  background: copyState.subject ? 'var(--av2-green)' : 'transparent',
                  color: copyState.subject ? '#fff' : 'var(--av2-green)',
                  border: `1px solid var(--av2-green)`,
                  padding: '4px 10px',
                  borderRadius: 4,
                  fontSize: 11,
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'all .15s',
                }}
              >
                {copyState.subject ? '✓ Copied' : 'Copy subject'}
              </button>
            </div>
            <div style={{
              padding: '10px 12px',
              background: 'var(--av2-bg)',
              border: '1px solid var(--av2-border)',
              borderRadius: 6,
              fontFamily: 'var(--av2-font)',
              fontSize: 14,
              fontWeight: 600,
            }}>
              {filledSubject}
            </div>
          </div>

          {/* Body */}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
              <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--av2-text-muted)', textTransform: 'uppercase', letterSpacing: 0.6 }}>
                Body
              </label>
              <div style={{ display: 'flex', gap: 6 }}>
                <button
                  onClick={() => copyText(filledBody, 'body')}
                  style={{
                    background: copyState.body ? 'var(--av2-green)' : 'transparent',
                    color: copyState.body ? '#fff' : 'var(--av2-green)',
                    border: `1px solid var(--av2-green)`,
                    padding: '4px 10px',
                    borderRadius: 4,
                    fontSize: 11,
                    fontWeight: 600,
                    cursor: 'pointer',
                    transition: 'all .15s',
                  }}
                >
                  {copyState.body ? '✓ Copied' : 'Copy body'}
                </button>
                <button
                  onClick={copyBoth}
                  style={{
                    background: 'var(--av2-green)',
                    color: '#fff',
                    border: '1px solid var(--av2-green)',
                    padding: '4px 10px',
                    borderRadius: 4,
                    fontSize: 11,
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  Copy subject + body
                </button>
              </div>
            </div>
            <div style={{
              padding: '14px 16px',
              background: 'var(--av2-bg)',
              border: '1px solid var(--av2-border)',
              borderRadius: 6,
              fontFamily: 'var(--av2-font)',
              fontSize: 14,
              lineHeight: 1.6,
              whiteSpace: 'pre-wrap',
              minHeight: 200,
            }}>
              {filledBody}
            </div>
          </div>

          {/* Variable legend */}
          <div style={{ marginTop: 14, fontSize: 11, color: 'var(--av2-text-muted)' }}>
            <strong>Available variables:</strong> {'{firstName}'}, {'{fullName}'}, {'{senderName}'}, {'{propertyId}'}, {'{address}'}, {'{jobTitle}'}, {'{scheduledDate}'}, {'{scheduledTime}'}, {'{techName}'}, {'{totalAmount}'}, {'{approvalLink}'}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════
   CATALOG PAGE — Personal services cheat sheet
   Lets you build up a list of common jobs with typical
   hours/materials/notes so you can quote faster.
   Not published publicly. Admin-only.
   Stored in Firestore: `flatRateCatalog` collection.
   ══════════════════════════════════════════════ */

function CatalogPage({ loadData }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({
    name: '',
    category: '',
    typicalHours: '',
    typicalMaterials: '',
    notes: '',
  });

  const CATEGORIES = [
    'Plumbing',
    'Electrical',
    'Carpentry',
    'Drywall & Paint',
    'Doors & Windows',
    'Fixtures',
    'Appliances',
    'Outdoor',
    'Other',
  ];

  // Load items
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDocs(query(collection(db, 'flatRateCatalog'), orderBy('category'), orderBy('name')));
        if (cancelled) return;
        setItems(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (err) {
        console.error('Catalog load failed', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const refresh = async () => {
    setLoading(true);
    try {
      const snap = await getDocs(query(collection(db, 'flatRateCatalog'), orderBy('category'), orderBy('name')));
      setItems(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) {
      dialog.alert('Error refreshing: ' + err.message);
    }
    setLoading(false);
  };

  const startAdd = () => {
    setEditingId(null);
    setForm({ name: '', category: 'Plumbing', typicalHours: '', typicalMaterials: '', notes: '' });
    setShowAdd(true);
  };

  const startEdit = (item) => {
    setEditingId(item.id);
    setForm({
      name: item.name || '',
      category: item.category || 'Other',
      typicalHours: item.typicalHours != null ? String(item.typicalHours) : '',
      typicalMaterials: item.typicalMaterials != null ? String(item.typicalMaterials) : '',
      notes: item.notes || '',
    });
    setShowAdd(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      dialog.alert('Job name is required.');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        category: form.category || 'Other',
        typicalHours: form.typicalHours ? Number(form.typicalHours) : null,
        typicalMaterials: form.typicalMaterials ? Number(form.typicalMaterials) : null,
        notes: form.notes.trim() || null,
        updatedAt: serverTimestamp(),
      };
      if (editingId) {
        await updateDoc(doc(db, 'flatRateCatalog', editingId), payload);
      } else {
        await addDoc(collection(db, 'flatRateCatalog'), {
          ...payload,
          createdAt: serverTimestamp(),
        });
      }
      await refresh();
      setShowAdd(false);
      setEditingId(null);
    } catch (err) {
      dialog.alert('Error saving: ' + err.message);
    }
    setSaving(false);
  };

  const handleDelete = async (item) => {
    if (!(await dialog.confirm(`Delete "${item.name}" from catalog?`))) return;
    try {
      await deleteDoc(doc(db, 'flatRateCatalog', item.id));
      await refresh();
    } catch (err) {
      dialog.alert('Error: ' + err.message);
    }
  };

  // Filter + group by category
  const filtered = items.filter(item =>
    !search ||
    (item.name || '').toLowerCase().includes(search.toLowerCase()) ||
    (item.category || '').toLowerCase().includes(search.toLowerCase()) ||
    (item.notes || '').toLowerCase().includes(search.toLowerCase())
  );

  const grouped = {};
  filtered.forEach(item => {
    const cat = item.category || 'Other';
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(item);
  });
  const sortedCategories = Object.keys(grouped).sort();

  return (
    <div className="av2-page">
      <div className="av2-page-header">
        <h1>Services Catalog</h1>
        <div style={{ display: 'flex', gap: 10 }}>
          <button className="av2-btn-secondary" onClick={refresh}>{Icons.refresh} Refresh</button>
          <button className="av2-btn-primary" onClick={startAdd}>+ Add Job</button>
        </div>
      </div>

      {/* Intro */}
      <div style={{ background: 'var(--av2-info-bg)', border: '1px solid #C8DEF0', borderRadius: 8, padding: 14, marginBottom: 20, fontSize: 13, color: 'var(--av2-text)', lineHeight: 1.5 }}>
        <strong>Internal cheat sheet.</strong> Build up a list of common jobs with typical hours and material costs so you can quote faster. Not published publicly. As you complete more jobs, refine the numbers — these are starting points, not commitments.
      </div>

      {/* Search */}
      <div className="av2-filters-bar">
        <div className="av2-search-box">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
          <input placeholder="Search by job name, category, or notes..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
      </div>

      {/* List */}
      {loading ? (
        <div className="av2-empty-state"><p>Loading...</p></div>
      ) : items.length === 0 ? (
        <div className="av2-empty-state">
          <p>No jobs in the catalog yet.</p>
          <p style={{ fontSize: 13, color: 'var(--av2-text-muted)', marginTop: 8 }}>
            Click <strong>+ Add Job</strong> above to start building your catalog. Common starters: "Replace kitchen faucet", "Install ceiling fan", "Patch drywall (small)", "Replace toilet flapper".
          </p>
        </div>
      ) : sortedCategories.length === 0 ? (
        <div className="av2-empty-state"><p>No jobs match your search.</p></div>
      ) : (
        sortedCategories.map(cat => (
          <div key={cat} className="av2-section" style={{ padding: 0, marginBottom: 16 }}>
            <div className="av2-section-header" style={{ padding: '14px 18px' }}>
              <h3 style={{ margin: 0, fontSize: 15 }}>{cat}</h3>
              <span className="av2-section-count">{grouped[cat].length}</span>
            </div>
            <div className="av2-table-wrap">
              <table className="av2-table">
                <thead>
                  <tr>
                    <th style={{ width: '40%' }}>Job</th>
                    <th>Typical Hours</th>
                    <th>Materials Est.</th>
                    <th>Est. Total</th>
                    <th>Notes</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {grouped[cat].map(item => {
                    const total = (item.typicalHours || 0) * 120 + (item.typicalMaterials || 0);
                    return (
                      <tr key={item.id} style={{ cursor: 'pointer' }} onClick={() => startEdit(item)}>
                        <td style={{ fontWeight: 600 }}>{item.name}</td>
                        <td>{item.typicalHours != null ? `${item.typicalHours}h` : '—'}</td>
                        <td>{item.typicalMaterials != null ? `$${item.typicalMaterials.toFixed(0)}` : '—'}</td>
                        <td style={{ fontWeight: 700, color: 'var(--av2-green)' }}>
                          {item.typicalHours != null ? `$${total.toFixed(0)}` : '—'}
                        </td>
                        <td style={{ fontSize: 12, color: 'var(--av2-text-muted)', maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {item.notes || ''}
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          <button
                            className="av2-btn-link"
                            style={{ color: 'var(--av2-danger)', fontSize: 12 }}
                            onClick={(e) => { e.stopPropagation(); handleDelete(item); }}
                          >Delete</button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        ))
      )}

      {/* Add/Edit Modal */}
      {showAdd && (
        <div className="av2-modal-overlay" onClick={() => setShowAdd(false)}>
          <div className="av2-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 560 }}>
            <div className="av2-modal-header">
              <h2>{editingId ? 'Edit Job' : 'Add Job to Catalog'}</h2>
              <button className="av2-modal-close" onClick={() => setShowAdd(false)}>{Icons.close}</button>
            </div>
            <div style={{ padding: 24 }}>
              <div className="av2-form-field">
                <label>Job Name <span style={{ color: 'var(--av2-danger)' }}>*</span></label>
                <input
                  value={form.name}
                  onChange={e => setForm({ ...form, name: e.target.value })}
                  placeholder="Replace kitchen faucet"
                />
              </div>

              <div className="av2-form-field">
                <label>Category</label>
                <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}>
                  {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>

              <div className="av2-form-row">
                <div className="av2-form-field">
                  <label>Typical Hours</label>
                  <input
                    type="number"
                    step="0.25"
                    value={form.typicalHours}
                    onChange={e => setForm({ ...form, typicalHours: e.target.value })}
                    placeholder="1.5"
                  />
                  <div style={{ fontSize: 11, color: 'var(--av2-text-muted)', marginTop: 4 }}>
                    At $120/hr × {form.typicalHours || '0'}h = ${((Number(form.typicalHours) || 0) * 120).toFixed(0)} labor
                  </div>
                </div>
                <div className="av2-form-field">
                  <label>Materials Estimate ($)</label>
                  <input
                    type="number"
                    step="1"
                    value={form.typicalMaterials}
                    onChange={e => setForm({ ...form, typicalMaterials: e.target.value })}
                    placeholder="85"
                  />
                </div>
              </div>

              {form.typicalHours && (
                <div style={{ padding: 12, background: 'var(--av2-success-bg, #EAF7F0)', border: '1px solid #B5D9C5', borderRadius: 6, marginTop: 4, fontSize: 14 }}>
                  <strong>Estimated total:</strong>{' '}
                  <span style={{ fontWeight: 700, color: 'var(--av2-green)' }}>
                    ${((Number(form.typicalHours) || 0) * 120 + (Number(form.typicalMaterials) || 0)).toFixed(0)}
                  </span>
                </div>
              )}

              <div className="av2-form-field">
                <label>Notes <span style={{ fontWeight: 400, color: 'var(--av2-text-muted)' }}>(common gotchas, scope assumptions, materials list, etc.)</span></label>
                <textarea
                  value={form.notes}
                  onChange={e => setForm({ ...form, notes: e.target.value })}
                  rows={3}
                  placeholder="Assumes existing shutoffs work. Add $30 if shutoffs need replacing. Basic chrome faucet — upgrade pricing varies."
                />
              </div>

              <div style={{ display: 'flex', gap: 10, marginTop: 20, justifyContent: 'flex-end' }}>
                <button className="av2-btn-secondary" onClick={() => setShowAdd(false)} disabled={saving}>Cancel</button>
                <button className="av2-btn-primary" onClick={handleSave} disabled={saving}>
                  {saving ? 'Saving...' : (editingId ? 'Save Changes' : 'Add to Catalog')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ComingSoon({ title, setPage }) {
  return (
    <div className="av2-page">
      <div className="av2-page-header">
        <h1>{title}</h1>
      </div>
      <div className="av2-coming-soon">
        <div style={{ fontSize: 48, marginBottom: 16 }}>🚧</div>
        <h2>Under Construction</h2>
        <p>This section is being built next. Check back soon.</p>
        <button className="av2-btn-primary" onClick={() => setPage('dashboard')} style={{ marginTop: 20 }}>
          Back to Dashboard
        </button>
      </div>
    </div>
  );
}
